<template>
  <div id="app">
    <!-- Статус авторизации -->
    <div v-if="authStatus.visible" :class="['auth-status', authStatus.type]">
      {{ authStatus.message }}
    </div>
    
    <!-- Форма логина -->
    <div v-if="!isAuthenticated" class="login-body">
      <div class="login-container">
        <div class="logo"></div>
        <div id="google-signin-button"></div>
        <div v-if="error" class="error-message">{{ error }}</div>
      </div>
    </div>
    
    <!-- Админка -->
    <div v-else class="admin-page">
        <div class="header">
        <div class="header-left">
          <div class="logo-small"></div>
          <div class="title">AI Stenograph</div>
          
          <!-- System Status - Одна иконка с dropdown -->
          <div class="system-status-dropdown">
            <button 
              @click="showStatusDropdown = !showStatusDropdown"
              class="status-indicator-btn"
              :class="getOverallStatusClass()"
              :title="getOverallStatusTitle()"
            >
              <span class="status-icon">{{ getOverallStatusIcon() }}</span>
            </button>
            
            <!-- Dropdown с деталями статусов -->
            <transition name="dropdown-fade">
              <div v-if="showStatusDropdown" class="status-dropdown-menu" @click.stop>
                <div class="status-dropdown-header">{{ t('system.status') }}</div>
                <div class="status-dropdown-item">
                  <span class="status-service-name">Backend</span>
                  <div class="status-value">
                    <div class="status-dot" :class="getServiceStatusClass('backend')"></div>
                    <span>{{ getServiceStatusText('backend') }}</span>
                  </div>
                </div>
                <div class="status-dropdown-item">
                  <span class="status-service-name">Redis</span>
                  <div class="status-value">
                    <div class="status-dot" :class="getServiceStatusClass('redis')"></div>
                    <span>{{ getServiceStatusText('redis') }}</span>
                  </div>
                </div>
                <div class="status-dropdown-item">
                  <span class="status-service-name">OpenAI API</span>
                  <div class="status-value">
                    <div class="status-dot" :class="getServiceStatusClass('openai_api')"></div>
                    <span>{{ getServiceStatusText('openai_api') }}</span>
                  </div>
                </div>
                <div class="status-dropdown-item">
                  <span class="status-service-name">Disk</span>
                  <div class="status-value">
                    <div class="status-dot" :class="getServiceStatusClass('disk')"></div>
                    <span>{{ getServiceStatusText('disk') }}</span>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
        
        <!-- User Info - Dropdown меню -->
        <div class="user-info">
          <div class="user-dropdown">
            <button 
              @click="showUserDropdown = !showUserDropdown"
              class="user-menu-btn"
            >
              <img v-if="adminUser?.picture" :src="adminUser.picture" alt="Profile" class="user-avatar" />
              <span v-else class="user-avatar-placeholder">{{ getDisplayName(adminUser).charAt(0).toUpperCase() }}</span>
              <span class="user-name-compact">{{ getDisplayName(adminUser) }}</span>
              <svg class="dropdown-arrow" width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <path d="M6 8L2 4h8z"/>
              </svg>
            </button>
            
            <!-- Dropdown меню пользователя -->
            <transition name="dropdown-fade">
              <div v-if="showUserDropdown" class="user-dropdown-menu" @click.stop>
                <div class="user-dropdown-header">
                  <img v-if="adminUser?.picture" :src="adminUser.picture" alt="Profile" class="user-avatar-large" />
                  <div class="user-dropdown-info">
                    <div class="user-dropdown-name">{{ getDisplayName(adminUser) }}</div>
                    <div class="user-dropdown-email">{{ adminUser?.email }}</div>
                    <div class="user-dropdown-role">{{ getRoleDisplayName(adminUser?.role) }}</div>
                    
                    <!-- Прогресс-бар токена -->
                    <div v-if="tokenTimeInfo" class="token-status" :title="getTokenTooltip()">
                      <div class="token-progress-bar">
                        <div 
                          class="token-progress-fill" 
                          :style="{ width: tokenTimeInfo.percentRemaining + '%' }"
                          :class="{ 
                            'token-expiring': tokenTimeInfo.percentRemaining < 20,
                            'token-warning': tokenTimeInfo.percentRemaining < 50 && tokenTimeInfo.percentRemaining >= 20
                          }"
                        ></div>
                      </div>
                      <div class="token-time-text">{{ getTokenTimeRemaining() }}</div>
                    </div>
                  </div>
                </div>
                
                <div class="user-dropdown-divider"></div>
                
                <button 
                  @click="showPluginInstallModal = true; showUserDropdown = false"
                  class="user-dropdown-item"
                >
                  <span>{{ $t('nav.downloadPlugin') }}</span>
                </button>
                
                <button 
                  @click="showTipsModal = true; showUserDropdown = false"
                  class="user-dropdown-item"
                >
                  <span>{{ $t('nav.tipsAndTricks') }}</span>
                </button>
                
                <button 
                  @click="showPrivacyModal = true; loadPrivacyPolicy(); showUserDropdown = false"
                  class="user-dropdown-item"
                >
                  <span>{{ $t('nav.privacyPolicy') }}</span>
                </button>
                
                <button 
                  @click="showChangelogModal = true; loadChangelog(); showUserDropdown = false"
                  class="user-dropdown-item"
                >
                  <span>{{ t('nav.changelog') }}</span>
                </button>
                
                <button 
                  @click="showPromptsModal = true; showUserDropdown = false"
                  class="user-dropdown-item"
                >
                  <span>{{ t('prompts.title') }}</span>
                </button>
                
                <button 
                  @click="openSettingsModal"
                  class="user-dropdown-item"
                >
                  <span>{{ t('nav.settings') }}</span>
                </button>
                
                <button 
                  @click="toggleDarkMode(); showUserDropdown = false"
                  class="user-dropdown-item"
                >
                  <span>{{ isDark ? t('settings.light') : t('settings.dark') }}</span>
                </button>
                
                <button 
                  @click="toggleLocale(); showUserDropdown = false"
                  class="user-dropdown-item"
                >
                  <span>{{ t('common.languageSwitchText') }}</span>
                </button>
                
                <div class="user-dropdown-divider"></div>
                
                <button 
                  @click="handleLogout"
                  class="user-dropdown-item user-dropdown-logout"
                >
                  <span>{{ t('nav.logout') }}</span>
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <div class="container">
        <!-- Навигация -->
        <div class="navigation">
          <button 
            @click="currentSection = 'my-sessions'; loadSectionData()"
            :class="['nav-btn', currentSection === 'my-sessions' ? 'active' : '']"
          >
            {{ t('sessions.title') }}
          </button>
          <button 
            v-if="adminUser && adminUser.role === 'owner'"
            @click="currentSection = 'dashboard'; loadSectionData()"
            :class="['nav-btn', currentSection === 'dashboard' ? 'active' : '']"
          >
            {{ $t('stats.title') }}
          </button>
          <button 
            v-if="adminUser && (adminUser.role === 'owner' || adminUser.role === 'administrator')"
            @click="currentSection = 'admins'"
            :class="['nav-btn', currentSection === 'admins' ? 'active' : '']"
          >
            {{ t('admin.title') }}
          </button>
        </div>

        <!-- Раздел "Мои записи" -->
        <div v-if="currentSection === 'my-sessions'">
          <h1>{{ t('sessions.title') }}</h1>
          
          <!-- Skeleton Loading -->
          <div v-if="loading" class="sessions-skeleton">
            <div v-for="n in 3" :key="n" class="skeleton-card">
              <div class="skeleton-content">
                <div class="skeleton-left">
                  <div class="skeleton-time"></div>
                  <div class="skeleton-title"></div>
                  <div class="skeleton-text"></div>
                </div>
                <div class="skeleton-right">
                  <div class="skeleton-id"></div>
                  <div class="skeleton-actions">
                    <div class="skeleton-btn"></div>
                    <div class="skeleton-btn"></div>
                    <div class="skeleton-btn"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else-if="!loading && sessions.length === 0" class="empty-state">
            <div class="empty-state-card">
              <h3>{{ t('sessions.noSessions') }}</h3>
              <p class="empty-description">
                {{ t('sessions.noSessionsDesc') }}
              </p>
              <a 
                href="https://chromewebstore.google.com/detail/ai-stenograph/njgbkdhhohamnjgkpopkojhmmokgmdjo"
                target="_blank"
                rel="noopener noreferrer"
                class="chrome-store-badge-link"
              >
                <img src="/chromewebstore.png" :alt="t('sessions.installPlugin')" />
              </a>
              <div class="plugin-login-hint">
                <img :src="isDark ? '/pluginlogindark.png' : '/pluginloginlight.png'" alt="Plugin login" class="plugin-login-img" />
                <p class="plugin-login-text">{{ t('sessions.pluginLoginHint') }}</p>
              </div>
              <div class="empty-features">
                <div class="feature-item">
                  <span class="feature-icon">✓</span>
                  <span>{{ t('sessions.features.recording') }}</span>
                </div>
                <div class="feature-item">
                  <span class="feature-icon">✓</span>
                  <span>{{ t('sessions.features.transcription') }}</span>
                </div>
                <div class="feature-item">
                  <span class="feature-icon">✓</span>
                  <span>{{ t('sessions.features.summary') }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="sessions-by-date">
            <div v-for="(sessions, date) in sessionsByDate" :key="date" class="date-group">
              <div class="date-header">
                <h3>{{ date }}</h3>
                <div class="date-separator"></div>
              </div>
              <transition-group name="session-list" tag="div" class="sessions-grid">
                <div 
                  v-for="session in sessions" 
                  :key="session.session_id || session.id"
                  :id="`session-${session.session_id || session.id}`"
                  :class="['session-card', { 'editing': editingSessionId === (session.session_id || session.id) }]"
                >
                  <!-- Основной контент карточки -->
                  <div class="session-content">
                    <!-- Левая часть: основная информация -->
                    <div class="session-info">
                      <!-- Время начала записи, статус и время записи в одной строке -->
                      <div class="session-time-status">
                        <strong>{{ session.created_at ? new Date(session.created_at).toLocaleTimeString(locale) : 'N/A' }}</strong>
                        <span :class="['status-badge', session.status]">
                          {{ getStatusDisplayName(session.status) }}
                        </span>
                    <span v-if="isActiveSession(session.status)" :class="['duration-text', 'active']">
                      {{ formatRecordingTime(session.created_at) }}
                    </span>
                    <span v-else class="duration-text">
                      {{ formatDuration(session.recording_duration) }}
                    </span>
                    <!-- 🆕 Значки типа обработки -->
                    <span v-if="session.force_full_context === true"
                          class="processing-type-badge"
                          :title="t('sessions.qualityMode')">
                      💎
                    </span>
                    <span v-else-if="session.force_full_context === false"
                          class="processing-type-badge"
                          :title="t('sessions.streamingMode')">
                      ⚡
                    </span>
                      </div>
                      
                      <!-- Название вкладки с inline редактированием -->
                      <div v-if="session.tab_title" class="session-tab-title">
                        <input 
                          v-if="editingSessionId === (session.session_id || session.id)"
                          v-model="editingTitle"
                          @keyup.enter="saveEditedTitle(session.session_id || session.id)"
                          @keyup.esc="cancelEdit"
                          @blur="handleEditBlur($event, session.session_id || session.id)"
                          class="tab-text-input"
                          :ref="el => { if (el && editingSessionId === (session.session_id || session.id)) el.focus() }"
                          maxlength="100"
                        />
                        <span v-else class="tab-text" :title="session.tab_title">{{ session.tab_title }}</span>
                        <button 
                          v-if="editingSessionId === (session.session_id || session.id)"
                          @click="saveEditedTitle(session.session_id || session.id)"
                          class="edit-save-btn"
                          :title="t('sessions.save')"
                        >
                          ✓
                        </button>
                        <button 
                          v-else-if="!isActiveSession(session.status)"
                          @click="startEdit(session.session_id || session.id, session.tab_title)"
                          class="edit-btn"
                          :title="t('sessions.edit')"
                        >
                          ✏️
                        </button>
                      </div>
                    </div>
                    
                    <!-- Правая часть: ID, email, язык и кнопки -->
                    <div class="session-right-column">
                      <!-- ID, email и язык -->
                      <div class="session-id-column">
                        <div 
                          class="session-id" 
                          @click="copyToClipboard(session.id || session.session_id)"
                          :title="t('sessions.fullIdTooltip', { id: session.id || session.session_id })"
                        >
                          {{ formatSessionId(session.id || session.session_id) }}
                        </div>
                        <div class="session-user-lang">
                          <span class="user-email">{{ session.user_email || t('sessions.anonymousUser') }}</span>
                          <!-- Язык показываем только для завершённых сессий (после определения) -->
                          <span 
                            v-if="getDisplayLanguage(session)"
                            class="language-badge"
                          >{{ getDisplayLanguage(session) }}</span>
                          <!-- TTL badge - скрываем для recording и receiving -->
                          <span 
                            v-if="shouldShowTTL(session.status)"
                            class="ttl-badge" 
                            :class="{ 'ttl-clickable': !isTTLInfinite(session) }"
                            :title="isTTLInfinite(session) ? t('sessions.ttlInfinite') : t('sessions.makeTTLInfinite')"
                            @click="setInfiniteTTL(session)"
                          >
                            {{ getSessionTTL(session) }}
                          </span>
                        </div>
                      </div>
                      
                      <!-- Кнопки действий -->
                      <div class="session-actions">
                      <div class="action-row">
                        <!-- Кнопки действий -->
                        <div class="action-buttons">
                          <!-- Скачать транскрипцию (показываем и для активных записей в streaming режиме, НО НЕ для анонимных) -->
                          <button 
                            v-if="(session.has_text || ((session.status === 'receiving' || session.status === 'paused') && !session.force_full_context)) && session.user_email"
                            @click="openModal(session.session_id || session.id, 'transcription')"
                            class="action-btn download-transcription"
                            :title="session.status === 'receiving' || session.status === 'paused' ? t('sessions.viewInterim') : t('sessions.viewTranscript')"
                          >
                          {{ t('sessions.viewText') }}
                          </button>
                          
                          <!-- Саммари (показываем если есть транскрипция, НО НЕ для анонимных) -->
                          <button 
                            v-if="session.has_text && session.user_email"
                            @click="openModal(session.session_id || session.id, 'summary')"
                            class="action-btn download-summary"
                            :title="session.has_summary ? t('sessions.viewSummary') : t('sessions.createSummary')"
                          >
                            {{ session.has_summary ? t('sessions.summary') : t('sessions.createSummary') }}
                          </button>
                          
                          <!-- Кнопка аудио с замком -->
                          <button 
                            v-if="session.has_audio"
                            @click="openAudioPlayer(session.session_id || session.id)"
                            class="action-btn audio-text-btn"
                            :class="{ locked: session.audio_locked }"
                            :title="t('sessions.playAudio')"
                          >
                            {{ t('sessions.audio') }}<span 
                              class="lock-icon"
                              @click.stop="toggleAudioLock(session.session_id || session.id, !session.audio_locked)"
                              :title="session.audio_locked ? t('sessions.unlockAudio') : t('sessions.lockAudio')"
                            >{{ session.audio_locked ? '🔒' : '🔓' }}</span>
                          </button>
                          
                          <!-- Удалить -->
                          <button 
                            @click="deleteSession(session.session_id || session.id)"
                            class="action-btn delete-btn"
                            :title="t('sessions.deleteSession')"
                          >
                            {{ t('sessions.delete') }}
                          </button>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
              </transition-group>
            </div>
          </div>
          <!-- Infinite scroll sentinel -->
          <div v-if="sessionsPage < sessionsTotalPages && !loading" ref="scrollSentinel" class="scroll-sentinel">
            <div v-if="loadingMore" class="load-more-indicator">
              <span class="spinner-small"></span>
              <span>{{ t('sessions.loading') }}</span>
            </div>
            <div v-else class="load-more-indicator load-more-hint">
              {{ sessions.length }} / {{ sessionsTotal }}
            </div>
          </div>
        </div>

        <!-- Раздел статистики и сессий (только для Owner) -->
        <div v-if="currentSection === 'dashboard'">
          <h1>{{ t('stats.title') }}</h1>
        <div v-if="apiError" class="error-message">{{ apiError }}</div>
        
        <!-- Компактная статистика -->
        <div v-if="stats" class="compact-stats-section">
          <div class="stats-line">
            <span class="stats-group">
              <span class="stats-label">{{ t('stats.sessions') }}</span>
              <span class="stats-values">
                <span class="stat-number" :title="t('stats.total') + ': ' + stats.total_sessions">{{ stats.total_sessions }}</span>
                <span class="divider">/</span>
                <span class="stat-number success" :title="t('stats.completed') + ': ' + stats.completed_sessions">{{ stats.completed_sessions }}</span>
                <span class="divider">/</span>
                <span class="stat-number" :title="t('stats.active') + ': ' + (stats.active_sessions || 0)">{{ stats.active_sessions || 0 }}</span>
              </span>
            </span>
            <span class="separator">|</span>
            <span class="stats-group">
              <span class="stats-label">{{ t('stats.totalTime') }}</span>
              <span class="stats-values">
                <span class="stat-number">{{ formatTotalDuration(stats.total_duration_seconds) }}</span>
              </span>
            </span>
          </div>
          
          <div class="stats-line">
            <span class="stats-group">
              <span class="stats-label">{{ t('stats.users') }}</span>
              <span class="stats-values">
                <span class="stat-number" :title="t('stats.totalUsers') + ': ' + stats.users_count">{{ stats.users_count }}</span>
                <span class="divider">/</span>
                <span class="stat-number" :title="t('stats.activeUsers') + ': ' + (stats.user_sessions?.length || 0)">{{ stats.user_sessions?.length || 0 }}</span>
              </span>
            </span>
            <span class="separator">|</span>
            <span class="stats-group">
              <span class="stats-label">{{ t('stats.disk') }}</span>
              <span class="stats-values" :class="'disk-' + getDiskStatusClass()">
                <span class="stat-number">{{ stats.disk_used_percent?.toFixed(1) }}% ({{ formatDiskSize(stats.disk_free_mb) }} {{ t('stats.free') }})</span>
              </span>
            </span>
          </div>
          
          <div v-if="adminUser?.role === 'owner'" class="stats-line">
            <span class="stats-group">
              <span class="stats-label">{{ t('stats.pluginInstalls') }}</span>
              <span class="stats-values">
                <span class="stat-number">{{ stats.plugin_stats?.totalInstalls || 0 }}</span>
              </span>
            </span>
            <span class="separator">|</span>
            <span class="stats-group">
              <span class="stats-label">{{ t('stats.anonymousSessions') }}</span>
              <span class="stats-values">
                <span class="stat-number" :title="t('stats.totalAnonymousCreated') + ': ' + (stats.total_anonymous_sessions_created || 0)">{{ stats.total_anonymous_sessions_created || 0 }}</span>
                <span class="divider">/</span>
                <span class="stat-number" :title="t('stats.currentAnonymousSessions') + ': ' + (stats.anonymous_sessions || 0)">{{ stats.anonymous_sessions || 0 }}</span>
              </span>
            </span>
          </div>
          
          <!-- Таблица установок плагина - сворачиваемая секция -->
          <div v-if="adminUser?.role === 'owner' && stats.plugin_stats?.authenticatedUsers && stats.plugin_stats.authenticatedUsers.length > 0" class="plugin-users-section">
            <button @click="showPluginInstallsSection = !showPluginInstallsSection" class="collapsible-header">
              <span class="collapsible-icon">{{ showPluginInstallsSection ? '▼' : '▶' }}</span>
              <h3>{{ t('stats.pluginInstallsExpanded') }} ({{ filteredPluginUsers.length }} {{ t('stats.of') }} {{ stats.plugin_stats.authenticatedUsers.length }})</h3>
            </button>
            <transition name="collapse">
              <div v-if="showPluginInstallsSection" class="collapsible-content">
                <!-- Фильтр по времени -->
                <div class="plugin-filter-controls">
                  <label for="plugin-time-filter">{{ $t('stats.activityPeriod') }}</label>
                  <select id="plugin-time-filter" v-model="selectedPluginTimeFilter">
                    <option value="all">{{ $t('stats.allTime') }}</option>
                    <option value="24h">{{ $t('stats.last24h') }}</option>
                    <option value="7d">{{ $t('stats.last7d') }}</option>
                    <option value="30d">{{ $t('stats.last30d') }}</option>
                  </select>
                </div>
            <table class="plugin-users-table">
              <thead>
                <tr>
                  <th>{{ $t('stats.version') }}</th>
                  <th>{{ $t('stats.id') }}</th>
                  <th>{{ $t('stats.email') }}</th>
                  <th>{{ $t('stats.country') }}</th>
                  <th>{{ $t('stats.lastIp') }}</th>
                  <th>{{ $t('stats.beta') }}</th>
                  <th>{{ $t('stats.lastActivity') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in filteredPluginUsers" :key="user.installationId">
                  <td class="version-cell">
                    <span class="version-badge">v{{ user.version }}</span>
                  </td>
                  <td class="id-cell">
                    <span class="install-id" :title="user.installationId">{{ user.installationId.substring(0, 8) }}...</span>
                  </td>
                  <td class="user-email">{{ user.email || '—' }}</td>
                  <td class="country-cell">
                    <span 
                      v-if="user.countryCode" 
                      class="country-flag" 
                      :title="`${user.countryName || user.countryCode} (${user.countryCode})`"
                    >
                      {{ getCountryFlag(user.countryCode) }}
                    </span>
                    <span v-else>—</span>
                  </td>
                  <td class="ip-cell">{{ user.lastIp || '—' }}</td>
                  <td class="beta-cell">
                    <span v-if="user.isBetaTester" class="beta-badge">✓</span>
                    <span v-else class="no-beta">—</span>
                  </td>
                  <td class="date-cell">{{ formatDateTime(user.lastSeen) }}</td>
                </tr>
              </tbody>
            </table>
              </div>
            </transition>
          </div>
        </div>
        
        <!-- Plugin Errors - сворачиваемая секция (admin+) -->
          <div v-if="adminUser?.role === 'owner' || adminUser?.role === 'administrator'" class="plugin-errors-section">
            <button @click="togglePluginErrorsSection" class="collapsible-header">
              <span class="collapsible-icon">{{ showPluginErrorsSection ? '▼' : '▶' }}</span>
              <h3>{{ t('pluginErrors.title') }} ({{ pluginErrorsTotal }})</h3>
            </button>
            <transition name="collapse">
              <div v-if="showPluginErrorsSection" class="collapsible-content">
                <!-- Фильтры -->
                <div class="plugin-errors-filters">
                  <select v-model="pluginErrorsFilterVersion" @change="loadPluginErrors">
                    <option value="">{{ t('pluginErrors.allVersions') }}</option>
                  </select>
                  <select v-model="pluginErrorsFilterType" @change="loadPluginErrors">
                    <option value="">{{ t('pluginErrors.allTypes') }}</option>
                    <option value="error">error</option>
                    <option value="unhandledrejection">unhandledrejection</option>
                    <option value="console.error">console.error</option>
                    <option value="api_error">api_error</option>
                    <option value="network_error">network_error</option>
                  </select>
                  <select v-model="pluginErrorsFilterTrust" @change="loadPluginErrors">
                    <option value="">{{ t('pluginErrors.allTrust') }}</option>
                    <option value="authenticated">{{ t('pluginErrors.authenticated') }}</option>
                    <option value="anonymous">{{ t('pluginErrors.anonymous') }}</option>
                  </select>
                  <button
                    v-if="adminUser?.role === 'owner'"
                    class="btn btn-danger btn-sm"
                    @click="clearAllPluginErrors"
                  >{{ t('pluginErrors.clearAll') }}</button>
                </div>

                <!-- Таблица ошибок -->
                <div v-if="pluginErrors.length > 0" class="plugin-errors-table-wrap">
                  <table class="plugin-errors-table">
                    <thead>
                      <tr>
                        <th>{{ t('pluginErrors.time') }}</th>
                        <th>{{ t('pluginErrors.version') }}</th>
                        <th>{{ t('pluginErrors.type') }}</th>
                        <th>{{ t('pluginErrors.message') }}</th>
                        <th>{{ t('pluginErrors.trust') }}</th>
                        <th>{{ t('pluginErrors.user') }}</th>
                        <th>{{ t('pluginErrors.count') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="err in pluginErrors"
                        :key="err.id"
                        class="plugin-error-row"
                        @click="expandedErrorId = expandedErrorId === err.id ? null : err.id"
                      >
                        <td class="date-cell">{{ formatDateTime(new Date(err.timestamp * 1000).toISOString()) }}</td>
                        <td><span class="version-badge">v{{ err.plugin_version }}</span></td>
                        <td><span class="error-type-badge">{{ err.error_type }}</span></td>
                        <td class="error-message-cell">{{ err.message }}</td>
                        <td>
                          <span v-if="err.trust_level === 'authenticated'" class="trust-badge trust-auth" :title="t('pluginErrors.authenticated')">🛡</span>
                          <span v-else class="trust-badge trust-anon" :title="t('pluginErrors.anonymous')">?</span>
                        </td>
                        <td>{{ err.user_email || '—' }}</td>
                        <td>{{ err.occurrences }}</td>
                      </tr>
                      <!-- Expandable stack trace row -->
                      <tr v-for="err in pluginErrors" :key="'stack-' + err.id" v-show="expandedErrorId === err.id && err.stack_trace" class="plugin-error-stack-row">
                        <td colspan="7">
                          <pre class="error-stack-trace"><code>{{ err.stack_trace }}</code></pre>
                          <div v-if="err.url" class="error-url">{{ err.url }}</div>
                          <div class="error-meta">
                            <span>ID: {{ err.installation_id.substring(0, 8) }}...</span>
                            <span v-if="err.client_ip">IP: {{ err.client_ip }}</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="no-errors-message">{{ t('pluginErrors.noErrors') }}</div>

                <!-- Пагинация -->
                <div v-if="pluginErrorsTotalPages > 1" class="plugin-errors-pagination">
                  <button :disabled="pluginErrorsPage <= 1" @click="pluginErrorsPage--; loadPluginErrors()">←</button>
                  <span>{{ pluginErrorsPage }} / {{ pluginErrorsTotalPages }}</span>
                  <button :disabled="pluginErrorsPage >= pluginErrorsTotalPages" @click="pluginErrorsPage++; loadPluginErrors()">→</button>
                </div>
              </div>
            </transition>
          </div>

        <!-- Все сессии системы с фильтром -->
        <div class="sessions-section-header">
          <h1>{{ t('stats.allSystemSessions') }}</h1>
          <div class="session-filter-controls" v-if="stats && stats.user_sessions">
            <label for="session-user-filter">{{ t('stats.userFilter') }}</label>
            <select id="session-user-filter" v-model="selectedUserFilter">
              <option value="">{{ t('stats.allUsers') }}</option>
              <option v-for="user in stats.user_sessions" :key="user.user_email" :value="user.user_email">
                {{ user.user_email }}
              </option>
            </select>
          </div>
        </div>

        <!-- Статистика по выбранному фильтру -->
        <div v-if="filteredSessionsStats" class="filtered-stats-bar">
          <div class="stat-item">
            <span class="stat-label">{{ t('stats.sessionsLabel') }}</span>
            <span class="stat-value">{{ filteredSessionsStats.total }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">{{ t('stats.completedLabel') }}</span>
            <span class="stat-value success-text">{{ filteredSessionsStats.completed }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">{{ t('stats.errorsLabel') }}</span>
            <span class="stat-value error-text">{{ filteredSessionsStats.errors }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">{{ t('stats.durationLabel') }}</span>
            <span class="stat-value">{{ formatTotalDuration(filteredSessionsStats.duration) }}</span>
          </div>
        </div>
        
        <!-- Skeleton Loading для All System Sessions -->
        <div v-if="loading" class="sessions-skeleton">
          <div v-for="n in 5" :key="n" class="skeleton-card">
            <div class="skeleton-content">
              <div class="skeleton-left">
                <div class="skeleton-time"></div>
                <div class="skeleton-title"></div>
                <div class="skeleton-text"></div>
              </div>
              <div class="skeleton-right">
                <div class="skeleton-id"></div>
                <div class="skeleton-actions">
                  <div class="skeleton-btn"></div>
                  <div class="skeleton-btn"></div>
                  <div class="skeleton-btn"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else-if="!loading && filteredSessions.length === 0" class="empty-state">
          <p>{{ selectedUserFilter ? t('sessions.noUserSessions') : t('sessions.noSessionsFound') }}</p>
        </div>
        <div v-else class="sessions-by-date">
          <div 
            v-for="(dateSessions, dateKey) in filteredSessionsByDate" 
            :key="dateKey"
            class="date-group"
          >
            <!-- Заголовок даты -->
            <div class="date-header">
              <h3>{{ dateKey }}</h3>
              <div class="date-separator"></div>
            </div>
            
            <!-- Сессии этой даты -->
            <transition-group name="session-list" tag="div" class="sessions-grid">
              <div 
                v-for="session in dateSessions" 
                :key="session.session_id || session.id"
                class="session-card"
              >
                <!-- Основной контент карточки -->
                <div class="session-content">
                  <!-- Левая часть: основная информация -->
                  <div class="session-info">
                    <!-- Время начала записи, статус и время записи в одной строке -->
                    <div class="session-time-status">
                      <strong>{{ session.created_at ? new Date(session.created_at).toLocaleTimeString('ru-RU') : 'N/A' }}</strong>
                      <span :class="['status-badge', session.status]">
                        {{ getStatusDisplayName(session.status) }}
                      </span>
                      <span v-if="isActiveSession(session.status)" :class="['duration-text', 'active']">
                        {{ formatRecordingTime(session.created_at) }}
                      </span>
                      <span v-else class="duration-text">
                        {{ formatDuration(session.recording_duration) }}
                      </span>
                      <!-- 🆕 Значки типа обработки -->
                      <span v-if="session.force_full_context === true"
                            class="processing-type-badge"
                            :title="t('sessions.qualityMode')">
                        💎
                      </span>
                      <span v-else-if="session.force_full_context === false"
                            class="processing-type-badge"
                            :title="t('sessions.streamingMode')">
                        ⚡
                      </span>
                    </div>
                    
                    <!-- Название вкладки с inline редактированием -->
                    <div v-if="session.tab_title" class="session-tab-title">
                      <input 
                        v-if="editingSessionId === (session.session_id || session.id)"
                        v-model="editingTitle"
                        @keyup.enter="saveEditedTitle(session.session_id || session.id)"
                        @keyup.esc="cancelEdit"
                        @blur="handleEditBlur($event, session.session_id || session.id)"
                        class="tab-text-input"
                        :ref="el => { if (el && editingSessionId === (session.session_id || session.id)) el.focus() }"
                        maxlength="100"
                      />
                      <span v-else class="tab-text" :title="session.tab_title">{{ session.tab_title }}</span>
                      <button 
                        v-if="editingSessionId === (session.session_id || session.id)"
                        @click="saveEditedTitle(session.session_id || session.id)"
                        class="edit-save-btn"
                        :title="t('common.save')"
                      >
                        ✓
                      </button>
                      <button 
                        v-else-if="!isActiveSession(session.status)"
                        @click="startEdit(session.session_id || session.id, session.tab_title)"
                        class="edit-btn"
                        :title="t('common.edit')"
                      >
                        ✏️
                      </button>
                    </div>
                  </div>
                  
                  <!-- Правая часть: ID, email, язык и кнопки -->
                  <div class="session-right-column">
                    <!-- ID, email и язык -->
                    <div class="session-id-column">
                      <div 
                        class="session-id" 
                        @click="copyToClipboard(session.id || session.session_id)"
                        :title="t('sessions.fullIdTooltip', { id: session.id || session.session_id })"
                      >
                        {{ formatSessionId(session.id || session.session_id) }}
                      </div>
                      <div class="session-user-lang">
                        <span class="user-email">{{ session.user_email || t('sessions.anonymousUser') }}</span>
                        <!-- Язык показываем только для завершённых сессий (после определения) -->
                        <span 
                          v-if="getDisplayLanguage(session)"
                          class="language-badge"
                        >{{ getDisplayLanguage(session) }}</span>
                        <!-- TTL badge - скрываем для recording и receiving -->
                        <span 
                          v-if="shouldShowTTL(session.status)"
                          class="ttl-badge" 
                          :class="{ 'ttl-clickable': !isTTLInfinite(session) }"
                          :title="isTTLInfinite(session) ? t('sessions.ttlInfinite') : t('sessions.makeTTLInfinite')"
                          @click="setInfiniteTTL(session)"
                        >
                          {{ getSessionTTL(session) }}
                        </span>
                      </div>
                    </div>
                    
                    <!-- Кнопки действий -->
                    <div class="session-actions">
                    <div class="action-row">
                      <!-- Кнопки действий -->
                      <div class="action-buttons">
                        <!-- Скачать транскрипцию (показываем и для активных записей в streaming режиме, НО НЕ для анонимных) -->
                        <button 
                          v-if="(session.has_text || ((session.status === 'receiving' || session.status === 'paused') && !session.force_full_context)) && session.user_email"
                          @click="openModal(session.session_id || session.id, 'transcription')"
                          class="action-btn download-transcription"
                          :title="session.status === 'receiving' || session.status === 'paused' ? t('sessions.viewInterim') : t('sessions.viewTranscript')"
                        >
                        {{ t('common.text') }}
                        </button>
                        
                        <!-- Саммари (показываем если есть транскрипция, НО НЕ для анонимных) -->
                        <button 
                          v-if="session.has_text && session.user_email"
                          @click="openModal(session.session_id || session.id, 'summary')"
                          class="action-btn download-summary"
                          :title="session.has_summary ? t('sessions.viewSummary') : t('sessions.createSummary')"
                        >
                          {{ session.has_summary ? t('common.summary') : t('common.create') }}
                        </button>
                        
                        <!-- Кнопка аудио с замком -->
                        <button 
                          v-if="session.has_audio"
                          @click="openAudioPlayer(session.session_id || session.id)"
                          class="action-btn audio-text-btn"
                          :class="{ locked: session.audio_locked }"
                          :title="t('sessions.playAudio')"
                        >
                          {{ t('sessions.audio') }}<span 
                            class="lock-icon"
                            @click.stop="toggleAudioLock(session.session_id || session.id, !session.audio_locked)"
                            :title="session.audio_locked ? t('sessions.unlockAudio') : t('sessions.lockAudio')"
                          >{{ session.audio_locked ? '🔒' : '🔓' }}</span>
                        </button>
                        
                        <!-- Удалить -->
                        <button 
                          @click="deleteSession(session.session_id || session.id)"
                          class="action-btn delete-btn"
                          :title="t('sessions.deleteSession')"
                        >
                          {{ t('common.delete') }}
                        </button>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            </transition-group>
          </div>
          </div>
          <!-- Infinite scroll sentinel for dashboard -->
          <div v-if="sessionsPage < sessionsTotalPages && !loading" ref="scrollSentinelDash" class="scroll-sentinel">
            <div v-if="loadingMore" class="load-more-indicator">
              <span class="spinner-small"></span>
              <span>{{ t('sessions.loading') }}</span>
            </div>
            <div v-else class="load-more-indicator load-more-hint">
              {{ sessions.length }} / {{ sessionsTotal }}
            </div>
          </div>
        </div>

        <!-- Раздел администраторов -->
        <div v-if="currentSection === 'admins'">
          <AdminManagement />
        </div>

        <!-- Раздел промптов - УДАЛЁН, перенесён в модальное окно -->

        <!-- Раздел настроек транскрипции - УДАЛЁН, перенесён в модальное окно -->
      </div>
    </div>
    
    <!-- Модальное окно промптов -->
    <Transition name="modal">
      <div v-if="showPromptsModal" class="modal-overlay" @click="showPromptsModal = false">
        <div class="modal-content prompts-modal" @click.stop>
          <div class="modal-header">
            <h2>📝 {{ t('prompts.title') }}</h2>
            <button @click="showPromptsModal = false" class="btn-close" :title="t('common.close')">✕</button>
          </div>
          <div class="modal-body prompts-modal-body">
            <PromptsManagement />
          </div>
        </div>
      </div>
    </Transition>
    
    <!-- Модальное окно настроек транскрипции -->
    <Transition name="modal">
      <div v-if="showSettingsModal" class="modal-overlay" @click="showSettingsModal = false">
        <div class="modal-content settings-modal" @click.stop>
          <div class="modal-header">
            <h2>{{ $t('settings.transcriptionConfig') }}</h2>
            <button @click="showSettingsModal = false" class="btn-close" :title="$t('modal.close')">✕</button>
          </div>
          <div class="modal-body">
            <!-- 🆕 Качественная обработка -->
            <div class="settings-section">
              <div class="setting-item">
                <div class="setting-toggle-row">
                  <div class="setting-info">
                    <label class="setting-label">{{ $t('settings.qualityMode') }}</label>
                    <p class="setting-description">{{ $t('settings.qualityModeDesc') }}</p>
                  </div>
                  <label class="switch" :class="{ 'disabled': hasActiveRecording }">
                    <input 
                      type="checkbox" 
                      :checked="useQualityMode"
                      :disabled="hasActiveRecording"
                      @change="useQualityMode = !useQualityMode; saveTranscriptionSettings()"
                    >
                    <span class="slider"></span>
                  </label>
                </div>
                
                <div v-if="hasActiveRecording" class="active-recording-warning">
                  ⚠️ {{ $t('settings.cannotChangeWhileRecording') }}
                </div>
                
                <div v-if="useQualityMode && !hasActiveRecording" class="quality-mode-note">
                  ⚠️ {{ $t('settings.qualityModeNote') }}
                </div>
              </div>
            </div>
            
            <!-- Сохранение аудио -->
            <div class="settings-section">
              <div class="setting-item">
                <div class="setting-toggle-row">
                  <div class="setting-info">
                    <label class="setting-label">{{ $t('settings.saveAudio') }}</label>
                    <p class="setting-description">{{ $t('settings.saveAudioDesc') }}</p>
                  </div>
                  <label class="switch">
                    <input 
                      type="checkbox" 
                      :checked="saveAudioRecording"
                      @change="saveAudioRecording = !saveAudioRecording; saveTranscriptionSettings()"
                    >
                    <span class="slider"></span>
                  </label>
                </div>
                <div v-if="saveAudioRecording" class="quality-mode-note">
                  ⚠️ {{ $t('settings.saveAudioNote') }}
                </div>
                
                <!-- Audio Storage Section -->
                <div v-if="saveAudioRecording" class="audio-storage-section">
                  <!-- Storage Usage Card -->
                  <div class="storage-card">
                    <div class="storage-card-header">
                      <div class="storage-card-title">
                        <span class="storage-icon">💾</span>
                        {{ $t('settings.storageTitle') }}
                      </div>
                      <button @click="loadStorageInfo" class="refresh-btn-mini" title="Refresh">
                        <span class="refresh-icon">↻</span>
                      </button>
                    </div>
                    
                    <div class="storage-progress">
                      <div class="storage-bar-container">
                        <div 
                          class="storage-bar-fill" 
                          :class="{ 
                            'warning': storageInfo.is_near_limit, 
                            'danger': storageInfo.is_over_limit 
                          }"
                          :style="{ width: Math.min(storageInfo.percent_used, 100) + '%' }"
                        ></div>
                      </div>
                      <div class="storage-stats">
                        <span class="storage-used">{{ storageInfo.used_mb.toFixed(1) }} MB</span>
                        <span class="storage-separator">/</span>
                        <span class="storage-limit">{{ storageInfo.limit_mb.toFixed(0) }} MB</span>
                        <span class="storage-percent-badge" :class="{ warning: storageInfo.is_near_limit, danger: storageInfo.is_over_limit }">
                          {{ storageInfo.percent_used.toFixed(0) }}%
                        </span>
                      </div>
                    </div>
                    
                    <div class="storage-breakdown-grid">
                      <div class="breakdown-item">
                        <span class="breakdown-icon">🎵</span>
                        <span class="breakdown-label">{{ $t('settings.storageAudio') }}</span>
                        <span class="breakdown-value">{{ storageInfo.audio_mb.toFixed(1) }} MB</span>
                      </div>
                      <div class="breakdown-item">
                        <span class="breakdown-icon">📝</span>
                        <span class="breakdown-label">{{ $t('settings.storageTranscriptions') }}</span>
                        <span class="breakdown-value">{{ storageInfo.transcriptions_mb.toFixed(2) }} MB</span>
                      </div>
                    </div>
                    
                    <div v-if="storageInfo.is_over_limit" class="storage-alert danger">
                      <span class="alert-icon">⚠️</span>
                      <span>{{ $t('settings.storageOverLimit') }}. {{ $t('settings.storageLimitWarning') }}</span>
                    </div>
                    <div v-else-if="storageInfo.is_near_limit" class="storage-alert warning">
                      <span class="alert-icon">⚠️</span>
                      <span>{{ $t('settings.storageNearLimit') }}</span>
                    </div>
                  </div>
                  
                  <!-- Cyclic Recording Option -->
                  <div class="cyclic-option-card">
                    <div class="cyclic-header">
                      <div class="cyclic-info">
                        <span class="cyclic-icon">🔄</span>
                        <div class="cyclic-text">
                          <span class="cyclic-title">{{ $t('settings.autoDeleteOldest') }}</span>
                          <span class="cyclic-desc">{{ $t('settings.autoDeleteOldestDesc') }}</span>
                        </div>
                      </div>
                      <label class="switch">
                        <input 
                          type="checkbox" 
                          :checked="autoDeleteOldestAudio"
                          @change="autoDeleteOldestAudio = !autoDeleteOldestAudio; saveTranscriptionSettings()"
                        >
                        <span class="slider"></span>
                      </label>
                    </div>
                    <div v-if="autoDeleteOldestAudio" class="cyclic-note">
                      <span class="note-icon">🔒</span>
                      {{ $t('settings.autoDeleteOldestNote') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="settings-section">
              <div class="setting-item">
                <div class="setting-header">
                  <label for="temperature-slider" class="setting-label">
                    {{ $t('settings.temperature') }}
                  </label>
                  <span class="setting-value">{{ whisperTemperature.toFixed(1) }}</span>
                </div>
                <input 
                  id="temperature-slider"
                  type="range" 
                  v-model.number="whisperTemperature" 
                  min="0" 
                  max="1" 
                  step="0.1"
                  class="setting-slider"
                  @change="saveTranscriptionSettings"
                />
                <div class="setting-description">
                  <div class="temp-markers">
                    <span class="temp-marker">
                      <strong>0.0</strong> — {{ $t('settings.maxAccuracy') }}
                    </span>
                    <span class="temp-marker">
                      <strong>0.5</strong> — {{ $t('settings.balanced') }}
                    </span>
                    <span class="temp-marker">
                      <strong>1.0</strong> — {{ $t('settings.maxVariability') }}
                    </span>
                  </div>
                  <p class="setting-note">
                    {{ $t('settings.temperatureDesc') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="resetToDefaults" class="btn btn-secondary">
              {{ $t('settings.resetToDefaults') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
    
    <!-- Модальное окно для просмотра текста/саммари -->
    <Transition name="modal">
      <div v-if="modal.isOpen" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ modal.title }}</h2>
          <div class="modal-actions">
            <button
              v-if="modal.type === 'summary'"
              @click="modal.showRegenerateForm = !modal.showRegenerateForm" 
              class="btn-regenerate" 
              :title="modal.content ? t('sessions.recreateSummary') : t('sessions.createSummary')"
            >
              {{ modal.content ? t('sessions.recreate') : t('common.create') }}
            </button>
            <button 
              v-if="modal.hasAudio && !isProcessingSession(modal.sessionStatus)"
              @click="reprocessFromModal" 
              class="btn-reprocess" 
              :title="t('sessions.reprocess')"
            >
              🔄 {{ t('sessions.reprocess') }}
            </button>
            <button 
              v-if="modal.content && !isProcessingSession(modal.sessionStatus)"
              @click="downloadFromModal" 
              class="btn-download" 
              :title="t('common.downloadFile')"
            >
              {{ t('common.download') }}
            </button>
            <button @click="closeModal" class="btn-close" :title="t('common.close')">
              ✕
            </button>
          </div>
        </div>
        <div class="modal-body">
          <div v-if="modal.loading" class="modal-loading">
            <div class="spinner"></div>
            <p>Загрузка...</p>
          </div>
          <div v-else class="modal-content-wrapper">
            <!-- Анимированная полоска генерации саммари -->
            <div v-if="modal.regenerating && modal.type === 'summary'" class="regenerate-progress">
              <div class="progress-content">
                <div class="progress-text">{{ t('sessions.generatingSummary') }}</div>
              </div>
            </div>

            <!-- Форма регенерации саммари (вне скролла) -->
            <div v-if="modal.showRegenerateForm && modal.type === 'summary' && !modal.regenerating" class="regenerate-form">
              <h3>{{ $t('sessions.createNewSummary') }}</h3>
              
              <!-- Выбор из сохраненных промптов -->
              <div v-if="modal.availablePrompts.length > 0" class="prompt-selector">
                <label>{{ $t('sessions.selectSavedPrompt') }}</label>
                <div class="prompts-dropdown">
                  <button
                    v-for="prompt in modal.availablePrompts"
                    :key="prompt.id"
                    @click="selectPrompt(prompt)"
                    class="prompt-option"
                    type="button"
                  >
                    <span class="prompt-name">{{ prompt.name }}</span>
                    <span class="prompt-preview">{{ prompt.content.substring(0, 60) }}{{ prompt.content.length > 60 ? '...' : '' }}</span>
                  </button>
                </div>
              </div>

              <div v-if="modal.availablePrompts.length > 0" class="prompt-divider">
                <span>{{ t('sessions.orEnterCustom') }}</span>
              </div>

              <!-- Текстовое поле для промпта -->
              <textarea 
                v-model="modal.newPrompt" 
                :placeholder="t('sessions.enterCustomPrompt')"
                rows="4"
              ></textarea>

              <div class="form-actions">
                <button 
                  @click="regenerateSummary" 
                  :disabled="modal.regenerating || !modal.newPrompt.trim()"
                  class="btn-primary"
                >
                  {{ t('sessions.generate') }}
                </button>
                <button 
                  @click="modal.showRegenerateForm = false; modal.newPrompt = ''" 
                  class="btn-secondary"
                >
                  {{ t('common.cancel') }}
                </button>
              </div>
            </div>
            
            <!-- Скроллируемое содержимое -->
            <div class="modal-text" ref="modalTextContainer">
              <!-- Скелетон при переобработке -->
              <div v-if="modal.sessionStatus === 'reprocessing'" class="reprocessing-skeleton">
                <div class="reprocessing-header">
                  <span>{{ t('sessions.status.reprocessing') }}...</span>
                </div>
                <div class="skeleton-lines">
                  <div class="skeleton-line" style="width: 95%;"></div>
                  <div class="skeleton-line" style="width: 87%;"></div>
                  <div class="skeleton-line" style="width: 92%;"></div>
                  <div class="skeleton-line" style="width: 78%;"></div>
                  <div class="skeleton-line" style="width: 85%;"></div>
                  <div class="skeleton-line" style="width: 90%;"></div>
                  <div class="skeleton-line" style="width: 65%;"></div>
                </div>
              </div>
              
              <!-- Placeholder если нет саммари -->
              <div v-else-if="modal.type === 'summary' && !modal.content" class="no-summary-placeholder">
                <div style="text-align: center; padding: 60px 20px; color: #666;">
                  <h3 style="margin: 0 0 12px 0; color: #555;">{{ t('sessions.noSummary') }}</h3>
                  <p style="margin: 0; color: #777;">{{ t('sessions.noSummaryDesc') }}</p>
                </div>
              </div>
              
              <!-- Саммари с опциональной ссылкой на промпт -->
              <div v-else-if="modal.type === 'summary' && modal.content">
                <!-- Сворачиваемый промпт -->
                <div v-if="modal.prompt" class="summary-prompt-collapsible">
                  <div class="prompt-toggle" @click="modal.showPromptDetails = !modal.showPromptDetails">
                    <span class="prompt-toggle-icon">{{ modal.showPromptDetails ? '▼' : '▶' }}</span>
                    <span class="prompt-toggle-label">{{ t('sessions.promptLabel') }}</span>
                  </div>
                  <div v-if="modal.showPromptDetails" class="prompt-details">
                    {{ modal.prompt }}
                  </div>
                </div>
                <!-- Контент саммари -->
                <div v-html="formatTextWithTimestamps(modal.content)"></div>
              </div>
              
              <!-- Основное содержимое (для транскрипции) -->
              <div v-else-if="modal.content" v-html="formatTextWithTimestamps(modal.content)"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </Transition>
    
    <!-- Модальное окно с инструкцией по установке плагина -->
    <Transition name="modal">
      <div v-if="showPluginInstallModal" class="modal-overlay" @click="showPluginInstallModal = false">
        <div class="modal-content plugin-install-modal" @click.stop>
        <div class="modal-header">
          <h2>{{ $t('modal.installPlugin.title') }}</h2>
          <button @click="showPluginInstallModal = false" class="btn-close" :title="$t('modal.close')">✕</button>
        </div>
        <div class="modal-body">
          <div class="install-chrome-store">
            <div class="chrome-store-info">
              <h3>{{ $t('modal.installPlugin.chromeWebStore') }}</h3>
              <p>{{ $t('modal.installPlugin.chromeWebStoreDesc') }}</p>
            </div>
            
            <a 
              href="https://chromewebstore.google.com/detail/ai-stenograph/njgbkdhhohamnjgkpopkojhmmokgmdjo" 
              target="_blank"
              rel="noopener noreferrer"
              class="chrome-store-badge"
            >
              <img src="/chromewebstore.png" alt="Available in the Chrome Web Store" />
            </a>
          </div>
        </div>
      </div>
    </div>
    </Transition>
    
    <!-- Модальное окно Tips & Tricks -->
    <Transition name="modal">
      <div v-if="showTipsModal" class="modal-overlay" @click="showTipsModal = false">
        <div class="modal-content tips-modal" @click.stop>
        <div class="modal-header">
          <h2>💡 {{ $t('modal.tips.title') }}</h2>
          <button @click="showTipsModal = false" class="btn-close" :title="$t('modal.close')">✕</button>
        </div>
        <div class="modal-body">
          <div class="tips-list">
            <div class="tip-item">
              <div class="tip-icon">🔐</div>
              <div class="tip-content">
                <h4>{{ $t('modal.tips.authTitle') }}</h4>
                <p>{{ $t('modal.tips.authDesc') }}</p>
                <p class="tip-note">{{ $t('modal.tips.authAnonymous') }}</p>
              </div>
            </div>
            
            <div class="tip-item">
              <div class="tip-icon">☁️</div>
              <div class="tip-content">
                <h4>{{ $t('modal.tips.webViewTitle') }}</h4>
                <p>{{ $t('modal.tips.webViewDesc') }}</p>
              </div>
            </div>
            
            <div class="tip-item">
              <div class="tip-icon">✏️</div>
              <div class="tip-content">
                <h4>{{ $t('modal.tips.editTitle') }}</h4>
                <p>{{ $t('modal.tips.editDesc') }}</p>
              </div>
            </div>
            
            <div class="tip-item">
              <div class="tip-icon">⌨️</div>
              <div class="tip-content">
                <h4>{{ $t('modal.tips.hotkeysTitle') }}</h4>
                <p>
                  <code>Alt+S</code> {{ $t('modal.tips.hotkeyRecord') }}<br>
                  <code>Alt+A</code> {{ $t('modal.tips.hotkeyActivation') }}<br>
                  <code>Alt+M</code> {{ $t('modal.tips.hotkeyMic') }}
                </p>
              </div>
            </div>
            
            <div class="tip-item">
              <div class="tip-icon">🎤</div>
              <div class="tip-content">
                <h4>{{ $t('modal.tips.permissionsTitle') }}</h4>
                <p>{{ $t('modal.tips.permissionsDesc') }}</p>
              </div>
            </div>
            
            <div class="tip-item">
              <div class="tip-icon">⏱️</div>
              <div class="tip-content">
                <h4>{{ $t('modal.tips.autoStopTitle') }}</h4>
                <p>{{ $t('modal.tips.autoStopDesc') }}</p>
              </div>
            </div>
            
            <div class="tip-item">
              <div class="tip-icon">🎥</div>
              <div class="tip-content">
                <h4>{{ $t('modal.tips.googleMeetTitle') }}</h4>
                <p>{{ $t('modal.tips.googleMeetDesc') }}</p>
              </div>
            </div>
            
            <div class="tip-item">
              <div class="tip-icon">🔇</div>
              <div class="tip-content">
                <h4>{{ $t('modal.tips.micDetectionTitle') }}</h4>
                <p>{{ $t('modal.tips.micDetectionDesc') }}</p>
              </div>
            </div>
            
            <div class="tip-item">
              <div class="tip-icon">⚙️</div>
              <div class="tip-content">
                <h4>{{ $t('modal.tips.advancedSettingsTitle') }}</h4>
                <p>{{ $t('modal.tips.advancedSettingsDesc') }}</p>
              </div>
            </div>
            
            <div class="tip-item">
              <div class="tip-icon">🎚️</div>
              <div class="tip-content">
                <h4>{{ $t('modal.tips.audioSourceTitle') }}</h4>
                <p>{{ $t('modal.tips.audioSourceDesc') }}</p>
              </div>
            </div>
            
            <div class="tip-item">
              <div class="tip-icon">🌍</div>
              <div class="tip-content">
                <h4>{{ $t('modal.tips.languageTitle') }}</h4>
                <p>{{ $t('modal.tips.languageDesc') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </Transition>
    
    <!-- Модальное окно Audio Player -->
    <Transition name="modal">
      <div v-if="audioPlayerModal.show" class="modal-overlay" @click="closeAudioPlayer">
        <div class="modal-content audio-player-modal" @click.stop>
          <div class="modal-header">
            <h2>🎵 {{ audioPlayerModal.sessionTitle }}</h2>
            <button @click="closeAudioPlayer" class="btn-close" :title="$t('modal.close')">✕</button>
          </div>
          <div class="modal-body audio-player-body">
            <!-- Индикатор загрузки -->
            <div v-if="audioPlayerModal.loading" class="audio-loading">
              <div class="audio-loading-spinner"></div>
              <div class="audio-loading-text">
                {{ $t('sessions.loadingAudio') }}
                <span v-if="audioPlayerModal.loadingProgress > 0">
                  {{ audioPlayerModal.loadingProgress }}%
                </span>
              </div>
              <div v-if="audioPlayerModal.loadingProgress > 0" class="audio-progress-bar">
                <div 
                  class="audio-progress-fill" 
                  :style="{ width: audioPlayerModal.loadingProgress + '%' }"
                ></div>
              </div>
            </div>
            
            <!-- Аудио плеер -->
            <audio 
              ref="audioPlayerRef"
              v-if="audioPlayerModal.audioUrl"
              :src="audioPlayerModal.audioUrl"
              controls
              class="audio-player"
            >
              Your browser does not support the audio element.
            </audio>
            
            <div v-if="!audioPlayerModal.loading" class="audio-player-actions">
              <div class="audio-actions-row">
                <button 
                  @click="downloadAudio(audioPlayerModal.sessionId)"
                  class="btn btn-secondary"
                >
                  📥 {{ $t('sessions.downloadAudio') }}
                </button>
              </div>
              <div class="audio-actions-row secondary">
                <button 
                  @click="toggleAudioLockInModal()"
                  class="btn btn-outline"
                  :class="{ active: audioPlayerModal.audioLocked }"
                >
                  {{ audioPlayerModal.audioLocked ? '🔒' : '🔓' }} 
                  {{ audioPlayerModal.audioLocked ? $t('sessions.audioLocked') : $t('sessions.lockAudio') }}
                </button>
                <button 
                  @click="deleteAudioFromModal()"
                  class="btn btn-danger-outline"
                >
                  🗑️ {{ $t('sessions.deleteAudio') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
    
    <!-- Модальное окно Privacy Policy -->
    <Transition name="modal">
      <div v-if="showPrivacyModal" class="modal-overlay" @click="showPrivacyModal = false">
        <div class="modal-content privacy-modal" @click.stop>
          <div class="modal-header">
            <h2>🔒 {{ $t('modal.privacy.title') }}</h2>
            <button @click="showPrivacyModal = false" class="btn-close" :title="$t('modal.close')">✕</button>
          </div>
          <div class="modal-body">
            <div v-if="privacyLoading" class="loading-spinner">{{ $t('common.loading') }}</div>
            <div v-else-if="privacyError" class="error-message">
              {{ $t('modal.privacy.loadError') }}
            </div>
            <div v-else class="privacy-content" v-html="privacyContent"></div>
          </div>
        </div>
      </div>
    </Transition>
    
    <!-- Модальное окно Changelog -->
    <Transition name="modal">
      <div v-if="showChangelogModal" class="modal-overlay" @click="showChangelogModal = false">
        <div class="modal-content changelog-modal" @click.stop>
        <div class="modal-header">
          <h2>{{ t('changelog.title') }}</h2>
          <div class="modal-header-actions">
            <button 
              v-if="adminUser?.role === 'owner' && !editingChangelog"
              @click="startAddingChangelog"
              class="btn btn-primary btn-sm"
            >
              {{ t('changelog.add') }}
            </button>
            <button @click="showChangelogModal = false" class="btn-close" :title="t('common.close')">✕</button>
          </div>
        </div>
        <div class="modal-body">
          <!-- Форма добавления/редактирования -->
          <div v-if="editingChangelog" class="changelog-editor">
            <h3>{{ editingChangelog.isNew ? t('changelog.addEntry') : t('changelog.editEntry') }}</h3>
            
            <div class="form-group">
              <label>{{ t('changelog.component') }}:</label>
              <select v-model="editingChangelog.component" :disabled="!editingChangelog.isNew">
                <option value="plugin">{{ t('changelog.plugin') }}</option>
                <option value="backend">{{ t('changelog.backend') }}</option>
                <option value="frontend">{{ t('changelog.frontend') }}</option>
              </select>
            </div>
            
            <div class="form-group">
              <label>{{ t('changelog.version') }}:</label>
              <input 
                v-model="editingChangelog.version" 
                type="text" 
                placeholder="0.0.65"
                :disabled="!editingChangelog.isNew"
              />
            </div>
            
            <div class="form-group">
              <label>{{ $t('changelog.dateRelease') }}:</label>
              <input 
                v-model="editingChangelog.dateInput" 
                type="date"
              />
              <small class="form-hint">{{ $t('changelog.dateHint') }}</small>
            </div>
            
            <div class="form-group">
              <label>{{ $t('changelog.changes') }}:</label>
              <textarea 
                v-model="editingChangelog.changesText"
                rows="10"
                :placeholder="$t('changelog.changesHint')"
              ></textarea>
              <small class="form-hint">{{ $t('changelog.changesHint') }}</small>
            </div>
            
            <div class="form-actions">
              <button @click="saveChangelog" class="btn btn-primary">
                {{ $t('changelog.save') }}
              </button>
              <button @click="cancelEditingChangelog" class="btn btn-secondary">
                {{ $t('changelog.cancel') }}
              </button>
            </div>
          </div>
          
          <!-- Список changelog -->
          <div v-else>
            <div v-if="changelogLoading" class="loading-state">
              <div class="spinner"></div>
              <p>Загрузка changelog...</p>
            </div>
            <div v-else class="changelog-content">
            <!-- Plugin Section -->
            <div class="changelog-section">
              <h3 class="changelog-section-title">{{ t('changelog.plugin') }}</h3>
              <div v-if="changelog.plugin.length === 0" class="empty-changelog">
                {{ t('changelog.noEntries') }}
              </div>
              <div v-else class="changelog-entries">
                <div v-for="entry in changelog.plugin" :key="entry.version" class="changelog-entry">
                  <div class="changelog-header">
                    <div>
                      <span class="changelog-version">v{{ entry.version }}</span>
                      <span class="changelog-date">{{ formatChangelogDate(entry.date) }}</span>
                    </div>
                    <div v-if="adminUser?.role === 'owner'" class="changelog-actions">
                      <button @click="startEditingChangelog('plugin', entry)" class="btn-icon" :title="t('common.edit')">Edit</button>
                      <button @click="deleteChangelog('plugin', entry.version)" class="btn-icon btn-icon-danger" :title="t('common.delete')">Delete</button>
                    </div>
                  </div>
                  <ul class="changelog-changes">
                    <li v-for="(change, idx) in entry.changes" :key="idx">{{ change }}</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Backend Section -->
            <div class="changelog-section">
              <h3 class="changelog-section-title">{{ t('changelog.backend') }}</h3>
              <div v-if="changelog.backend.length === 0" class="empty-changelog">
                {{ t('changelog.noEntries') }}
              </div>
              <div v-else class="changelog-entries">
                <div v-for="entry in changelog.backend" :key="entry.version" class="changelog-entry">
                  <div class="changelog-header">
                    <div>
                      <span class="changelog-version">v{{ entry.version }}</span>
                      <span class="changelog-date">{{ formatChangelogDate(entry.date) }}</span>
                    </div>
                    <div v-if="adminUser?.role === 'owner'" class="changelog-actions">
                      <button @click="startEditingChangelog('backend', entry)" class="btn-icon" :title="t('common.edit')">Edit</button>
                      <button @click="deleteChangelog('backend', entry.version)" class="btn-icon btn-icon-danger" :title="t('common.delete')">Delete</button>
                    </div>
                  </div>
                  <ul class="changelog-changes">
                    <li v-for="(change, idx) in entry.changes" :key="idx">{{ change }}</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Frontend Section -->
            <div class="changelog-section">
              <h3 class="changelog-section-title">{{ t('changelog.frontend') }}</h3>
              <div v-if="changelog.frontend.length === 0" class="empty-changelog">
                {{ t('changelog.noEntries') }}
              </div>
              <div v-else class="changelog-entries">
                <div v-for="entry in changelog.frontend" :key="entry.version" class="changelog-entry">
                  <div class="changelog-header">
                    <div>
                      <span class="changelog-version">v{{ entry.version }}</span>
                      <span class="changelog-date">{{ formatChangelogDate(entry.date) }}</span>
                    </div>
                    <div v-if="adminUser?.role === 'owner'" class="changelog-actions">
                      <button @click="startEditingChangelog('frontend', entry)" class="btn-icon" :title="t('common.edit')">Edit</button>
                      <button @click="deleteChangelog('frontend', entry.version)" class="btn-icon btn-icon-danger" :title="t('common.delete')">Delete</button>
                    </div>
                  </div>
                  <ul class="changelog-changes">
                    <li v-for="(change, idx) in entry.changes" :key="idx">{{ change }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
    </Transition>
    
    <!-- Модальное окно подтверждения удаления -->
    <Transition name="modal">
      <div v-if="deleteConfirmModal.show" class="modal-overlay" @click="cancelDelete">
        <div class="modal-content delete-confirm-modal" @click.stop>
        <div class="modal-header">
          <h2>{{ deleteConfirmModal.deleteType === 'audio' ? t('sessions.deleteAudio') : t('sessions.deleteEntry') }}</h2>
          <button @click="cancelDelete" class="btn-close" :title="t('common.close')">✕</button>
        </div>
        <div class="modal-body">
          <p class="delete-warning">{{ deleteConfirmModal.deleteType === 'audio' ? t('sessions.deleteAudioConfirm') : t('sessions.deleteConfirm') }}</p>
          <div class="delete-session-info">
            <div>
              <strong>ID:</strong>
              <span>{{ deleteConfirmModal.sessionId ? deleteConfirmModal.sessionId.substring(0, 8) : '' }}...</span>
            </div>
            <div v-if="deleteConfirmModal.sessionTitle">
              <strong>{{ t('common.title') }}:</strong>
              <span>{{ deleteConfirmModal.sessionTitle }}</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="cancelDelete" class="btn btn-secondary">{{ t('common.cancel') }}</button>
          <button @click="confirmDelete" class="btn btn-danger">{{ t('common.delete') }}</button>
        </div>
      </div>
    </div>
    </Transition>
    
    <!-- Модальное окно подтверждения переобработки -->
    <Transition name="modal">
      <div v-if="reprocessConfirmModal.show" class="modal-overlay" @click="cancelReprocess">
        <div class="modal-content reprocess-confirm-modal" @click.stop>
          <div class="modal-header">
            <h2>{{ t('sessions.reprocess') }}</h2>
            <button @click="cancelReprocess" class="btn-close" :title="t('common.close')">✕</button>
          </div>
          <div class="modal-body">
            <p class="reprocess-info">{{ t('sessions.reprocessConfirm') }}</p>
            <div class="reprocess-session-info">
              <div>
                <strong>ID:</strong>
                <span>{{ reprocessConfirmModal.sessionId ? reprocessConfirmModal.sessionId.substring(0, 8) : '' }}...</span>
              </div>
              <div v-if="reprocessConfirmModal.sessionTitle">
                <strong>{{ t('common.title') }}:</strong>
                <span>{{ reprocessConfirmModal.sessionTitle }}</span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="cancelReprocess" class="btn btn-secondary">{{ t('common.cancel') }}</button>
            <button @click="confirmReprocess" class="btn btn-primary">🔄 {{ t('sessions.reprocess') }}</button>
          </div>
        </div>
      </div>
    </Transition>
    
    <!-- Toast уведомления -->
    <transition name="toast">
      <div v-if="toastNotification.show" :class="['toast-notification', toastNotification.type]">
        <span class="toast-icon">
          <span v-if="toastNotification.type === 'success'">✓</span>
          <span v-else-if="toastNotification.type === 'error'">✗</span>
          <span v-else>i</span>
        </span>
        <span class="toast-message">{{ toastNotification.message }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick, watch, onBeforeUnmount } from 'vue'
import AdminManagement from './components/AdminManagement.vue'
import PromptsManagement from './components/PromptsManagement.vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { useDarkMode } from './composables/useDarkMode'
import { useLanguage } from './composables/useLanguage'
import { authService } from './services/auth.js'
import { useSnowfall } from './composables/useSnowfall'

export default {
  name: 'App',
  components: {
    AdminManagement,
    PromptsManagement
  },
  setup() {
    const { isDark, toggleDarkMode, initTheme } = useDarkMode()
    const { locale, availableLocales, setLocale, toggleLocale, t } = useLanguage()
    const { initSnowfall, destroySnowfall } = useSnowfall()
    const isAuthenticated = ref(false)
    const adminUser = ref(null)
    const tokenTimeInfo = ref(null)
    const authStatus = ref({ message: '', type: '', visible: false })
    const error = ref('')
    const apiError = ref('')
    const stats = ref(null)
    const sessions = ref([])
    const loading = ref(true)  // Начинаем с true чтобы не моргать empty state
    const currentSection = ref('my-sessions')
    const selectedUserFilter = ref('')

    // Pagination state for lazy loading (infinite scroll)
    const sessionsPage = ref(1)
    const sessionsTotalPages = ref(1)
    const sessionsTotal = ref(0)
    const sessionsPerPage = ref(30)
    const loadingMore = ref(false)
    const scrollSentinel = ref(null)
    const scrollSentinelDash = ref(null)

    // Настройки транскрипции
    const whisperTemperature = ref(0.0) // По умолчанию максимальная точность
    const useQualityMode = ref(false) // Качественная обработка выключена по умолчанию
    const saveAudioRecording = ref(false) // Сохранение аудио выключено по умолчанию
    const autoDeleteOldestAudio = ref(false) // Циклическая запись выключена по умолчанию
    
    // Информация о хранилище
    const storageInfo = ref({
      used_mb: 0,
      limit_mb: 500,
      percent_used: 0,
      audio_mb: 0,
      transcriptions_mb: 0,
      is_near_limit: false,
      is_over_limit: false
    })

    // Ref для контейнера текста в модалке
    const modalTextContainer = ref(null)
    
    // Состояние модального окна
    const modal = ref({
      isOpen: false,
      title: '',
      content: '',
      sessionId: '',
      type: '', // 'transcription' или 'summary'
      loading: false,
      prompt: '', // Промпт для саммари
      showPromptDetails: false, // Показывать ли детали промпта (сворачиваемый блок)
      showRegenerateForm: false, // Показывать ли форму регенерации
      newPrompt: '', // Новый промпт для регенерации
      regenerating: false, // Идет ли процесс регенерации
      availablePrompts: [], // Список доступных промптов
      loadingPrompts: false, // Загрузка промптов
      autoRefreshInterval: null, // Интервал для автообновления активных сессий
      sessionStatus: null, // Статус сессии для определения нужно ли автообновление
      hasAudio: false // Есть ли аудио для переобработки
    })
    
    // Модальное окно для установки плагина
    const showPluginInstallModal = ref(false)
    
    // Модальное окно Prompts
    const showPromptsModal = ref(false)
    
    // Модальное окно Settings
    const showSettingsModal = ref(false)
    
    // Модальное окно Tips & Tricks
    const showTipsModal = ref(false)
    
    // Модальное окно Audio Player
    const audioPlayerModal = ref({
      show: false,
      sessionId: null,
      sessionTitle: '',
      audioUrl: null,
      loading: false,
      loadingProgress: 0,
      audioLocked: false
    })
    const audioPlayerRef = ref(null)
    
    // Модальное окно Privacy Policy
    const showPrivacyModal = ref(false)
    const privacyLoading = ref(false)
    const privacyError = ref(false)
    const privacyContent = ref('')
    
    // Changelog modal
    const showChangelogModal = ref(false)
    const changelogLoading = ref(false)
    const changelog = ref({
      plugin: [],
      backend: [],
      frontend: []
    })
    
    // Dropdown меню
    const showStatusDropdown = ref(false)
    const showUserDropdown = ref(false)
    
    // Collapsible секция установок плагина
    const showPluginInstallsSection = ref(false)

    // Plugin Errors section
    const showPluginErrorsSection = ref(false)
    const pluginErrors = ref([])
    const pluginErrorsTotal = ref(0)
    const pluginErrorsPage = ref(1)
    const pluginErrorsTotalPages = ref(1)
    const pluginErrorsFilterVersion = ref('')
    const pluginErrorsFilterType = ref('')
    const pluginErrorsFilterTrust = ref('')
    const expandedErrorId = ref(null)

    const togglePluginErrorsSection = () => {
      showPluginErrorsSection.value = !showPluginErrorsSection.value
      if (showPluginErrorsSection.value && pluginErrors.value.length === 0) {
        loadPluginErrors()
      }
    }

    const loadPluginErrors = async () => {
      try {
        const params = new URLSearchParams({
          page: pluginErrorsPage.value.toString(),
          per_page: '50'
        })
        if (pluginErrorsFilterVersion.value) params.set('plugin_version', pluginErrorsFilterVersion.value)
        if (pluginErrorsFilterType.value) params.set('error_type', pluginErrorsFilterType.value)
        if (pluginErrorsFilterTrust.value) params.set('trust_level', pluginErrorsFilterTrust.value)

        const response = await apiRequest(`/admin/plugin-errors?${params}`)
        if (response && response.ok) {
          const data = await response.json()
          pluginErrors.value = data.errors || []
          pluginErrorsTotal.value = data.total || 0
          pluginErrorsTotalPages.value = data.total_pages || 1
        }
      } catch (e) {
        console.error('Failed to load plugin errors:', e)
      }
    }

    const clearAllPluginErrors = async () => {
      if (!confirm(t('pluginErrors.confirmClear'))) return
      try {
        const response = await apiRequest('/admin/plugin-errors', { method: 'DELETE' })
        if (response && response.ok) {
          pluginErrors.value = []
          pluginErrorsTotal.value = 0
          pluginErrorsPage.value = 1
          pluginErrorsTotalPages.value = 1
        }
      } catch (e) {
        console.error('Failed to clear plugin errors:', e)
      }
    }
    
    // Фильтр для установок плагина (сохраняется в localStorage)
    const selectedPluginTimeFilter = ref(localStorage.getItem('plugin_time_filter') || 'all')
    
    // Отслеживаем изменения фильтра и сохраняем в localStorage
    watch(selectedPluginTimeFilter, (newValue) => {
      localStorage.setItem('plugin_time_filter', newValue)
    })
    
    // Сортировка пользователей плагина по версии
    const sortedPluginUsers = computed(() => {
      if (!stats.value?.plugin_stats?.authenticatedUsers) {
        return []
      }
      
      const users = [...stats.value.plugin_stats.authenticatedUsers]
      
      // Сортируем по версии (убывание - новые сверху)
      users.sort((a, b) => {
        const versionA = a.version.split('.').map(n => parseInt(n) || 0)
        const versionB = b.version.split('.').map(n => parseInt(n) || 0)
        
        for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
          const numA = versionA[i] || 0
          const numB = versionB[i] || 0
          
          if (numA !== numB) {
            return numB - numA  // Убывание
          }
        }
        
        return 0
      })
      
      return users
    })

    // Фильтрация установок плагина по времени
    const filteredPluginUsers = computed(() => {
      const users = sortedPluginUsers.value
      
      if (selectedPluginTimeFilter.value === 'all') {
        return users
      }
      
      const now = new Date()
      let cutoffTime
      
      // Определяем время отсечки в зависимости от фильтра
      if (selectedPluginTimeFilter.value === '24h') {
        cutoffTime = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      } else if (selectedPluginTimeFilter.value === '7d') {
        cutoffTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      } else if (selectedPluginTimeFilter.value === '30d') {
        cutoffTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      } else {
        return users
      }
      
      return users.filter(user => {
        try {
          const lastSeen = new Date(user.lastSeen)
          return lastSeen >= cutoffTime
        } catch (e) {
          return false
        }
      })
    })
    
    // Helper: check if session is active
    const isActiveStatus = (status) => {
      return ['receiving', 'paused', 'transcribing', 'assembling', 'summarizing', 'pending_processing', 'finalizing'].includes(status)
    }

    // Группировка сессий по датам
    const sessionsByDate = computed(() => {
      if (!sessions.value || sessions.value.length === 0) {
        return {}
      }

      const grouped = {}
      
      sessions.value.forEach(session => {
        let dateKey
        if (session.created_at) {
          const date = new Date(session.created_at)
          dateKey = date.toLocaleDateString(locale.value, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        } else {
          dateKey = t('sessions.dateUnknown')
        }

        if (!grouped[dateKey]) {
          grouped[dateKey] = []
        }
        grouped[dateKey].push(session)
      })

      // Sort sessions within each date group: active first, then by time
      Object.keys(grouped).forEach(dateKey => {
        grouped[dateKey].sort((a, b) => {
          const aActive = isActiveStatus(a.status)
          const bActive = isActiveStatus(b.status)
          if (aActive && !bActive) return -1
          if (!aActive && bActive) return 1
          const timeA = a.created_at ? new Date(a.created_at).getTime() : 0
          const timeB = b.created_at ? new Date(b.created_at).getTime() : 0
          return timeB - timeA
        })
      })

      // Сортируем даты (новые сверху)
      const sortedEntries = Object.entries(grouped).sort((a, b) => {
        const dateA = grouped[a[0]][0]?.created_at
        const dateB = grouped[b[0]][0]?.created_at
        if (!dateA) return 1
        if (!dateB) return -1
        return new Date(dateB).getTime() - new Date(dateA).getTime()
      })

      return Object.fromEntries(sortedEntries)
    })

    // Фильтрованные сессии по выбранному пользователю (для раздела dashboard)
    const filteredSessions = computed(() => {
      if (!sessions.value || sessions.value.length === 0) {
        return []
      }

      if (!selectedUserFilter.value) {
        return sessions.value
      }

      return sessions.value.filter(
        session => session.user_email === selectedUserFilter.value
      )
    })

    // Фильтрованные сессии по датам
    const filteredSessionsByDate = computed(() => {
      if (!filteredSessions.value || filteredSessions.value.length === 0) {
        return {}
      }

      const grouped = {}
      
      filteredSessions.value.forEach(session => {
        let dateKey
        if (session.created_at) {
          const date = new Date(session.created_at)
          dateKey = date.toLocaleDateString(locale.value, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        } else {
          dateKey = t('sessions.dateUnknown')
        }

        if (!grouped[dateKey]) {
          grouped[dateKey] = []
        }
        grouped[dateKey].push(session)
      })

      // Sort sessions within each date group: active first, then by time
      Object.keys(grouped).forEach(dateKey => {
        grouped[dateKey].sort((a, b) => {
          const aActive = isActiveStatus(a.status)
          const bActive = isActiveStatus(b.status)
          if (aActive && !bActive) return -1
          if (!aActive && bActive) return 1
          const timeA = a.created_at ? new Date(a.created_at).getTime() : 0
          const timeB = b.created_at ? new Date(b.created_at).getTime() : 0
          return timeB - timeA
        })
      })

      // Сортируем даты (новые сверху)
      const sortedEntries = Object.entries(grouped).sort((a, b) => {
        const dateA = grouped[a[0]][0]?.created_at
        const dateB = grouped[b[0]][0]?.created_at
        if (!dateA) return 1
        if (!dateB) return -1
        return new Date(dateB).getTime() - new Date(dateA).getTime()
      })

      return Object.fromEntries(sortedEntries)
    })

    // Статистика по фильтрованным сессиям
    const filteredSessionsStats = computed(() => {
      if (!filteredSessions.value || filteredSessions.value.length === 0) {
        return { total: 0, completed: 0, errors: 0, duration: 0 }
      }

      let completed = 0
      let errors = 0
      let duration = 0

      filteredSessions.value.forEach(session => {
        if (session.status === 'completed') {
          completed++
        } else if (session.status === 'error' || session.status === 'failed') {
          errors++
        }
        
        if (session.recording_duration) {
          duration += session.recording_duration
        }
      })

      return {
        total: filteredSessions.value.length,
        completed,
        errors,
        duration
      }
    })
    
    // Проверка активной сессии (идёт запись/приём/транскрипция)
    const isActiveSession = (status) => {
      return status && ['recording', 'receiving', 'paused', 'transcribing', 'assembling'].includes(status)
    }
    
    // Проверка что сессия в процессе (включая reprocessing) - для скрытия кнопок
    const isProcessingSession = (status) => {
      return status && ['recording', 'receiving', 'paused', 'transcribing', 'assembling', 'reprocessing'].includes(status)
    }

    // Проверка наличия активных записей у текущего пользователя
    const hasActiveRecording = computed(() => {
      if (!sessions.value || sessions.value.length === 0) {
        return false
      }
      return sessions.value.some(session => isActiveSession(session.status))
    })
    
    // Модальное окно подтверждения удаления
    const deleteConfirmModal = ref({
      show: false,
      sessionId: null,
      sessionTitle: '',
      deleteType: 'session' // 'session' | 'audio'
    })
    
    // Модальное окно подтверждения переобработки
    const reprocessConfirmModal = ref({
      show: false,
      sessionId: null,
      sessionTitle: ''
    })
    
    // Универсальное управление modal-open классом для всех модальных окон
    watch(
      [
        () => modal.value.isOpen,
        showPluginInstallModal,
        showPromptsModal,
        showSettingsModal,
        showTipsModal,
        showPrivacyModal,
        showChangelogModal,
        () => deleteConfirmModal.value.show,
        () => reprocessConfirmModal.value.show
      ],
      (newValues) => {
        // Если хотя бы одно модальное окно открыто
        const anyModalOpen = newValues.some(value => value === true)
        
        if (anyModalOpen) {
          // Вычисляем ширину скроллбара ПЕРЕД его исчезновением
          const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
          
          if (scrollbarWidth > 0) {
            // Фиксируем текущую ширину #app чтобы не было растяжения
            const app = document.querySelector('#app')
            if (app) {
              app.style.width = `${app.offsetWidth}px`
            }
            
            // Добавляем padding-right к body для компенсации
            document.body.style.paddingRight = `${scrollbarWidth}px`
            
            // Также компенсируем для fixed элементов с right позиционированием
            const fixedElements = document.querySelectorAll('.toast-notification')
            fixedElements.forEach(el => {
              const currentRight = parseInt(window.getComputedStyle(el).right) || 0
              el.style.right = `${currentRight + scrollbarWidth}px`
            })
          }
          
          document.documentElement.classList.add('modal-open')
          document.body.classList.add('modal-open')
        } else {
          // Убираем фиксированную ширину
          const app = document.querySelector('#app')
          if (app) {
            app.style.width = ''
          }
          
          // Убираем padding-right при закрытии
          document.body.style.paddingRight = ''
          
          // Также убираем у fixed элементов
          const fixedElements = document.querySelectorAll('.toast-notification')
          fixedElements.forEach(el => {
            el.style.right = ''
          })
          
          document.documentElement.classList.remove('modal-open')
          document.body.classList.remove('modal-open')
        }
      }
    )
    
    // Inline редактирование названия сессии
    const editingSessionId = ref(null)
    const editingTitle = ref('')
    const originalTitle = ref('')
    
    // Уведомления (toast)
    const toastNotification = ref({
      show: false,
      message: '',
      type: 'success' // 'success', 'error', 'info'
    })
    
    // System status
    const systemStatus = ref({
      timestamp: null,
      services: {},
      overall_status: 'unknown'
    })

    // Функции для статуса авторизации
    const showAuthStatus = (message, type = 'info', duration = 5000) => {
      authStatus.value = { message, type, visible: true }
      if (type === 'success') {
        setTimeout(() => {
          authStatus.value.visible = false
        }, duration)
      }
    }

const hideAuthStatus = () => {
  authStatus.value.visible = false
}

const getDisplayName = (user) => {
  if (!user) return t('user.defaultName')
  
  // Приоритет: given_name, name, email до @
  if (user.given_name) {
    return user.given_name
  }
  
  if (user.name) {
    // Если есть полное имя, берем первое слово
    return user.name.split(' ')[0]
  }
  
  if (user.email) {
    return user.email.split('@')[0]
  }
  
  return t('user.defaultName')
}

const getRoleDisplayName = (role) => {
  const roleKeys = {
    'owner': 'user.roles.owner',
    'administrator': 'user.roles.administrator',
    'user': 'user.roles.user'
  }
  return t(roleKeys[role] || 'user.roles.user')
}

// Обновить информацию о токене
const updateTokenTimeInfo = () => {
  tokenTimeInfo.value = authService.getTokenTimeInfo()
}

// Получить строку с оставшимся временем токена
const getTokenTimeRemaining = () => {
  if (!tokenTimeInfo.value) return ''
  
  if (tokenTimeInfo.value.expired) {
    return t('user.tokenExpired')
  }
  
  const seconds = tokenTimeInfo.value.timeRemaining
  const days = Math.floor(seconds / (24 * 60 * 60))
  const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60))
  const minutes = Math.floor((seconds % (60 * 60)) / 60)
  
  if (days > 0) {
    return `${t('user.tokenValid')} ${days}${t('stats.day')} ${hours}${t('stats.hour')}`
  } else if (hours > 0) {
    return `${t('user.tokenValid')} ${hours}${t('stats.hour')} ${minutes}${t('stats.minute')}`
  } else {
    return `${t('user.tokenValid')} ${minutes}${t('stats.minute')}`
  }
}

// Получить текст для tooltip
const getTokenTooltip = () => {
  if (!tokenTimeInfo.value) return ''
  
  const expiresDate = new Date(tokenTimeInfo.value.expiresAt)
  const expiresStr = expiresDate.toLocaleString(locale.value, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
  
  let tooltip = `Токен истекает: ${expiresStr}`
  
  if (tokenTimeInfo.value.lastRefreshed) {
    const refreshedDate = new Date(tokenTimeInfo.value.lastRefreshed)
    const refreshedStr = refreshedDate.toLocaleString(locale.value, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    tooltip += `\nПоследнее обновление: ${refreshedStr}`
  }
  
  if (tokenTimeInfo.value.percentRemaining < 20) {
    tooltip += '\n⚠ Токен скоро истечет, рекомендуется переавторизоваться'
  }
  
  return tooltip
}

const formatSessionId = (sessionId) => {
  if (!sessionId || sessionId.length <= 8) {
    return sessionId || 'N/A'
  }
  
  // Показываем первые 2 и последние 6 символов
  const start = sessionId.substring(0, 2)
  const end = sessionId.substring(sessionId.length - 6)
  return `${start}...${end}`
}

// System Status Functions
const loadSystemStatus = async () => {
  try {
    const response = await apiRequest('/api/status')
    if (response.ok) {
      const data = await response.json()
      systemStatus.value = data
    }
  } catch (error) {
    console.error('Failed to load system status:', error)
  }
}

const getServiceStatusClass = (serviceName) => {
  const service = systemStatus.value.services[serviceName]
  if (!service) return 'status-unknown'
  
  switch (service.status) {
    case 'healthy':
      return 'status-healthy'
    case 'warning':
    case 'degraded':
      return 'status-warning'
    case 'error':
    case 'critical':
    case 'unavailable':
      return 'status-error'
    default:
      return 'status-unknown'
  }
}

const getServiceStatusText = (serviceName) => {
  const service = systemStatus.value.services[serviceName]
  if (!service) return '—'
  
  if (service.status === 'healthy') return 'OK'
  if (service.status === 'warning' || service.status === 'degraded') return 'WARN'
  if (service.status === 'error' || service.status === 'critical' || service.status === 'unavailable') return 'ERR'
  return '?'
}

const formatStatusTime = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp * 1000)
  return date.toLocaleTimeString('ru-RU')
}

// Функции для определения общего статуса системы
const getOverallStatusClass = () => {
  const services = systemStatus.value.services
  if (!services || Object.keys(services).length === 0) return 'status-unknown'
  
  // Если хотя бы один сервис в error/critical/unavailable
  const hasError = Object.values(services).some(s => 
    ['error', 'critical', 'unavailable'].includes(s.status)
  )
  if (hasError) return 'status-error'
  
  // Если хотя бы один сервис в warning/degraded
  const hasWarning = Object.values(services).some(s => 
    ['warning', 'degraded'].includes(s.status)
  )
  if (hasWarning) return 'status-warning'
  
  // Если все healthy
  const allHealthy = Object.values(services).every(s => s.status === 'healthy')
  if (allHealthy) return 'status-healthy'
  
  return 'status-unknown'
}

const getOverallStatusIcon = () => {
  const statusClass = getOverallStatusClass()
  if (statusClass === 'status-healthy') return 'OK'
  if (statusClass === 'status-warning') return 'WARN'
  if (statusClass === 'status-error') return 'ERR'
  return '◉'
}

const getOverallStatusTitle = () => {
  const statusClass = getOverallStatusClass()
  if (statusClass === 'status-healthy') return t('system.allSystemsHealthy')
  if (statusClass === 'status-warning') return t('system.someSystemsWarning')
  if (statusClass === 'status-error') return t('system.systemsHaveErrors')
  return t('system.statusUnknown')
}

// Закрытие dropdown при клике вне их
const handleClickOutside = (event) => {
  if (showStatusDropdown.value && !event.target.closest('.system-status-dropdown')) {
    showStatusDropdown.value = false
  }
  if (showUserDropdown.value && !event.target.closest('.user-dropdown')) {
    showUserDropdown.value = false
  }
}

    // Получить токен из localStorage (с автоматическим обновлением)
    const getAuthToken = async () => {
      // ИСПРАВЛЕНИЕ: Используем authService.getToken() для автоматического обновления токена
      return await authService.getToken()
    }

    // Сохранить токен
    const setAuthToken = (token) => {
      localStorage.setItem('admin_token', token)
    }

    // API запрос с авторизацией
    const apiRequest = async (url, options = {}) => {
      const token = await getAuthToken()  // await для асинхронного вызова
      const headers = {
        ...options.headers,
        'Content-Type': 'application/json',
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(url, {
        ...options,
        headers,
      })

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('admin_token')
        isAuthenticated.value = false
        adminUser.value = null
        // Принудительно перезагружаем Google кнопку при ошибке авторизации
        setTimeout(() => {
          setTimeout(initGoogleSignIn, 100)
        }, 100)
      }
      return response
    }

    // Загрузка информации о пользователе
    const loadUserInfo = async () => {
      try {
        const response = await apiRequest('/auth/me')
        if (response && response.ok) {
          const userData = await response.json()
          adminUser.value = userData.user
        }
      } catch (error) {
        console.error('Error loading user info:', error)
      }
    }

    // Google OAuth авторизация
    const handleCredentialResponse = async (response) => {
      showAuthStatus(t('auth.checkingAuth'), 'info')
      try {
        const authResponse = await fetch('/auth/google', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            provider: 'google',
            token: response.credential
          })
        })

        const data = await authResponse.json()
        
        if (data.success) {
          // Сохраняем токены через authService (включая refresh_token)
          authService.setAuthData(data.token, data.refresh_token, data.user)
          setAuthToken(data.token)  // Для обратной совместимости
          updateTokenTimeInfo()  // Обновляем информацию о токене
          
          // Сразу проверяем доступ к админке
          showAuthStatus(t('auth.checkingPermissions'), 'info')
          
          try {
            const checkResponse = await apiRequest('/auth/me')
            if (checkResponse && checkResponse.ok) {
              const userData = await checkResponse.json()
              
              // ✅ ИСПРАВЛЕНИЕ: Проверяем права доступа ДО показа интерфейса
              // Делаем пробный запрос к /admin/my-sessions чтобы убедиться что пользователь имеет доступ
              try {
                const accessCheck = await apiRequest('/admin/my-sessions?page=1&per_page=1')
                if (accessCheck.ok) {
                  // У пользователя есть доступ - показываем админку сразу
                  adminUser.value = userData.user
                  updateTokenTimeInfo()  // ✨ Обновляем информацию о токене
                  showAuthStatus(t('auth.loginSuccess'), 'success')
                  
                  // Сразу переходим к админке - скелетон покажется автоматически
                  isAuthenticated.value = true
                  
                  // ✅ Загружаем статусы системы и данные (loading=true покажет скелетон)
                  await loadSystemStatus()
                  await loadSectionData()
                  
                  setTimeout(() => {
                    hideAuthStatus()
                  }, 1000)
                } else {
                  // Пользователь не имеет доступа к системе
                  localStorage.removeItem('admin_token')
                  showAuthStatus(t('auth.accessDenied'), 'error')
                  setTimeout(() => {
                    hideAuthStatus()
                    // Принудительно перезагружаем Google кнопку
                    setTimeout(initGoogleSignIn, 100)
                  }, 3000)
                }
              } catch (accessErr) {
                // Ошибка проверки доступа (403 или другая)
                localStorage.removeItem('admin_token')
                showAuthStatus('У вас нет доступа к системе', 'error')
                setTimeout(() => {
                  hideAuthStatus()
                  setTimeout(initGoogleSignIn, 100)
                }, 3000)
              }
            } else {
              // Пользователь не имеет доступа к системе
              localStorage.removeItem('admin_token')
              showAuthStatus('У вас нет доступа к системе', 'error')
              setTimeout(() => {
                hideAuthStatus()
                // Принудительно перезагружаем Google кнопку
                setTimeout(initGoogleSignIn, 100)
              }, 3000)
            }
          } catch (checkErr) {
            localStorage.removeItem('admin_token')
            showAuthStatus(t('auth.permissionCheckError'), 'error')
            setTimeout(() => {
              hideAuthStatus()
              setTimeout(initGoogleSignIn, 100)
            }, 3000)
          }
        } else {
          error.value = data.error || t('auth.authError')
          showAuthStatus(error.value, 'error')
          setTimeout(() => {
            hideAuthStatus()
            setTimeout(initGoogleSignIn, 100)
          }, 3000)
        }
      } catch (err) {
        error.value = t('auth.connectionError')
        showAuthStatus(error.value, 'error')
        setTimeout(() => {
          hideAuthStatus()
          setTimeout(initGoogleSignIn, 100)
        }, 3000)
      }
    }

    // Выход из системы
    const handleLogout = () => {
      authService.logout()  // Используем authService для правильной очистки
      localStorage.removeItem('admin_token')  // На всякий случай очищаем старый токен
      isAuthenticated.value = false
      adminUser.value = null
      tokenTimeInfo.value = null  // Очищаем информацию о токене
      showAuthStatus(t('auth.loggedOut'), 'info')
      // Перезагружаем Google кнопку после выхода
      setTimeout(() => {
        hideAuthStatus()
        setTimeout(initGoogleSignIn, 100)
      }, 2000)
    }

    // Сохранение настроек транскрипции
    const saveTranscriptionSettings = async () => {
      try {
        console.log(`[Settings] Сохранение: temperature=${whisperTemperature.value}, quality_mode=${useQualityMode.value}, save_audio=${saveAudioRecording.value}, auto_delete=${autoDeleteOldestAudio.value}`)

        // Отправляем на бекенд
        const response = await apiRequest('/user/settings', {
          method: 'PUT',
          body: JSON.stringify({
            whisper_temperature: whisperTemperature.value,
            use_quality_mode: useQualityMode.value,
            save_audio_recording: saveAudioRecording.value,
            auto_delete_oldest_audio: autoDeleteOldestAudio.value
          })
        })

        if (response.ok) {
          const result = await response.json()
          console.log('[Settings] ✅ Настройки успешно сохранены на сервере:', result)
          showAuthStatus(t('settings.settingsSaved'), 'success', 2000)
        } else if (response.status === 409) {
          // 🔧 ИСПРАВЛЕНИЕ: Обрабатываем конфликт (активная запись)
          const errorData = await response.json()
          const errorMsg = errorData.detail || t('settings.cannotChangeWhileRecording')
          console.warn('[Settings] ⚠️ Нельзя изменить настройки во время записи')
          showAuthStatus(errorMsg, 'warning', 4000)
          // Перезагружаем настройки с сервера для отката изменений
          await loadTranscriptionSettings()
        } else {
          throw new Error(`Ошибка сохранения: ${response.status}`)
        }
      } catch (error) {
        console.error('[Settings] Ошибка сохранения:', error)
        showAuthStatus(t('settings.settingsSaveError'), 'error')
      }
    }

    // Сброс настроек к значениям по умолчанию
    const resetToDefaults = () => {
      whisperTemperature.value = 0.0
      useQualityMode.value = false // 🆕
      saveTranscriptionSettings()
      showAuthStatus(t('settings.settingsSaved'), 'info', 2000)
    }

    // Загрузка настроек транскрипции
    const loadTranscriptionSettings = async () => {
      try {
        // Загружаем с бекенда
        const response = await apiRequest('/user/settings')
        if (response.ok) {
          const settings = await response.json()
          whisperTemperature.value = settings.whisper_temperature ?? 0.0
          useQualityMode.value = settings.use_quality_mode ?? false
          saveAudioRecording.value = settings.save_audio_recording ?? false
          autoDeleteOldestAudio.value = settings.auto_delete_oldest_audio ?? false
          console.log(`[Settings] ✅ Загружено с сервера: temperature=${whisperTemperature.value}, quality_mode=${useQualityMode.value}, save_audio=${saveAudioRecording.value}, auto_delete=${autoDeleteOldestAudio.value}`)
        } else {
          console.log('[Settings] Настройки не найдены на сервере, используем значение по умолчанию')
          whisperTemperature.value = 0.0
          useQualityMode.value = false
          saveAudioRecording.value = false
          autoDeleteOldestAudio.value = false
        }
      } catch (error) {
        console.error('[Settings] Ошибка загрузки:', error)
      }
    }

    // Загрузка информации о хранилище
    const loadStorageInfo = async () => {
      try {
        const response = await apiRequest('/user/storage')
        if (response.ok) {
          const data = await response.json()
          storageInfo.value = data
          console.log(`[Storage] Loaded: ${data.used_mb}/${data.limit_mb} MB (${data.percent_used}%)`)
        }
      } catch (error) {
        console.error('[Storage] Error loading:', error)
      }
    }
    
    // Загрузка личных сессий пользователя
    // Умное слияние сессий (без моргания)
    const mergeSessions = (oldSessions, newSessions) => {
      const sessionMap = new Map()
      
      // Добавляем старые сессии в карту
      oldSessions.forEach(session => {
        const id = session.session_id || session.id
        sessionMap.set(id, session)
      })
      
      let addedCount = 0
      let updatedCount = 0
      
      // Обновляем или добавляем новые
      newSessions.forEach(newSession => {
        const id = newSession.session_id || newSession.id
        const existingSession = sessionMap.get(id)
        
        if (existingSession) {
          // Обновляем только если есть изменения
          const hasChanges = 
            existingSession.status !== newSession.status ||
            existingSession.ttl_seconds !== newSession.ttl_seconds ||
            existingSession.tab_title !== newSession.tab_title ||
            existingSession.has_text !== newSession.has_text ||
            existingSession.has_summary !== newSession.has_summary
          
          if (hasChanges) {
            // ✅ ИСПРАВЛЕНИЕ: Создаем новый объект вместо мутации существующего
            // Это заставит Vue transition-group увидеть изменение
            sessionMap.set(id, { ...existingSession, ...newSession })
            updatedCount++
          }
        } else {
          // Добавляем новую сессию
          sessionMap.set(id, newSession)
          addedCount++
        }
      })
      
      // НЕ удаляем сессии сразу - они могут быть в процессе создания
      // Удаляем только если сессия отсутствует 2+ раза подряд
      const newIds = new Set(newSessions.map(s => s.session_id || s.id))
      let removedCount = 0
      for (const [id] of sessionMap.entries()) {
        if (!newIds.has(id)) {
          // Проверяем - может это только что созданная сессия
          const session = sessionMap.get(id)
          
          // 🔒 ИСПРАВЛЕНИЕ: Анонимные сессии (без user_email) удаляем сразу
          // Они управляются плагином и могут быть удалены в любой момент
          const isAnonymous = !session.user_email
          
          // Не удаляем активные сессии сразу (кроме анонимных)
          const isActive = ['recording', 'receiving', 'processing', 'pending_processing', 'assembling', 'transcribing', 'summarizing'].includes(session.status)
          
          if (session && (isAnonymous || !isActive)) {
            sessionMap.delete(id)
            removedCount++
            if (isAnonymous) {
              console.log('[MergeSessions] Удалена анонимная сессия:', id, 'статус:', session.status)
            }
          }
        }
      }
      
      // Логируем только если были изменения
      if (addedCount > 0 || updatedCount > 0 || removedCount > 0) {
        console.log('[MergeSessions] Added:', addedCount, 'Updated:', updatedCount, 'Removed:', removedCount, 'Total:', sessionMap.size)
      }
      
      return Array.from(sessionMap.values())
    }
    
    // Флаг для отслеживания первой загрузки секции
    const sectionLoadedBefore = ref({
      'my-sessions': false,
      'dashboard': false
    })
    
    const loadMySessions = async () => {
      const isInitialLoad = !sectionLoadedBefore.value['my-sessions']
      if (isInitialLoad) {
        loading.value = true
        sectionLoadedBefore.value['my-sessions'] = true
      }
      apiError.value = ''
      
      try {
        if (isInitialLoad) {
          await loadUserInfo()
        }
        
        // Initial load: first page only. Polling: refresh already loaded count.
        const perPage = isInitialLoad ? sessionsPerPage.value : Math.max(sessions.value.length, sessionsPerPage.value)
        const sessionsResponse = await apiRequest(`/admin/my-sessions?page=1&per_page=${perPage}`)
        if (sessionsResponse.ok) {
          const sessionsData = await sessionsResponse.json()
          
          sessionsTotal.value = sessionsData.total || 0
          sessionsTotalPages.value = Math.max(1, Math.ceil(sessionsTotal.value / sessionsPerPage.value))
          if (isInitialLoad) {
            sessionsPage.value = 1
          }
          
          const processedSessions = (sessionsData.sessions || []).map(session => ({
            ...session,
            created_at: session.created_at ? session.created_at * 1000 : null
          }))
          
          if (isInitialLoad) {
            sessions.value = processedSessions
          } else {
            const merged = mergeSessions(sessions.value, processedSessions)
            sessions.value = [...merged]
          }
        } else {
          apiError.value = `Ошибка загрузки записей: ${sessionsResponse.status}`
        }
      } catch (err) {
        if (isInitialLoad) {
          apiError.value = `Ошибка загрузки данных: ${err.message}`
        } else {
          console.warn('[AutoUpdate] Failed to refresh sessions:', err.message)
        }
      } finally {
        if (isInitialLoad) {
          loading.value = false
        }
      }
    }

    // Реактивная загрузка данных в зависимости от секции
    let previousSection = ref('my-sessions')
    
    const loadSectionData = async (clearData = false) => {
      // Если секция изменилась - сбрасываем флаг для полной перезагрузки
      if (previousSection.value !== currentSection.value) {
        console.log(`[SectionSwitch] ${previousSection.value} → ${currentSection.value}, сброс данных`)
        sectionLoadedBefore.value[currentSection.value] = false
        previousSection.value = currentSection.value
        clearData = true // При переключении секций всегда очищаем
      }
      
      if (clearData) {
        sessions.value = []
        sessionsPage.value = 1
        sessionsTotalPages.value = 1
        sessionsTotal.value = 0
      }
      
      if (currentSection.value === 'my-sessions') {
        await loadMySessions()
      } else if (currentSection.value === 'dashboard') {
        await loadData()
      }
    }

    // Загрузка данных (для Owner - статистика и все сессии)
    const loadData = async () => {
      const isInitialLoad = !sectionLoadedBefore.value['dashboard']
      if (isInitialLoad) {
        loading.value = true
        sectionLoadedBefore.value['dashboard'] = true
      }
      apiError.value = ''
      
      // ✅ ВАЖНО: Сбрасываем фильтр пользователей только при первой загрузке dashboard
      if (isInitialLoad) {
        selectedUserFilter.value = ''
      }
      
      try {
        // Загружаем информацию о пользователе
        if (isInitialLoad) {
          await loadUserInfo()
        }
        
        // Загружаем статистику
        const statsResponse = await apiRequest('/admin/stats')
        if (statsResponse.ok) {
          stats.value = await statsResponse.json()
          console.log('[Stats] Loaded:', stats.value)
          if (stats.value.plugin_stats) {
            console.log('[PluginStats] Loaded:', stats.value.plugin_stats)
          }
        } else {
          apiError.value = `Ошибка загрузки статистики: ${statsResponse.status}`
        }

        // Initial load: first page only. Polling: refresh already loaded count.
        const perPage = isInitialLoad ? sessionsPerPage.value : Math.max(sessions.value.length, sessionsPerPage.value)
        const sessionsResponse = await apiRequest(`/admin/sessions?page=1&per_page=${perPage}`)
        if (sessionsResponse.ok) {
          const sessionsData = await sessionsResponse.json()
          
          sessionsTotal.value = sessionsData.total || 0
          sessionsTotalPages.value = Math.max(1, Math.ceil(sessionsTotal.value / sessionsPerPage.value))
          if (isInitialLoad) {
            sessionsPage.value = 1
          }
          
          const processedSessions = (sessionsData.sessions || []).map(session => ({
            ...session,
            created_at: session.created_at ? session.created_at * 1000 : null
          }))
          
          if (isInitialLoad) {
            sessions.value = processedSessions
          } else {
            const merged = mergeSessions(sessions.value, processedSessions)
            sessions.value = [...merged]
          }
        } else {
          apiError.value = `Ошибка загрузки сессий: ${sessionsResponse.status}`
        }
      } catch (err) {
        if (isInitialLoad) {
          apiError.value = `Ошибка загрузки данных: ${err.message}`
        } else {
          console.warn('[AutoUpdate] Failed to refresh data:', err.message)
        }
      } finally {
        if (isInitialLoad) {
          loading.value = false
        }
      }
    }

    const loadMoreSessions = async () => {
      if (loadingMore.value || sessionsPage.value >= sessionsTotalPages.value) return
      loadingMore.value = true
      try {
        const nextPage = sessionsPage.value + 1
        const endpoint = currentSection.value === 'dashboard'
          ? '/admin/sessions' : '/admin/my-sessions'
        const resp = await apiRequest(`${endpoint}?page=${nextPage}&per_page=${sessionsPerPage.value}`)
        if (resp.ok) {
          const data = await resp.json()
          const processed = (data.sessions || []).map(s => ({
            ...s,
            created_at: s.created_at ? s.created_at * 1000 : null
          }))
          sessions.value = [...sessions.value, ...processed]
          sessionsPage.value = nextPage
          sessionsTotalPages.value = data.total_pages || sessionsTotalPages.value
          sessionsTotal.value = data.total || sessionsTotal.value
        }
      } catch (err) {
        console.warn('[LoadMore] Failed:', err.message)
      } finally {
        loadingMore.value = false
      }
    }

    // Открытие модального окна настроек с обновлением состояния сессий
    const openSettingsModal = async () => {
      showUserDropdown.value = false
      
      // Обновляем список сессий для актуализации hasActiveRecording
      if (currentSection.value === 'my-sessions') {
        await loadMySessions()
      } else if (currentSection.value === 'dashboard') {
        await loadData()
      }
      
      // Загружаем информацию о хранилище
      loadStorageInfo()
      
      showSettingsModal.value = true
      console.log('[Settings] Modal opened, hasActiveRecording:', hasActiveRecording.value)
    }
    
    // Загрузка Privacy Policy
    const loadPrivacyPolicy = async () => {
      if (privacyContent.value) return // Уже загружено
      
      privacyLoading.value = true
      privacyError.value = false
      try {
        // Используем ?raw=true для получения контента без стилей
        const response = await fetch('/api/privacy?raw=true')
        if (response.ok) {
          const htmlContent = await response.text()
          privacyContent.value = htmlContent
        } else {
          console.error('Failed to load privacy policy:', response.status)
          privacyError.value = true
        }
      } catch (err) {
        console.error('Error loading privacy policy:', err)
        privacyError.value = true
      } finally {
        privacyLoading.value = false
      }
    }
    
    // Загрузка changelog
    const loadChangelog = async () => {
      changelogLoading.value = true
      try {
        const response = await fetch('/api/changelog')
        if (response.ok) {
          const data = await response.json()
          changelog.value = data
        } else {
          console.error('Failed to load changelog:', response.status)
          showToast(t('changelog.loadError'), 'error')
        }
      } catch (err) {
        console.error('Error loading changelog:', err)
        showToast(t('changelog.loadError'), 'error')
      } finally {
        changelogLoading.value = false
      }
    }

    // Форматирование даты для changelog
    const formatChangelogDate = (dateString) => {
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString(locale.value, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (e) {
        return dateString
      }
    }

    // Состояние редактора changelog
    const editingChangelog = ref(null)

    // Начать добавление новой записи
    const startAddingChangelog = () => {
      const today = new Date()
      const dateStr = today.toISOString().split('T')[0] // YYYY-MM-DD
      
      editingChangelog.value = {
        isNew: true,
        component: 'plugin',
        version: '',
        changesText: '',
        date: new Date().toISOString(),
        dateInput: dateStr // Для input type="date"
      }
    }

    // Начать редактирование существующей записи
    const startEditingChangelog = (component, entry) => {
      // Конвертируем ISO дату в YYYY-MM-DD для input type="date"
      const dateStr = entry.date ? new Date(entry.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
      
      editingChangelog.value = {
        isNew: false,
        component: component,
        version: entry.version,
        changesText: entry.changes.join('\n'),
        date: entry.date,
        dateInput: dateStr
      }
    }

    // Отменить редактирование
    const cancelEditingChangelog = () => {
      editingChangelog.value = null
    }

    // Сохранить changelog
    const saveChangelog = async () => {
      if (!editingChangelog.value) return

      const { component, version, changesText, dateInput } = editingChangelog.value

      // Валидация
      if (!version.trim()) {
        showToast(t('changelog.versionRequired'), 'error')
        return
      }

      if (!changesText.trim()) {
        showToast(t('changelog.changesRequired'), 'error')
        return
      }

      // Парсим изменения
      const changes = changesText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)

      if (changes.length === 0) {
        showToast(t('changelog.changesRequired'), 'error')
        return
      }

      // Конвертируем дату из input (YYYY-MM-DD) в ISO формат
      let isoDate
      if (dateInput) {
        // Создаем дату в UTC из строки YYYY-MM-DD
        const [year, month, day] = dateInput.split('-').map(Number)
        isoDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString()
      } else {
        isoDate = new Date().toISOString()
      }

      try {
        const response = await apiRequest('/api/changelog/publish', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            component,
            version,
            changes,
            date: isoDate
          })
        })

        if (response.ok) {
          showToast('Changelog успешно сохранён', 'success')
          editingChangelog.value = null
          await loadChangelog() // Перезагрузить список
        } else {
          const error = await response.json()
          showToast(`Ошибка: ${error.detail || 'Не удалось сохранить'}`, 'error')
        }
      } catch (err) {
        console.error('Error saving changelog:', err)
        showToast(t('changelog.saveError'), 'error')
      }
    }

    // Удалить changelog запись
    const deleteChangelog = async (component, version) => {
      if (!confirm(t('changelog.confirmDeleteEntry', { component, version }))) {
        return
      }

      try {
        const response = await apiRequest(`/api/changelog/${component}/${version}`, {
          method: 'DELETE'
        })

        if (response.ok) {
          showToast(t('changelog.deleted'), 'success')
          await loadChangelog() // Перезагрузить список
        } else {
          const error = await response.json()
          showToast(`Ошибка: ${error.detail || 'Не удалось удалить'}`, 'error')
        }
      } catch (err) {
        console.error('Error deleting changelog:', err)
        showToast(t('changelog.deleteError'), 'error')
      }
    }

    // Инициализация Google Sign-In
    const initGoogleSignIn = () => {
      if (window.google && document.getElementById("google-signin-button")) {
        window.google.accounts.id.initialize({
          client_id: "564813948287-p0hn5kd8vuopjr2ognofjssnvo7qjarv.apps.googleusercontent.com",
          callback: handleCredentialResponse
        })
        window.google.accounts.id.renderButton(
          document.getElementById("google-signin-button"),
          { 
            type: "standard", 
            size: "large", 
            theme: "outline", 
            text: "signin_with", 
            shape: "rectangular", 
            logo_alignment: "left" 
          }
        )
      } else {
        // Если элемент еще не готов, попробуем еще раз через 100мс
        setTimeout(initGoogleSignIn, 100)
      }
    }

    // Проверка существующей авторизации
    const checkAuth = async () => {
      const token = await getAuthToken()  // ✅ await для асинхронного вызова
      if (token) {
        showAuthStatus(t('auth.checkingSavedAuth'), 'info')
        try {
          // Используем /auth/me вместо /admin/stats для проверки авторизации
          // так как /auth/me доступен всем авторизованным пользователям
          const response = await apiRequest('/auth/me')
          if (response.ok) {
            const userData = await response.json()
            
            // ✅ ИСПРАВЛЕНИЕ: Проверяем права доступа через загрузку данных
            showAuthStatus(t('auth.checkingPermissions'), 'info')
            try {
              // У пользователя есть доступ - показываем интерфейс
              adminUser.value = userData.user
              updateTokenTimeInfo()  // ✨ Обновляем информацию о токене
              
              // Переходим к админке (скелетон покажется автоматически)
              hideAuthStatus()
              isAuthenticated.value = true
              
              // ✅ Загружаем статусы системы и данные (loading=true покажет скелетон)
              await loadSystemStatus()
              await loadSectionData()
            } catch (accessErr) {
              // Ошибка загрузки данных или проверки доступа
              localStorage.removeItem('admin_token')
              showAuthStatus('У вас нет доступа к системе', 'error')
              loading.value = false  // ✅ Останавливаем loading для показа формы логина
              setTimeout(() => {
                hideAuthStatus()
                setTimeout(initGoogleSignIn, 100)
              }, 3000)
            }
          } else {
            loading.value = false  // ✅ Останавливаем loading для показа формы логина
            showAuthStatus(t('auth.reAuthRequired'), 'warning')
            setTimeout(() => {
              hideAuthStatus()
            }, 2000)
          }
        } catch (err) {
          loading.value = false  // ✅ Останавливаем loading для показа формы логина
          showAuthStatus(t('auth.reAuthRequired'), 'warning')
          setTimeout(() => {
            hideAuthStatus()
          }, 2000)
        }
      } else {
        // ✅ Нет токена - показываем форму логина
        loading.value = false
      }
    }

    // Инициализация при загрузке
    onMounted(async () => {
      initTheme()
      await checkAuth()
      await loadTranscriptionSettings() // Загружаем настройки транскрипции
      
      // Обработчик для закрытия dropdown при клике вне их
      document.addEventListener('click', handleClickOutside)
      
      // 🎯 Обработка якорей в URL для перехода к конкретной сессии
      if (window.location.hash && window.location.hash.startsWith('#session-')) {
        const sessionId = window.location.hash.substring(1) // Убираем #
        console.log('[Frontend] 🎯 Detected anchor for session:', sessionId)
        
        // Даём время на загрузку данных и рендер
        await nextTick()
        setTimeout(() => {
          const element = document.getElementById(sessionId)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
            // Добавляем визуальное выделение
            element.classList.add('highlight-session')
            setTimeout(() => element.classList.remove('highlight-session'), 3000)
            console.log('[Frontend] ✅ Scrolled to session:', sessionId)
          } else {
            console.warn('[Frontend] ⚠️ Session element not found:', sessionId)
          }
        }, 1500) // Даём время на загрузку сессий с сервера
      }
      
      // Обновляем информацию о токене при загрузке
      updateTokenTimeInfo()
      
      // 🔧 Миграция старых данных: если есть только admin_token без admin_auth_data
      const oldToken = localStorage.getItem('admin_token')
      const authData = authService.getAuthData()
      if (oldToken && (!authData || !authData.refreshToken)) {
        console.log('[Init] Обнаружен старый формат данных, требуется переавторизация для получения refresh_token')
        // Показываем уведомление пользователю
        if (isAuthenticated.value) {
          console.warn('[Init] ⚠️ Для получения refresh_token необходимо выйти и войти заново')
        }
      }
      
      // 🔄 Фоновое обновление токенов каждые 30 минут
      setInterval(async () => {
        if (isAuthenticated.value) {
          try {
            console.log('[Frontend] 🔄 Background token refresh check...')
            const token = await authService.getToken()
            if (token) {
              console.log('[Frontend] ✅ Token is valid or successfully refreshed')
              updateTokenTimeInfo()
            } else {
              console.warn('[Frontend] ⚠️ Token refresh failed, user needs to re-authenticate')
              // Токен истёк и не удалось обновить
              isAuthenticated.value = false
              adminUser.value = null
              showAuthStatus(t('auth.reAuthRequired'), 'warning')
              setTimeout(() => {
                hideAuthStatus()
                initGoogleSignIn()
              }, 3000)
            }
          } catch (error) {
            console.error('[Frontend] Error during background token refresh:', error)
          }
        }
      }, 1800000) // 30 минут (30 * 60 * 1000)
      
      // Интервал для обновления информации о токене каждую минуту
      setInterval(() => {
        if (isAuthenticated.value) {
          updateTokenTimeInfo()
        }
      }, 60000) // 60 секунд
      
      // Интервал для обновления таймеров активных сессий (только если есть активные)
      setInterval(() => {
        // Проверяем есть ли активные сессии
        const hasActive = sessions.value.some(s => isActiveSession(s.status))
        if (hasActive) {
          // Триггерим реактивность только если есть активные записи - таймер обновляется каждую секунду
          // Запросы на сервер идут отдельно раз в 10 секунд (см. loadSectionData ниже)
          sessions.value = [...sessions.value]
        }
      }, 1000) // 1 секунда для плавного таймера, реальные запросы - раз в 10 сек
      
      // Автообновление данных сессий - адаптивный интервал
      let pollCounter = 0
      setInterval(async () => {
        if (isAuthenticated.value && (currentSection.value === 'my-sessions' || currentSection.value === 'dashboard')) {
          pollCounter++
          
          // Если есть активные сессии - обновляем каждый раз (каждые 5 сек)
          // Если нет активных сессий - обновляем раз в 2 итерации (каждые 10 сек)
          const hasActive = sessions.value.some(s => isActiveSession(s.status))
          if (hasActive || pollCounter % 2 === 0) {
            await loadSectionData()
          }
        }
      }, 5000) // Базовый интервал 5 сек, но для неактивных сессий эффективно 10 сек
      
      // ✅ Автообновление статусов системы каждые 30 секунд (если авторизован)
      setInterval(async () => {
        if (isAuthenticated.value) {
          await loadSystemStatus()
        }
      }, 30000)
      
      // ❄️ Инициализация снежинок для header
      nextTick(() => {
        const headerElement = document.querySelector('.header')
        if (headerElement) {
          initSnowfall(headerElement)
          console.log('❄️ Snowfall initialized for header')
        }
      })
      
      // Ждем загрузки Google OAuth скрипта
      const checkGoogleLoaded = () => {
        if (window.google) {
          initGoogleSignIn()
        } else {
          setTimeout(checkGoogleLoaded, 100)
        }
      }
      checkGoogleLoaded()
    })

    // Infinite scroll: observe sentinel elements to trigger loadMoreSessions
    let scrollObserver = null

    const setupScrollObserver = () => {
      if (scrollObserver) scrollObserver.disconnect()
      scrollObserver = new IntersectionObserver((entries) => {
        const visible = entries.some(e => e.isIntersecting)
        if (visible && !loadingMore.value && sessionsPage.value < sessionsTotalPages.value) {
          loadMoreSessions()
        }
      }, { rootMargin: '200px' })
    }

    watch([scrollSentinel, scrollSentinelDash], ([el1, el2]) => {
      if (!scrollObserver) setupScrollObserver()
      scrollObserver.disconnect()
      if (el1) scrollObserver.observe(el1)
      if (el2) scrollObserver.observe(el2)
    })

    onBeforeUnmount(() => {
      destroySnowfall()
      if (scrollObserver) scrollObserver.disconnect()
    })

    // Форматирование времени записи для активных сессий
    const formatRecordingTime = (createdAt) => {
      if (!createdAt) return '00:00:00'
      
      const now = Date.now()
      const created = new Date(createdAt).getTime()
      const diffMs = now - created
      
      const hours = Math.floor(diffMs / (1000 * 60 * 60))
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000)
      
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    // Форматирование длительности для завершенных сессий
    const formatDuration = (durationSeconds) => {
      if (!durationSeconds || durationSeconds <= 0) return '—'
      
      const hours = Math.floor(durationSeconds / 3600)
      const minutes = Math.floor((durationSeconds % 3600) / 60)
      const seconds = Math.floor(durationSeconds % 60)
      
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    // Форматирование TTL (время до удаления)
    const formatTTL = (ttlSeconds) => {
      if (!ttlSeconds || ttlSeconds <= 0) return '—'
      
      const hours = Math.floor(ttlSeconds / 3600)
      const minutes = Math.floor((ttlSeconds % 3600) / 60)
      const seconds = ttlSeconds % 60
      
      const hourUnit = locale.value === 'ru' ? 'ч' : 'h'
      const minUnit = locale.value === 'ru' ? 'м' : 'm'
      const secUnit = locale.value === 'ru' ? 'с' : 's'
      
      if (hours > 0) {
        return `${hours}${hourUnit}`
      } else if (minutes > 0) {
        return `${minutes}${minUnit}`
      } else {
        return `${seconds}${secUnit}`
      }
    }

    // Форматирование длительности в формате ЧЧ:ММ:СС (для таблицы)
    const formatDurationHMS = (durationSeconds) => {
      if (!durationSeconds || durationSeconds <= 0) return '00:00:00'
      
      const hours = Math.floor(durationSeconds / 3600)
      const minutes = Math.floor((durationSeconds % 3600) / 60)
      const seconds = Math.floor(durationSeconds % 60)
      
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    // Форматирование общей длительности (более читаемый формат)
    const formatTotalDuration = (durationSeconds) => {
      const hourUnit = locale.value === 'ru' ? 'ч' : 'h'
      const minUnit = locale.value === 'ru' ? 'м' : 'm'
      
      if (!durationSeconds || durationSeconds <= 0) return `0${hourUnit} 0${minUnit}`
      
      const hours = Math.floor(durationSeconds / 3600)
      const minutes = Math.floor((durationSeconds % 3600) / 60)
      
      if (hours > 0) {
        return `${hours}${hourUnit} ${minutes}${minUnit}`
      }
      return `${minutes}${minUnit}`
    }
    
    const formatDateTime = (isoString) => {
      if (!isoString) return '—'
      try {
        const date = new Date(isoString)
        const now = new Date()
        const diffMs = now - date
        const diffMins = Math.floor(diffMs / 60000)
        
        // Если меньше часа - показываем "X минут назад"
        if (diffMins < 60) {
          return `${diffMins} ${t('stats.minutesAgo')}`
        }
        
        // Если меньше 24 часов - показываем "X часов назад"
        const diffHours = Math.floor(diffMins / 60)
        if (diffHours < 24) {
          return `${diffHours} ${t('stats.hoursAgo')}`
        }
        
        // Если меньше 7 дней - показываем "X дней назад"
        const diffDays = Math.floor(diffHours / 24)
        if (diffDays < 7) {
          return `${diffDays} д назад`
        }
        
        // Иначе показываем дату
        return date.toLocaleDateString(locale.value, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      } catch (e) {
        return '—'
      }
    }
    
    const getCountryFlag = (countryCode) => {
      if (!countryCode || countryCode.length !== 2) return '🌍'
      
      // Конвертируем ISO код в emoji флаг
      // Каждая буква кода преобразуется в региональный индикатор (U+1F1E6-1F1FF)
      const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0))
      
      return String.fromCodePoint(...codePoints)
    }

    // Функции для работы с модальным окном
    const openModal = async (sessionId, type) => {
      // Находим сессию для получения has_audio
      const session = sessions.value.find(s => (s.session_id || s.id) === sessionId)
      
      modal.value.isOpen = true
      modal.value.sessionId = sessionId
      modal.value.type = type
      modal.value.title = type === 'transcription' ? t('sessions.transcriptionText') : t('sessions.summary')
      modal.value.content = ''
      modal.value.loading = true
      modal.value.sessionStatus = null
      modal.value.hasAudio = session?.has_audio || false
      
      // Загружаем промпты если это саммари
      if (type === 'summary') {
        loadUserPrompts()
      }
      
      try {
        // Загружаем контент
        let result = await loadFileContent(sessionId, type)
        
        // ✅ Для транскрипций loadFileContent возвращает объект { text, status }
        let content, sessionStatus
        if (type === 'transcription' && typeof result === 'object' && result !== null) {
          content = result.text
          sessionStatus = result.status
          modal.value.sessionStatus = sessionStatus
          console.log(`[OpenModal] Загружен текст: ${content?.length || 0} символов, статус: ${sessionStatus}`)
        } else {
          // Для саммари - просто текст
          content = result
        }
        
        // Если это активная транскрипция или переобработка, настраиваем автообновление
        if (type === 'transcription' && (sessionStatus === 'receiving' || sessionStatus === 'paused' || sessionStatus === 'reprocessing')) {
          console.log(`✅ [OpenModal] Запуск автообновления для активной сессии ${sessionId} (статус: ${sessionStatus})`)
          
          if (sessionStatus === 'reprocessing') {
            modal.value.title = t('sessions.transcriptionText') + ' (' + t('sessions.status.reprocessing') + ')'
          } else {
            modal.value.title = t('sessions.transcriptionText') + ' (' + (t('locale') === 'ru' ? 'автообновляется' : 'auto-updating') + ')'
          }
          
          // Если текст еще пустой - показываем placeholder
          if (!content || content.trim() === '') {
            content = t('sessions.waitingForTranscription') + '\n\n' + t('sessions.transcriptionWillAppear')
            console.log('[OpenModal] Текст пустой, показываем placeholder')
          }
                
          // Обновляем каждые 5 секунд
          modal.value.autoRefreshInterval = setInterval(async () => {
            try {
              console.log(`[AutoUpdate] Запрос обновления текста для сессии ${sessionId}...`)
              const refreshResult = await loadFileContent(sessionId, type)
              const refreshedContent = refreshResult?.text || refreshResult
              const refreshedStatus = refreshResult?.status
              
              console.log(`[AutoUpdate] Получено: ${refreshedContent?.length || 0} символов (статус: ${refreshedStatus}), текущий: ${modal.value.content?.length || 0}`)
              
              // ✅ Обновляем статус сессии
              if (refreshedStatus && refreshedStatus !== modal.value.sessionStatus) {
                console.log(`[AutoUpdate] Статус изменился: ${modal.value.sessionStatus} → ${refreshedStatus}`)
                modal.value.sessionStatus = refreshedStatus
              }
              
              // ✅ Если статус reprocessing - НЕ обновляем контент, показываем скелетон
              if (modal.value.sessionStatus === 'reprocessing' || refreshedStatus === 'reprocessing') {
                console.log('[AutoUpdate] Статус reprocessing - оставляем скелетон')
                modal.value.content = '' // Гарантируем что скелетон показан
                return
              }
              
              // Игнорируем пустой результат если сейчас показан placeholder
              if (!refreshedContent && modal.value.content.includes(t('sessions.waitingForTranscription'))) {
                console.log('[AutoUpdate] Текст еще не готов, оставляем placeholder')
                return
              }
              
              if (refreshedContent && refreshedContent !== modal.value.content) {
                const oldLength = modal.value.content.length
                console.log(`✅ [AutoUpdate] Текст обновлен: ${refreshedContent.length} символов (было ${oldLength})`)
                
                // ✨ Анимируем появление новых символов
                await animateTextTyping(modal.value.content, refreshedContent)
              } else if (refreshedContent === modal.value.content) {
                console.log(`[AutoUpdate] Текст не изменился (${refreshedContent.length} символов)`)
              } else {
                console.warn('[AutoUpdate] Получен пустой текст')
              }
            } catch (refreshError) {
              console.error('[AutoUpdate] Ошибка обновления текста:', refreshError)
            }
          }, 5000)
        } else if (type === 'transcription') {
          console.log(`❌ [OpenModal] Автообновление НЕ запущено. Статус: ${modal.value.sessionStatus} (ожидается receiving/paused/reprocessing)`)
        }
        
        // Устанавливаем контент в модалку (НО не при reprocessing - там показываем скелетон)
        if (sessionStatus === 'reprocessing') {
          modal.value.content = '' // Пустой контент → покажется скелетон
          console.log('[OpenModal] Статус reprocessing - показываем скелетон вместо старого текста')
        } else {
          modal.value.content = content
        }
        
      } catch (error) {
        // Если это саммари и ошибка 404, показываем placeholder
        if (type === 'summary' && error.message.includes('404')) {
          modal.value.content = ''  // Оставляем пустым для показа placeholder
        } else if (type === 'transcription' && error.message.includes('404')) {
          // ✅ Для транскрипции с 404 - проверяем статус и решаем что делать
          try {
            const statusResponse = await apiRequest(`/status/${sessionId}`)
            if (statusResponse.ok) {
              const statusData = await statusResponse.json()
              const sessionStatus = statusData.status
              modal.value.sessionStatus = sessionStatus
              console.log(`[OpenModal] 404, но статус из /status: ${sessionStatus}`)
              
              if (sessionStatus === 'receiving' || sessionStatus === 'paused') {
                // Активная сессия, текста еще нет - показываем placeholder и запускаем автообновление
                modal.value.content = t('sessions.waitingForTranscription') + '\n\n' + t('sessions.transcriptionWillAppear')
                modal.value.title = t('sessions.transcriptionText') + ' (' + t('sessions.autoUpdating') + ')'
                console.log('[OpenModal] 404 для активной сессии - показываем placeholder и запускаем автообновление')
                
                // Запускаем автообновление
                modal.value.autoRefreshInterval = setInterval(async () => {
                  try {
                    console.log(`[AutoUpdate] Запрос обновления текста для сессии ${sessionId}...`)
                    const refreshResult = await loadFileContent(sessionId, type)
                    const refreshedContent = refreshResult?.text || refreshResult
                    
                    console.log(`[AutoUpdate] Получено: ${refreshedContent?.length || 0} символов`)
                    
                    // Игнорируем пустой результат если сейчас показан placeholder
                    if (!refreshedContent && modal.value.content.includes(t('sessions.waitingForTranscription'))) {
                      console.log('[AutoUpdate] Текст еще не готов, оставляем placeholder')
                      return
                    }
                    
                    if (refreshedContent && refreshedContent !== modal.value.content) {
                      console.log(`✅ [AutoUpdate] Текст обновлен: ${refreshedContent.length} символов`)
                      
                      // ✨ Анимируем появление новых символов
                      await animateTextTyping(modal.value.content, refreshedContent)
                    }
                  } catch (refreshError) {
                    console.error('[AutoUpdate] Ошибка обновления текста:', refreshError)
                  }
                }, 5000)
              } else {
                // Завершенная сессия, текста нет - показываем ошибку
                modal.value.content = `Ошибка загрузки: ${error.message}`
              }
            } else {
              modal.value.content = `Ошибка загрузки: ${error.message}`
            }
          } catch (statusError) {
            console.error('[OpenModal] Ошибка проверки статуса:', statusError)
            modal.value.content = `Ошибка загрузки: ${error.message}`
          }
        } else {
          modal.value.content = `Ошибка загрузки: ${error.message}`
        }
      } finally {
        modal.value.loading = false
      }
    }
    
    const closeModal = () => {
      // ✅ НОВОЕ: Останавливаем автообновление если оно активно
      if (modal.value.autoRefreshInterval) {
        clearInterval(modal.value.autoRefreshInterval)
        modal.value.autoRefreshInterval = null
        console.log('Автообновление остановлено')
      }
      
      // ✨ Останавливаем анимацию набора текста
      if (typingAnimationTimer) {
        clearTimeout(typingAnimationTimer)
        typingAnimationTimer = null
      }
      
      modal.value.isOpen = false
      modal.value.content = ''
      modal.value.sessionId = ''
      modal.value.type = ''
      modal.value.loading = false
      modal.value.prompt = ''
      modal.value.showPromptDetails = false
      modal.value.showRegenerateForm = false
      modal.value.newPrompt = ''
      modal.value.regenerating = false
      modal.value.sessionStatus = null
      modal.value.hasAudio = false
      
      clearTypingAnimation()
    }
    
    // ✅ НОВОЕ: Автоматическая прокрутка к концу текста
    const scrollModalToBottom = () => {
      if (modalTextContainer.value) {
        modalTextContainer.value.scrollTop = modalTextContainer.value.scrollHeight
      }
    }
    
    // ✨ Таймер для анимации набора текста
    let typingAnimationTimer = null
    
    // ✨ Очистка анимации набора текста
    const clearTypingAnimation = () => {
      if (typingAnimationTimer) {
        clearTimeout(typingAnimationTimer)
        typingAnimationTimer = null
      }
    }
    
    // ✨ Эффект постепенного набора новых символов
    const animateTextTyping = async (oldText, newText) => {
      // Останавливаем предыдущую анимацию если она есть
      if (typingAnimationTimer) {
        clearTimeout(typingAnimationTimer)
        typingAnimationTimer = null
      }
      
      // Определяем общую часть и новую часть
      let commonLength = 0
      const minLength = Math.min(oldText.length, newText.length)
      for (let i = 0; i < minLength; i++) {
        if (oldText[i] === newText[i]) {
          commonLength++
        } else {
          break
        }
      }
      
      const baseText = newText.substring(0, commonLength)
      const newPart = newText.substring(commonLength)
      
      if (newPart.length === 0) {
        // Нет новых символов
        modal.value.content = newText
        return
      }
      
      console.log(`✨ [TypeAnimation] Анимация ${newPart.length} новых символов`)
      
      // Анимируем добавление новых символов
      const chunkSize = 15 // Символов за раз
      const delay = 30 // Миллисекунд между добавлениями
      let currentIndex = 0
      
      const addChunk = () => {
        if (currentIndex >= newPart.length) {
          // Анимация завершена
          modal.value.content = newText
          scrollModalToBottom()
          return
        }
        
        const endIndex = Math.min(currentIndex + chunkSize, newPart.length)
        const visiblePart = newPart.substring(0, endIndex)
        modal.value.content = baseText + visiblePart
        currentIndex = endIndex
        
        // Прокручиваем вниз по мере добавления
        nextTick().then(() => scrollModalToBottom())
        
        typingAnimationTimer = setTimeout(addChunk, delay)
      }
      
      addChunk()
    }
    
    const downloadFromModal = async () => {
      if (modal.value.sessionId && modal.value.type) {
        await downloadFile(modal.value.sessionId, modal.value.type)
      }
    }
    
    const reprocessFromModal = async () => {
      if (modal.value.sessionId) {
        const sessionId = modal.value.sessionId
        
        // ✅ НЕ закрываем модальное окно! Показываем скелетон
        modal.value.sessionStatus = 'reprocessing'
        modal.value.title = t('sessions.transcriptionText') + ' (' + t('sessions.status.reprocessing') + ')'
        
        // Сначала запускаем переобработку и ждём ответа от бэкенда
        const reprocessStarted = await doReprocess(sessionId)
        
        if (!reprocessStarted) {
          // Если не удалось запустить - возвращаем статус
          modal.value.sessionStatus = 'completed'
          modal.value.title = t('sessions.transcriptionText')
          return
        }
        
        // Помечаем время начала переобработки для защиты от race condition
        const reprocessStartTime = Date.now()
        
        // Запускаем автообновление только ПОСЛЕ успешного запуска переобработки
        if (!modal.value.autoRefreshInterval) {
          modal.value.autoRefreshInterval = setInterval(async () => {
            try {
              const refreshResult = await loadFileContent(sessionId, 'transcription')
              const refreshedContent = refreshResult?.text || refreshResult
              const refreshedStatus = refreshResult?.status
              
              // Защита от race condition: игнорируем completed в первые 5 секунд
              const timeSinceStart = Date.now() - reprocessStartTime
              if (refreshedStatus === 'completed' && timeSinceStart < 5000) {
                console.log(`[Reprocess AutoUpdate] Игнорируем ранний completed (${timeSinceStart}ms с начала)`)
                return
              }
              
              // Обновляем статус
              if (refreshedStatus && refreshedStatus !== modal.value.sessionStatus) {
                console.log(`[Reprocess AutoUpdate] Статус изменился: ${modal.value.sessionStatus} → ${refreshedStatus}`)
                modal.value.sessionStatus = refreshedStatus
                
                // Если обработка завершена - обновляем заголовок
                if (refreshedStatus === 'completed') {
                  modal.value.title = t('sessions.transcriptionText')
                }
              }
              
              // Обновляем контент когда готов (и прошло достаточно времени)
              if (refreshedContent && refreshedStatus === 'completed' && timeSinceStart >= 5000) {
                modal.value.content = refreshedContent
                // Останавливаем автообновление после завершения
                clearInterval(modal.value.autoRefreshInterval)
                modal.value.autoRefreshInterval = null
                console.log('[Reprocess] ✅ Переобработка завершена, контент обновлён')
                // Обновляем список сессий
                await loadSectionData()
                showToast(t('sessions.reprocessCompleted') || 'Reprocessing completed', 'success', 3000)
              }
            } catch (refreshError) {
              console.error('[Reprocess AutoUpdate] Ошибка:', refreshError)
            }
          }, 3000) // Проверяем каждые 3 секунды
        }
      }
    }

    // Загрузка содержимого файла для просмотра
    const loadFileContent = async (sessionId, type) => {
      try {
        // ✅ Для транскрипции сначала пробуем получить через /status (для активных сессий)
        if (type === 'transcription') {
          try {
            const statusResponse = await apiRequest(`/status/${sessionId}`)
            if (statusResponse.ok) {
              const statusData = await statusResponse.json()
              
              // ✅ Возвращаем объект с текстом и статусом ТОЛЬКО если текст есть
              // НО для reprocessing игнорируем старый текст - возвращаем пустой чтобы показать скелетон
              if (statusData.status === 'reprocessing') {
                console.log(`[LoadContent] Статус reprocessing - возвращаем пустой текст для скелетона`)
                return { text: '', status: 'reprocessing' }
              }
              if (statusData.result_text) {
                console.log(`[LoadContent] Текст получен через /status: ${statusData.result_text.length} символов, статус: ${statusData.status}`)
                return { text: statusData.result_text, status: statusData.status }
              } else if (statusData.status === 'receiving' || statusData.status === 'paused' || 
                         statusData.status === 'transcribing' || statusData.status === 'assembling') {
                // Для активных сессий возвращаем пустой текст со статусом
                console.log(`[LoadContent] /status вернул пустой result_text для активной сессии, статус: ${statusData.status}`)
                return { text: '', status: statusData.status }
              }
              // Для completed/error сессий - продолжаем к /download
              console.log(`[LoadContent] /status: статус ${statusData.status}, текст пустой - продолжаем к /download`)
            }
          } catch (statusError) {
            console.warn('[LoadContent] Не удалось получить текст через /status, пробуем через /download:', statusError)
          }
        }
        
        // Стандартный путь: загружаем из файла
        const endpoint = type === 'transcription' 
          ? `/admin/sessions/${sessionId}/download/transcription`
          : `/admin/sessions/${sessionId}/download/summary`
        
        const response = await apiRequest(endpoint)
        
        if (response.ok) {
          const content = await response.text()
          
          // Если это саммари, пытаемся загрузить промпт
          if (type === 'summary') {
            try {
              // ✅ Используем правильный эндпоинт в зависимости от раздела
              const metadataEndpoint = currentSection.value === 'dashboard' 
                ? `/admin/sessions/${sessionId}/metadata`
                : `/user/sessions/${sessionId}/metadata`
              
              const detailsResponse = await apiRequest(metadataEndpoint)
              if (detailsResponse.ok) {
                const details = await detailsResponse.json()
                modal.value.prompt = details.summary_prompt || details.metadata?.summary_prompt || ''
              }
            } catch (promptError) {
              console.warn('Не удалось загрузить промпт:', promptError)
            }
          }
          
          // ✅ Для транскрипции возвращаем объект, для summary - просто текст
          return type === 'transcription' ? { text: content, status: null } : content
        } else {
          throw new Error(`Ошибка загрузки: ${response.status} ${response.statusText}`)
        }
      } catch (error) {
        throw new Error(`Ошибка загрузки ${type}: ${error.message}`)
      }
    }
    
    // Регенерация саммари
    const loadUserPrompts = async () => {
      modal.value.loadingPrompts = true
      modal.value.availablePrompts = []
      
      try {
        const response = await apiRequest('/user/prompts')
        if (response.ok) {
          const data = await response.json()
          modal.value.availablePrompts = data.prompts || []
        }
      } catch (error) {
        console.error('Error loading prompts:', error)
      } finally {
        modal.value.loadingPrompts = false
      }
    }

    const selectPrompt = (prompt) => {
      modal.value.newPrompt = prompt.content
    }

    const regenerateSummary = async () => {
      if (!modal.value.newPrompt.trim()) {
        return
      }
      
      modal.value.regenerating = true
      
      try {
        // ✅ Используем правильный эндпоинт в зависимости от раздела
        const regenerateEndpoint = currentSection.value === 'dashboard'
          ? `/admin/sessions/${modal.value.sessionId}/regenerate-summary`
          : `/user/sessions/${modal.value.sessionId}/regenerate-summary`
        
        const response = await apiRequest(
          regenerateEndpoint,
          {
            method: 'POST',
            body: JSON.stringify({ custom_prompt: modal.value.newPrompt.trim() })
          }
        )
        
        if (response.ok) {
          const result = await response.json()
          
          // Обновляем содержимое модального окна
          modal.value.content = result.summary
          modal.value.prompt = result.prompt
          modal.value.showRegenerateForm = false
          modal.value.newPrompt = ''
          
          // ✅ ВАЖНО: Обновляем has_summary в локальном массиве sessions!
          sessions.value = sessions.value.map(s => 
            s.session_id === modal.value.sessionId 
              ? { ...s, has_summary: true }
              : s
          )
          
          showAuthStatus('Саммари успешно регенерировано!', 'success')
          
          // Обновляем список сессий
          if (currentSection.value === 'my-sessions') {
            await loadMySessions()
          } else if (currentSection.value === 'dashboard') {
            await loadData()
          }
        } else {
          const errorData = await response.json().catch(() => ({ detail: 'Неизвестная ошибка' }))
          throw new Error(errorData.detail || `HTTP ${response.status}`)
        }
      } catch (error) {
        showAuthStatus(`Ошибка регенерации: ${error.message}`, 'error')
        console.error('Ошибка регенерации саммари:', error)
      } finally {
        modal.value.regenerating = false
      }
    }
    
    // Проверка, является ли текст Markdown
    const isMarkdownText = (text) => {
      if (!text || typeof text !== 'string') return false
      
      // Проверка на блоки кода с тройными бектиками
      if (text.includes('```')) return true
      
      // Проверяем наличие типичных Markdown-паттернов
      const markdownPatterns = [
        /^#+ .+$/m,              // Заголовки
        /^[ \t]*[\*\-\+] .+$/m,  // Списки
        /^[ \t]*\d+\. .+$/m,     // Нумерованные списки
        /\[.+?\]\(.+?\)/,        // Ссылки
        /\*\*.+?\*\*/,           // Жирный текст
        /__.+?__/,               // Жирный текст
        /\*.+?\*/,               // Курсив
        /`.+?`/,                 // Код
        /^>[ \t].+$/m,           // Блок цитаты
        /\|.+\|.+\|/m            // Таблицы
      ]
      
      // Если нашли хотя бы один паттерн - это Markdown
      return markdownPatterns.some(pattern => pattern.test(text))
    }
    
    // Форматирование текста с таймкодами или Markdown
    const formatTextWithTimestamps = (text) => {
      if (!text) return '<p>Текст отсутствует</p>'
      
      // Если это Markdown - рендерим через marked с санитизацией через DOMPurify
      if (isMarkdownText(text)) {
        try {
          // Настройки для marked
          marked.setOptions({
            breaks: true,
            gfm: true,
            headerIds: false,
            mangle: false
          })
          
          // 🔒 БЕЗОПАСНОСТЬ: Санитизация HTML через DOMPurify для защиты от XSS
          const rawHtml = marked.parse(text)
          return DOMPurify.sanitize(rawHtml, {
            ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'strong', 'em', 'u', 'code', 'pre', 'ul', 'ol', 'li', 'blockquote', 'a', 'div', 'span'],
            ALLOWED_ATTR: ['href', 'title', 'class'],
            ALLOW_DATA_ATTR: false
          })
        } catch (error) {
          console.error('Ошибка рендеринга Markdown:', error)
          // Если ошибка - показываем как обычный текст (с экранированием)
          const escapedText = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          return `<pre>${escapedText}</pre>`
        }
      }
      
      // Иначе - форматируем с таймкодами
      const timestampRegex = /\[(\d{2}):(\d{2})\]/g
      
      // 🔒 БЕЗОПАСНОСТЬ: Функция для экранирования HTML
      const escapeHtml = (unsafe) => {
        return unsafe
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
      }
      
      // Проверяем наличие таймкодов
      if (!timestampRegex.test(text)) {
        // Нет таймкодов - просто возвращаем текст в параграфах (с экранированием)
        return text.split('\n\n').map(p => p.trim() ? `<p>${escapeHtml(p.trim())}</p>` : '').join('')
      }
      
      // Сбрасываем регексп
      timestampRegex.lastIndex = 0
      
      // Разбиваем по таймкодам
      const parts = text.split(timestampRegex)
      
      let html = ''
      for (let i = 0; i < parts.length; i += 3) {
        const textPart = parts[i]?.trim()
        const minutes = parts[i + 1]
        const seconds = parts[i + 2]
        
        if (textPart && i === 0) {
          // Текст до первого таймкода (с экранированием)
          html += `<p class="timestamp-paragraph">${escapeHtml(textPart)}</p>`
        }
        
        if (minutes !== undefined && seconds !== undefined) {
          const timestamp = `[${minutes}:${seconds}]`
          const nextText = parts[i + 3]?.trim() || ''
          // 🔒 БЕЗОПАСНОСТЬ: Экранируем nextText для защиты от XSS
          html += `<p class="timestamp-paragraph"><strong class="timestamp">${timestamp}</strong> ${escapeHtml(nextText)}</p>`
        }
      }
      
      return html || '<p>Текст отсутствует</p>'
    }

    // Скачивание файла (существующая функция) (транскрипция или саммари)
    const downloadFile = async (sessionId, type) => {
      try {
        const endpoint = type === 'transcription' 
          ? `/admin/sessions/${sessionId}/download/transcription`
          : `/admin/sessions/${sessionId}/download/summary`
        
        const response = await apiRequest(endpoint)
        
        if (response.ok) {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          
          // Получаем имя файла из заголовков
          const contentDisposition = response.headers.get('Content-Disposition')
          let filename = `${type}_${sessionId.substring(0, 8)}.txt`
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/)
            if (filenameMatch) {
              filename = filenameMatch[1]
            }
          }
          
          link.download = filename
          document.body.appendChild(link)
          link.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(link)
        } else {
          alert(`Ошибка скачивания: ${response.status} ${response.statusText}`)
        }
      } catch (error) {
        alert(`Ошибка скачивания: ${error.message}`)
      }
    }

    // ============================================
    // TOAST УВЕДОМЛЕНИЯ
    // ============================================
    
    const showToast = (message, type = 'success', duration = 3000) => {
      toastNotification.value = {
        show: true,
        message,
        type
      }
      setTimeout(() => {
        toastNotification.value.show = false
      }, duration)
    }
    
    // ============================================
    // INLINE РЕДАКТИРОВАНИЕ НАЗВАНИЯ
    // ============================================
    
    const startEdit = (sessionId, currentTitle) => {
      editingSessionId.value = sessionId
      editingTitle.value = currentTitle
      originalTitle.value = currentTitle
    }
    
    const cancelEdit = () => {
      editingSessionId.value = null
      editingTitle.value = ''
      originalTitle.value = ''
    }
    
    // Обработка blur - откладываем отмену, чтобы успел сработать клик на галочке
    let blurTimeout = null
    const handleEditBlur = (event, sessionId) => {
      // Если клик был на кнопке сохранения, не отменяем редактирование
      blurTimeout = setTimeout(() => {
        if (editingSessionId.value === sessionId) {
          cancelEdit()
        }
      }, 200)
    }
    
    const saveEditedTitle = async (sessionId) => {
      if (blurTimeout) clearTimeout(blurTimeout)
      
      const newTitle = editingTitle.value.trim()
      
      if (newTitle === '') {
        showToast('Название не может быть пустым', 'error')
        return
      }
      
      if (newTitle.length > 100) {
        showToast('Название слишком длинное (максимум 100 символов)', 'error')
        return
      }
      
      if (newTitle === originalTitle.value) {
        // Название не изменилось
        cancelEdit()
        return
      }
      
      try {
        const response = await apiRequest(`/user/sessions/${sessionId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ title: newTitle })
        })
        
        if (response.ok) {
          // Обновляем локально
          sessions.value = sessions.value.map(s => 
            (s.session_id || s.id) === sessionId
              ? { ...s, tab_title: newTitle }
              : s
          )
          
          showToast('Название обновлено', 'success')
          cancelEdit()
        } else {
          const errorData = await response.json()
          showToast(`Ошибка: ${errorData.detail || response.statusText}`, 'error')
        }
      } catch (error) {
        showToast(`Ошибка: ${error.message}`, 'error')
      }
    }
    
    // ============================================
    // УДАЛЕНИЕ СЕССИИ
    // ============================================
    
    const deleteSession = (sessionId) => {
      // Находим сессию для получения дополнительной информации
      const session = sessions.value.find(s => (s.session_id || s.id) === sessionId)
      
      deleteConfirmModal.value = {
        show: true,
        sessionId: sessionId,
        sessionTitle: session?.tab_title || '',
        deleteType: 'session'
      }
    }
    
    const showDeleteAudioConfirm = (sessionId) => {
      const session = sessions.value.find(s => (s.session_id || s.id) === sessionId)
      
      deleteConfirmModal.value = {
        show: true,
        sessionId: sessionId,
        sessionTitle: session?.tab_title || '',
        deleteType: 'audio'
      }
    }
    
    const cancelDelete = () => {
      deleteConfirmModal.value = {
        show: false,
        sessionId: null,
        sessionTitle: '',
        deleteType: 'session'
      }
    }
    
    const confirmDelete = async () => {
      const sessionId = deleteConfirmModal.value.sessionId
      const deleteType = deleteConfirmModal.value.deleteType
      cancelDelete()
      
      if (deleteType === 'audio') {
        await deleteAudio(sessionId)
        return
      }
      
      try {
        const response = await apiRequest(`/admin/sessions/${sessionId}`, {
          method: 'DELETE'
        })
        
        if (response.ok) {
          showToast(t('sessions.deleted'), 'success')
          await loadSectionData()
        } else {
          const errorData = await response.json()
          showToast(`${t('common.error')}: ${errorData.detail || response.statusText}`, 'error')
        }
      } catch (error) {
        showToast(`${t('common.error')}: ${error.message}`, 'error')
      }
    }

    // ============================================
    // ФУНКЦИИ ДЛЯ РАБОТЫ С АУДИО
    // ============================================
    
    const downloadAudio = async (sessionId) => {
      try {
        const response = await apiRequest(`/user/sessions/${sessionId}/audio`)
        if (response.ok) {
          const blob = await response.blob()
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `recording_${sessionId}.webm`
          a.click()
          URL.revokeObjectURL(url)
          showToast(t('common.downloaded') || 'Downloaded', 'success', 2000)
        } else {
          showToast(t('sessions.audioDownloadError'), 'error')
        }
      } catch (error) {
        console.error('[Audio] Download error:', error)
        showToast(t('sessions.audioDownloadError'), 'error')
      }
    }
    
    const openAudioPlayer = async (sessionId) => {
      // Находим сессию для заголовка
      const session = sessions.value.find(s => (s.session_id || s.id) === sessionId)
      
      // Показываем модалку сразу с индикатором загрузки
      audioPlayerModal.value = {
        show: true,
        sessionId: sessionId,
        sessionTitle: session?.tab_title || sessionId.substring(0, 8),
        audioUrl: null,
        loading: true,
        loadingProgress: 0,
        audioLocked: session?.audio_locked || false
      }
      
      try {
        const token = await getAuthToken()
        const response = await fetch(`/user/sessions/${sessionId}/audio`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (!response.ok) {
          throw new Error('Failed to load audio')
        }
        
        // Простой подход - получаем blob напрямую
        const blob = await response.blob()
        console.log('[Audio] Blob received:', blob.size, 'bytes, type:', blob.type)
        
        // Создаём URL для плеера
        const url = URL.createObjectURL(blob)
        
        audioPlayerModal.value.audioUrl = url
        audioPlayerModal.value.loading = false
        audioPlayerModal.value.loadingProgress = 100
        
      } catch (error) {
        console.error('[Audio] Player error:', error)
        audioPlayerModal.value.show = false
        showToast(t('sessions.audioDownloadError'), 'error')
      }
    }
    
    const closeAudioPlayer = () => {
      // Останавливаем воспроизведение
      if (audioPlayerRef.value) {
        audioPlayerRef.value.pause()
        audioPlayerRef.value.currentTime = 0
      }
      // Освобождаем URL
      if (audioPlayerModal.value.audioUrl) {
        URL.revokeObjectURL(audioPlayerModal.value.audioUrl)
      }
      audioPlayerModal.value = {
        show: false,
        sessionId: null,
        sessionTitle: '',
        audioUrl: null,
        loading: false,
        loadingProgress: 0,
        audioLocked: false
      }
    }
    
    const deleteAudio = async (sessionId) => {
      try {
        const response = await apiRequest(`/user/sessions/${sessionId}/audio`, {
          method: 'DELETE'
        })
        if (response.ok) {
          showToast(t('sessions.audioDeleted'), 'success', 2000)
          await loadSectionData()
        } else {
          const errorData = await response.json()
          showToast(`${t('common.error')}: ${errorData.detail || 'Unknown error'}`, 'error')
        }
      } catch (error) {
        console.error('[Audio] Delete error:', error)
        showToast(t('common.error'), 'error')
      }
    }
    
    const deleteAudioFromModal = () => {
      const sessionId = audioPlayerModal.value.sessionId
      closeAudioPlayer()
      showDeleteAudioConfirm(sessionId)
    }
    
    const toggleAudioLockInModal = async () => {
      const sessionId = audioPlayerModal.value.sessionId
      const newLockState = !audioPlayerModal.value.audioLocked
      await toggleAudioLock(sessionId, newLockState)
      audioPlayerModal.value.audioLocked = newLockState
    }
    
    const toggleAudioLock = async (sessionId, locked) => {
      try {
        const response = await apiRequest(`/user/sessions/${sessionId}/audio/lock`, {
          method: 'POST',
          body: JSON.stringify({ locked })
        })
        if (response.ok) {
          const result = await response.json()
          showToast(result.message, 'success', 2000)
          // Update local session data
          const session = sessions.value.find(s => (s.session_id || s.id) === sessionId)
          if (session) {
            session.audio_locked = locked
          }
        } else {
          const errorData = await response.json()
          showToast(`Error: ${errorData.detail || 'Unknown error'}`, 'error')
        }
      } catch (error) {
        console.error('[Audio] Lock toggle error:', error)
        showToast(t('common.error'), 'error')
      }
    }
    
    const showReprocessConfirm = (sessionId) => {
      const session = sessions.value.find(s => (s.session_id || s.id) === sessionId)
      
      reprocessConfirmModal.value = {
        show: true,
        sessionId: sessionId,
        sessionTitle: session?.tab_title || ''
      }
    }
    
    const cancelReprocess = () => {
      reprocessConfirmModal.value = {
        show: false,
        sessionId: null,
        sessionTitle: ''
      }
    }
    
    const confirmReprocess = async () => {
      const sessionId = reprocessConfirmModal.value.sessionId
      cancelReprocess()
      await doReprocess(sessionId)
    }
    
    const doReprocess = async (sessionId) => {
      try {
        const response = await apiRequest(`/user/sessions/${sessionId}/reprocess`, {
          method: 'POST'
        })
        if (response.ok) {
          showToast(t('sessions.reprocessStarted'), 'success', 3000)
          await loadSectionData()
          return true
        } else {
          const errorData = await response.json()
          showToast(`Error: ${errorData.detail || 'Unknown error'}`, 'error')
          return false
        }
      } catch (error) {
        console.error('[Reprocess] Error:', error)
        showToast(t('common.error'), 'error')
        return false
      }
    }
    
    const reprocessSession = async (sessionId, skipConfirm = false) => {
      if (skipConfirm) {
        await doReprocess(sessionId)
      } else {
        showReprocessConfirm(sessionId)
      }
    }

    // Копирование в буфер обмена
    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        // Временно показываем уведомление
        const originalTitle = document.title;
        document.title = '✓ ID скопирован!';
        setTimeout(() => {
          document.title = originalTitle;
        }, 1000);
      } catch (error) {
        console.error('Ошибка при копировании:', error);
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    }
    
    // Копирование chrome://extensions/ URL
    const copyExtensionUrl = async () => {
      try {
        await navigator.clipboard.writeText('chrome://extensions/');
        showToast(t('common.copied'), 'success', 2000);
      } catch (error) {
        console.error('Ошибка при копировании:', error);
        showToast(t('common.copyError'), 'error', 2000);
      }
    };

    // Отображение статуса
    const getStatusDisplayName = (status) => {
      const statusKeys = {
        'recording': 'sessions.status.recording',
        'receiving': 'sessions.status.receiving',
        'paused': 'sessions.status.paused',
        'pending_processing': 'sessions.status.pending',
        'assembling': 'sessions.status.processing',
        'processing': 'sessions.status.processing',
        'transcribing': 'sessions.status.processing',
        'summarizing': 'sessions.status.processing',
        'reprocessing': 'sessions.status.reprocessing',
        'completed': 'sessions.status.completed',
        'error': 'sessions.status.failed',
        'not_found': 'sessions.status.failed',
        'expired': 'sessions.status.failed',
        'failed': 'sessions.status.failed',
        'finalizing': 'sessions.status.finalizing'
      };
      const key = statusKeys[status] || 'sessions.status.pending';
      return t(key);
    };

    // Получение TTL с правильным форматированием
    const getSessionTTL = (session) => {
      if (session.ttl_seconds === null || session.ttl_seconds === undefined) {
        return '∞';
      }
      if (session.ttl_seconds <= 0) {
        return t('sessions.ttlExpired');
      }
      return formatTTL(session.ttl_seconds);
    };
    
    // Получение языка для отображения
    // Показываем язык только для завершённых сессий (после определения Whisper'ом)
    const getDisplayLanguage = (session) => {
      // Не показываем язык для активных сессий - мы ещё не знаем реальный язык
      const activeStatuses = ['recording', 'receiving', 'paused', 'transcribing', 'assembling', 'reprocessing'];
      if (activeStatuses.includes(session.status)) {
        return null;
      }
      
      // Для качественного режима (reprocessed) используем original_language
      if (session.original_language) {
        return session.original_language.toUpperCase();
      }
      
      // Для стриминга используем detected_language
      if (session.detected_language) {
        return session.detected_language.toUpperCase();
      }
      
      // Fallback на language из настроек (для старых сессий)
      if (session.language) {
        return session.language.toUpperCase();
      }
      
      return null;
    };
    
    // Проверка, является ли TTL бесконечным
    const isTTLInfinite = (session) => {
      return session.ttl_seconds === null || session.ttl_seconds === undefined;
    };
    
    // Определяем, показывать ли TTL для данного статуса
    const shouldShowTTL = (status) => {
      // Не показываем TTL для активных процессов записи/получения данных
      // Для 'paused' показываем - это может быть прерванная передача
      const hideTTLStatuses = ['recording', 'receiving'];
      return !hideTTLStatuses.includes(status);
    };
    
    // Установка бесконечного TTL
    const setInfiniteTTL = async (session) => {
      // Если TTL уже бесконечный - ничего не делаем
      if (isTTLInfinite(session)) {
        return;
      }
      
      if (!confirm(`Сделать TTL для сессии ${formatSessionId(session.session_id)} бесконечным?`)) {
        return;
      }
      
      try {
        const response = await apiRequest(`/admin/sessions/${session.session_id}/ttl`, {
          method: 'PATCH'
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Не удалось изменить TTL');
        }
        
        // Обновляем TTL в локальных данных
        session.ttl_seconds = null;
        
        // Показываем уведомление
        apiError.value = '';
        const successMsg = document.createElement('div');
        successMsg.style.cssText = 'position:fixed;top:20px;right:20px;background:#4caf50;color:white;padding:16px 24px;border-radius:8px;z-index:10000;box-shadow:0 4px 12px rgba(0,0,0,0.15);';
        successMsg.textContent = 'TTL установлен в бесконечность';
        document.body.appendChild(successMsg);
        setTimeout(() => successMsg.remove(), 3000);
        
        console.log(`TTL для сессии ${session.session_id} установлен в бесконечность`);
      } catch (err) {
        console.error('Ошибка при изменении TTL:', err);
        apiError.value = err.message || 'Не удалось изменить TTL';
      }
    };

    // Форматирование размера диска
    const formatDiskSize = (mb) => {
      if (!mb && mb !== 0) return 'N/A'
      if (mb < 1024) {
        return `${mb.toFixed(0)} MB`
      } else {
        return `${(mb / 1024).toFixed(1)} GB`
      }
    }

    // Определение класса статуса диска
    const getDiskStatusClass = () => {
      if (!stats.value || !stats.value.disk_used_percent) return ''
      const percent = stats.value.disk_used_percent
      if (percent >= 90) return 'disk-critical'
      if (percent >= 75) return 'disk-warning'
      return 'disk-ok'
    }

    return {
      isDark,
      toggleDarkMode,
      locale,
      availableLocales,
      setLocale,
      toggleLocale,
      t,
      isAuthenticated,
      adminUser,
      tokenTimeInfo,
      getTokenTimeRemaining,
      getTokenTooltip,
      authStatus,
      error,
      apiError,
      stats,
      sessions,
      sessionsByDate,
      loading,
      loadingMore,
      sessionsPage,
      sessionsTotalPages,
      sessionsTotal,
      loadMoreSessions,
      scrollSentinel,
      scrollSentinelDash,
      currentSection,
      selectedUserFilter,
      filteredSessions,
      filteredSessionsByDate,
      filteredSessionsStats,
      sortedPluginUsers,
      filteredPluginUsers,
      selectedPluginTimeFilter,
      showChangelogModal,
      changelogLoading,
      changelog,
      loadChangelog,
      formatChangelogDate,
      editingChangelog,
      startAddingChangelog,
      startEditingChangelog,
      cancelEditingChangelog,
      saveChangelog,
      deleteChangelog,
      modal,
      modalTextContainer,
      showPluginInstallModal,
      showPromptsModal,
      showSettingsModal,
      openSettingsModal, // 🆕 Открытие настроек с обновлением
      showTipsModal,
      showPrivacyModal,
      privacyLoading,
      privacyError,
      privacyContent,
      loadPrivacyPolicy,
      showStatusDropdown,
      showUserDropdown,
      showPluginInstallsSection,
      showPluginErrorsSection,
      pluginErrors,
      pluginErrorsTotal,
      pluginErrorsPage,
      pluginErrorsTotalPages,
      pluginErrorsFilterVersion,
      pluginErrorsFilterType,
      pluginErrorsFilterTrust,
      expandedErrorId,
      togglePluginErrorsSection,
      loadPluginErrors,
      clearAllPluginErrors,
      editingSessionId,
      editingTitle,
      deleteConfirmModal,
      reprocessConfirmModal,
      toastNotification,
      handleLogout,
      whisperTemperature,
      useQualityMode,
      saveAudioRecording,
      hasActiveRecording,
      saveTranscriptionSettings,
      resetToDefaults,
      storageInfo,
      loadStorageInfo,
      loadSectionData,
      openModal,
      closeModal,
      downloadFromModal,
      reprocessFromModal,
      selectPrompt,
      regenerateSummary,
      formatTextWithTimestamps,
      getDisplayName,
      getRoleDisplayName,
      formatSessionId,
      isActiveSession,
      isProcessingSession,
      formatRecordingTime,
      formatDuration,
      formatDurationHMS,
      formatTotalDuration,
      formatDateTime,
      getCountryFlag,
      formatTTL,
      downloadFile,
      deleteSession,
      cancelDelete,
      confirmDelete,
      cancelReprocess,
      confirmReprocess,
      showReprocessConfirm,
      downloadAudio,
      deleteAudio,
      deleteAudioFromModal,
      reprocessSession,
      toggleAudioLock,
      toggleAudioLockInModal,
      openAudioPlayer,
      closeAudioPlayer,
      audioPlayerModal,
      audioPlayerRef,
      autoDeleteOldestAudio,
      startEdit,
      cancelEdit,
      handleEditBlur,
      saveEditedTitle,
      showToast,
      copyToClipboard,
      copyExtensionUrl,
      getStatusDisplayName,
      getSessionTTL,
      getDisplayLanguage,
      formatDiskSize,
      getDiskStatusClass,
      isTTLInfinite,
      setInfiniteTTL,
      shouldShowTTL,
      systemStatus,
      getServiceStatusClass,
      getServiceStatusText,
      formatStatusTime,
      getOverallStatusClass,
      getOverallStatusIcon,
      getOverallStatusTitle
    }
  }
}
</script>

<style>
/* ============================================
   APP-SPECIFIC STYLES - Стили главного компонента
   
   ВАЖНО: Большинство переиспользуемых стилей вынесено в:
   - styles/style.css - глобальные стили и темы
   - styles/components.css - кнопки, badges, карточки
   - styles/sessions.css - стили сессий
   - styles/admin.css - стили админки
   - styles/responsive.css - адаптивность
   ============================================ */

/* Скроллбары настроены глобально в style.css */

/* ============================================
   ПЛАВНЫЙ ПЕРЕХОД МЕЖДУ ЛОГИНОМ И АДМИНКОЙ
   ============================================ */

.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.3s ease;
}

.page-fade-enter-from {
  opacity: 0;
}

.page-fade-leave-to {
  opacity: 0;
}

/* Оба элемента видны одновременно во время перехода */
.page-fade-enter-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

/* ============================================
   АНИМАЦИИ ПОЯВЛЕНИЯ/ИСЧЕЗНОВЕНИЯ СЕССИЙ
   ============================================ */

/* Контейнер для анимированных сессий */
.sessions-grid {
  position: relative;
}

/* Infinite Scroll Sentinel */
.scroll-sentinel {
  min-height: 1px;
  padding: 16px 0;
}

.load-more-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  color: var(--text-secondary, #888);
  font-size: 13px;
}

.load-more-hint {
  opacity: 0.5;
}

.spinner-small {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Переходы для списка сессий */
.session-list-enter-active {
  animation: session-appear 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.session-list-leave-active {
  animation: session-disappear 0.4s cubic-bezier(0.55, 0.055, 0.675, 0.19);
  position: absolute;
  width: calc(100% - 2rem); /* Учитываем padding */
}

.session-list-move {
  transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Перспектива для 3D трансформаций */
.sessions-by-date {
  perspective: 1000px;
}

/* Анимация появления (снизу с масштабированием и поворотом) */
@keyframes session-appear {
  0% {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
  }
  50% {
    opacity: 1;
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 0 20px 10px rgba(102, 126, 234, 0.3);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

/* Анимация исчезновения (сжатие и уход влево) */
@keyframes session-disappear {
  0% {
    opacity: 1;
    transform: translateX(0) scale(1) rotateX(0);
  }
  30% {
    opacity: 0.7;
    transform: translateX(-20px) scale(0.98) rotateX(5deg);
  }
  100% {
    opacity: 0;
    transform: translateX(-100px) scale(0.7) rotateX(-15deg);
    filter: blur(8px);
  }
}

/* Дополнительная анимация для самой карточки при hover */
.session-card {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.session-card:hover {
  transform: translateY(-3px) scale(1.01);
  box-shadow: var(--shadow-md);
}

/* Карточка в режиме редактирования - выделяем визуально */
.session-card.editing {
  position: relative;
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 12px 40px rgba(76, 175, 80, 0.3), 0 0 0 3px var(--primary-color);
  z-index: 100;
}

.session-card.editing .session-tab-title {
  background: var(--bg-primary);
  border-color: var(--primary-color);
}

.session-card.editing .tab-text-input {
  background: var(--bg-primary);
  cursor: text;
}

/* Убираем hover эффект для редактируемой карточки */
.session-card.editing:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 12px 40px rgba(76, 175, 80, 0.3), 0 0 0 3px var(--primary-color);
}

/* Уникальное позиционирование .auth-status для App компонента */
.auth-status {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  width: 90%;
  max-width: 400px;
}

/* Остальные стили .auth-status, .login-body, .login-container, .logo - см. style.css */

.error-message {
  color: var(--error-color);
  background-color: var(--error-bg);
  border: 1px solid var(--error-color);
  padding: 1rem;
  border-radius: 5px;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

/* Админка */
.admin-page {
  font-family: 'JetBrains Mono', monospace, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  min-height: 100vh;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 25%, #4ade80 50%, #764ba2 75%, #667eea 100%);
  background-size: 400% 400%;
  animation: gradientShift 12s ease infinite;
  color: white;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  min-height: 60px;
  flex-wrap: nowrap;
  overflow: visible;
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  25% {
    background-position: 50% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  75% {
    background-position: 50% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.header .title {
  font-size: 1.8rem;
  font-weight: bold;
  color: white !important;
  white-space: nowrap; /* ✅ Не переносить на 2 строки */
  flex-shrink: 0; /* ✅ Не сжимать заголовок */
}

/* ============================================
   SYSTEM STATUS DROPDOWN
   ============================================ */

.system-status-dropdown {
  position: relative;
  margin-left: 12px;
  padding-left: 12px;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
}

.status-indicator-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 6px 12px;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-indicator-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.status-indicator-btn.status-healthy {
  border-color: rgba(76, 175, 80, 0.5);
}

.status-indicator-btn.status-warning {
  border-color: rgba(255, 152, 0, 0.5);
}

.status-indicator-btn.status-error {
  border-color: rgba(244, 67, 54, 0.5);
  animation: pulse-error 2s ease-in-out infinite;
}

@keyframes pulse-error {
  0%, 100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(244, 67, 54, 0); }
}

.status-icon {
  font-size: 14px;
}

.status-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  overflow: visible;
  z-index: 9999;
}

.status-dropdown-header {
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-light);
  background: var(--bg-tertiary);
}

.status-dropdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid var(--border-light);
  transition: background 0.15s ease;
}

.status-dropdown-item:last-child {
  border-bottom: none;
}

.status-dropdown-item:hover {
  background: var(--card-hover-bg);
}

.status-service-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}

.status-value {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-secondary);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.status-healthy {
  background: #4caf50;
  box-shadow: 0 0 6px rgba(76, 175, 80, 0.5);
}

.status-dot.status-warning {
  background: #ff9800;
  box-shadow: 0 0 6px rgba(255, 152, 0, 0.5);
}

.status-dot.status-error {
  background: #f44336;
  box-shadow: 0 0 6px rgba(244, 67, 54, 0.5);
}

.status-dot.status-unknown,
.status-dot.status-unavailable {
  background: #9e9e9e;
  box-shadow: 0 0 6px rgba(158, 158, 158, 0.3);
}

/* ============================================
   USER INFO & DROPDOWN
   ============================================ */

.header .user-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* theme-toggle перенесен в user dropdown menu */

.user-dropdown {
  position: relative;
}

.user-menu-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.user-menu-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  object-fit: cover;
}

.user-avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.user-name-compact {
  font-size: 14px;
  font-weight: 500;
  color: white;
  white-space: nowrap;
}

.dropdown-arrow {
  color: rgba(255, 255, 255, 0.7);
  transition: transform 0.2s ease;
}

.user-menu-btn:hover .dropdown-arrow {
  transform: translateY(2px);
}

.user-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 8px 0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  min-height: 200px;
  overflow-y: auto;
  max-height: 80vh;
  z-index: 9999;
}

.user-dropdown-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid var(--border-light);
  background: var(--bg-tertiary);
}

.user-avatar-large {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid var(--border-color);
  object-fit: cover;
}

.user-dropdown-info {
  flex: 1;
  min-width: 0;
}

.user-dropdown-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.user-dropdown-email {
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-role {
  font-size: 11px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

/* Token Status Progress Bar */
.token-status {
  margin-top: 8px;
  cursor: help;
}

.token-progress-bar {
  width: 100%;
  height: 3px;
  background-color: var(--border-color);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 4px;
}

.token-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #8bc34a);
  border-radius: 2px;
  transition: width 0.3s ease, background 0.3s ease;
}

.token-progress-fill.token-warning {
  background: linear-gradient(90deg, #ff9800, #ffc107);
}

.token-progress-fill.token-expiring {
  background: linear-gradient(90deg, #f44336, #ff5722);
}

.token-time-text {
  font-size: 10px;
  color: var(--text-secondary);
  opacity: 0.75;
}

.user-dropdown-divider {
  height: 1px;
  background: var(--border-light);
  margin: 4px 0;
}

.user-dropdown-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  font-family: inherit;
}

.user-dropdown-item:hover {
  background: var(--card-hover-bg);
}

.dropdown-item-icon {
  font-size: 18px;
  width: 20px;
  text-align: center;
}

.user-dropdown-logout {
  color: var(--error-color);
  border-top: 1px solid var(--border-light);
}

.user-dropdown-logout:hover {
  background: var(--error-bg);
}

/* Анимация dropdown */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.header .logout-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.logo-small {
  width: 40px;
  height: 40px;
  background-image: url('/logo.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  margin-right: 1rem;
}

.container {
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 2rem 2rem 2rem;
}

/* ============================================
   ЗАГОЛОВКИ СЕКЦИЙ - Единый стиль для всех вкладок
   ============================================ */

h1 {
  font-family: 'JetBrains Mono', monospace;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
  font-size: 2rem;
  font-weight: 600;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0.5rem;
  letter-spacing: -0.02em;
}

h2 {
  font-family: 'JetBrains Mono', monospace;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

/* .stats-grid - см. admin.css */

/* Карточка с информацией о диске */
.disk-card {
  grid-column: span 1;
}

.disk-card.disk-ok {
  border-left: 4px solid #4caf50;
}

.disk-card.disk-warning {
  border-left: 4px solid #ff9800;
}

.disk-card.disk-critical {
  border-left: 4px solid #f44336;
}

.disk-usage {
  font-size: 1.4rem !important;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0.5rem 0 !important;
}

.disk-bar {
  width: 100%;
  height: 12px;
  background: var(--border-color);
  border-radius: 6px;
  overflow: hidden;
  margin: 0.75rem 0;
}

.disk-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s ease, background 0.3s ease;
}

.disk-card.disk-warning .disk-bar-fill {
  background: linear-gradient(90deg, #ff9800 0%, #fb8c00 100%);
}

.disk-card.disk-critical .disk-bar-fill {
  background: linear-gradient(90deg, #f44336 0%, #e53935 100%);
}

.disk-details {
  font-size: 0.9rem !important;
  color: var(--text-secondary);
  margin: 0.5rem 0 0 0 !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.disk-free {
  font-weight: 600;
  color: var(--success-color);
}

.disk-card.disk-warning .disk-free {
  color: #ff9800;
}

.disk-card.disk-critical .disk-free {
  color: #f44336;
}

.sessions-table-container {
  overflow-x: auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.sessions-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
}

.sessions-table th, .sessions-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.sessions-table th {
  background-color: #f8f8f8;
  font-weight: bold;
  color: #4a5568;
  text-transform: uppercase;
  font-size: 0.85rem;
}

.sessions-table tbody tr:hover {
  background-color: #f0f4f8;
}

.session-id {
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  word-break: break-all;
  max-width: 200px;
}

.recording-time .timer {
  color: #e74c3c;
  font-weight: bold;
  font-family: 'Courier New', monospace;
}

.tab-title {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-data {
  text-align: center;
}

.data-indicators {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
}

.indicator {
  font-size: 1.2rem;
  cursor: help;
}

.indicator.text {
  color: #3498db;
}

.indicator.summary {
  color: #27ae60;
}

.indicator.error {
  color: #e74c3c;
}

.indicator.text:hover,
.indicator.summary:hover {
  cursor: pointer;
  transform: scale(1.2);
  transition: transform 0.2s;
}

.ttl-info {
  text-align: center;
}

.ttl-timer {
  color: #f39c12;
  font-weight: bold;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
}

.permanent {
  color: #27ae60;
  font-weight: bold;
  font-size: 1.2rem;
}

.language {
  text-transform: uppercase;
  font-weight: bold;
  color: #3498db;
  text-align: center;
}

.actions {
  text-align: center;
}

.status.receiving,
.status-badge.receiving {
  background: #cce5ff;
  color: #004085;
}

.status.transcribing,
.status-badge.transcribing {
  background: #fff3cd;
  color: #856404;
}

.status.reprocessing,
.status-badge.reprocessing {
  background: linear-gradient(90deg, #e0cffc, #d0e7ff, #e0cffc);
  background-size: 200% 100%;
  animation: reprocessing-shimmer 1.5s ease-in-out infinite;
  color: #5a32a3;
  font-weight: 500;
}

@keyframes reprocessing-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Навигация */
.navigation {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0;
}

.nav-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  border-bottom: 3px solid transparent;
  transition: all 0.2s;
}

.nav-btn:hover {
  color: var(--primary-color);
  background: var(--bg-tertiary);
}

.nav-btn.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
}

/* ============================================
   СТИЛИ СЕССИЙ ВЫНЕСЕНЫ В:
   - styles/sessions.css (включая .sessions-by-date, .date-group, .date-header, .sessions-grid, @keyframes fadeInSlide)
   - styles/components.css (badges, buttons)
   ============================================ */

/* ============================================
   АДАПТИВНЫЕ СТИЛИ ВЫНЕСЕНЫ В:
   - styles/responsive.css
   - styles/sessions.css (медиа-запросы для сессий)
   - styles/admin.css (медиа-запросы для админки)
   - styles/components.css (медиа-запросы для компонентов)
   ============================================ */


/* Модальное окно */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
  overscroll-behavior: contain;
  touch-action: none; /* Блокируем тач-жесты для прокрутки */
}

/* Vue Transition анимации для модальных окон */
.modal-enter-active {
  transition: opacity 0.2s ease-out;
}

.modal-leave-active {
  transition: opacity 0.15s ease-in;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-to,
.modal-leave-from {
  opacity: 1;
}

/* Блокировка прокрутки при открытом модальном окне */
html.modal-open,
body.modal-open {
  overflow: hidden !important;
}

/* Блокируем прокрутку #app */
body.modal-open #app {
  overflow: hidden !important;
}

.modal-content {
  background: var(--bg-secondary);
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: min(800px, 90vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  touch-action: auto; /* Разрешаем тач-жесты внутри модального окна */
}

/* Анимации для .modal-content внутри Transition */
.modal-enter-active .modal-content {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-leave-active .modal-content {
  transition: all 0.2s ease-in;
}

.modal-enter-from .modal-content {
  opacity: 0;
  transform: scale(0.9) translateY(-20px);
}

.modal-leave-to .modal-content {
  opacity: 0;
  transform: scale(0.95) translateY(10px);
}

.modal-enter-to .modal-content,
.modal-leave-from .modal-content {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--bg-tertiary);
}

.modal-header h2 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
}

.modal-actions {
  display: flex;
  gap: 8px;
}

.btn-download {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-download:hover {
  opacity: 0.8;
}

.btn-close {
  background: var(--text-secondary);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-close:hover {
  opacity: 0.8;
}

.modal-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Изоляция прокрутки для всех modal-body с overflow-y */
.modal-body[style*="overflow-y"],
.privacy-modal .modal-body,
.tips-modal .modal-body,
.settings-modal .modal-body,
.changelog-modal .modal-body,
.prompts-modal-body {
  overscroll-behavior: contain;
}

.modal-content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--text-secondary);
}

.modal-loading .spinner {
  width: 32px;
  height: 32px;
  margin-bottom: 16px;
}

.modal-text {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  min-height: 0;
  overscroll-behavior: contain; /* Предотвращает прокрутку фона при достижении края */
  -webkit-overflow-scrolling: touch; /* Плавная прокрутка на iOS */
  touch-action: auto; /* Разрешаем прокрутку внутри модального окна */
}

.modal-text pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-primary);
}

/* НОВОЕ: Стили для форматированных таймкодов */
.modal-text .timestamp-paragraph {
  margin: 0 0 1.5em 0;
  padding: 8px 0;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-primary);
  border-left: 3px solid transparent;
  padding-left: 12px;
  transition: border-color 0.2s;
}

.modal-text .timestamp-paragraph:hover {
  border-left-color: var(--primary-color);
  background-color: var(--bg-tertiary);
}

.modal-text .timestamp-paragraph .timestamp {
  display: inline-block;
  color: var(--primary-color);
  font-weight: 600;
  font-family: 'Courier New', monospace;
  margin-right: 8px;
  min-width: 60px;
  user-select: none;
}

.modal-text .timestamp-paragraph:first-child {
  margin-top: 0;
}

/* Стили для Markdown контента в модальном окне */
.modal-text h1 {
  font-size: 1.8em;
  margin-top: 1em;
  margin-bottom: 0.5em;
  color: var(--text-primary);
  font-weight: 600;
}

.modal-text h2 {
  font-size: 1.5em;
  margin-top: 0.8em;
  margin-bottom: 0.4em;
  color: var(--text-primary);
  font-weight: 600;
}

.modal-text h3 {
  font-size: 1.2em;
  margin-top: 0.6em;
  margin-bottom: 0.3em;
  color: var(--text-primary);
  font-weight: 600;
}

.modal-text ul,
.modal-text ol {
  margin-left: 1.5em;
  margin-bottom: 1em;
  padding-left: 0.5em;
}

.modal-text li {
  margin-bottom: 0.3em;
  line-height: 1.6;
}

.modal-text strong {
  font-weight: 600;
  color: var(--text-primary);
}

.modal-text em {
  font-style: italic;
}

.modal-text code {
  background-color: var(--bg-tertiary);
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
  color: var(--error-color);
}

.modal-text pre {
  background-color: var(--bg-tertiary);
  padding: 1em;
  border-radius: 4px;
  overflow-x: auto;
  margin-bottom: 1em;
}

.modal-text pre code {
  background-color: transparent;
  padding: 0;
  color: inherit;
}

.modal-text blockquote {
  border-left: 3px solid var(--primary-color);
  padding-left: 1em;
  margin-left: 1em;
  color: var(--text-secondary);
  font-style: italic;
}

.modal-text a {
  color: var(--primary-color);
  text-decoration: none;
}

.modal-text a:hover {
  text-decoration: underline;
}

.modal-text table {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 1em;
}

.modal-text th,
.modal-text td {
  border: 1px solid var(--border-color);
  padding: 0.5em;
  text-align: left;
}

.modal-text th {
  background-color: var(--bg-tertiary);
  font-weight: 600;
}

.modal-text p {
  margin-bottom: 1em;
  line-height: 1.6;
}

.modal-text p:first-child {
  margin-top: 0;
}

/* Стили для промпта саммари */
.summary-prompt-display {
  background-color: var(--bg-tertiary);
  border-left: 3px solid var(--primary-color);
  padding: 12px 15px;
  margin-bottom: 20px;
  border-radius: 4px;
}

.prompt-label {
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 6px;
}

.prompt-text {
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-primary);
  font-style: italic;
}

/* Сворачиваемый промпт для саммари */
.summary-prompt-collapsible {
  margin-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 12px;
}

.prompt-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 13px;
  padding: 4px 0;
  user-select: none;
}

.prompt-toggle:hover {
  color: var(--text-primary);
}

.prompt-toggle-icon {
  font-size: 10px;
  transition: transform 0.2s;
}

.prompt-toggle-label {
  font-weight: 500;
}

.prompt-details {
  margin-top: 10px;
  padding: 12px;
  background: var(--bg-tertiary);
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
  font-style: italic;
  max-height: 150px;
  overflow-y: auto;
}

/* Кнопка регенерации */
.btn-regenerate {
  background: #9c27b0;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-regenerate:hover {
  opacity: 0.8;
}

/* Кнопка переобработки */
.btn-reprocess {
  background: #1976d2;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-reprocess:hover {
  background: #1565c0;
}

/* Форма регенерации саммари */
/* Анимированная полоска генерации саммари */
.regenerate-progress {
  background: linear-gradient(90deg, 
    rgba(102, 126, 234, 0.1) 0%,
    rgba(118, 75, 162, 0.15) 50%,
    rgba(102, 126, 234, 0.1) 100%
  );
  background-size: 200% 100%;
  border-radius: 8px;
  padding: 14px 20px;
  margin: 0 24px 16px 24px;
  flex-shrink: 0;
  border: 1px solid rgba(102, 126, 234, 0.2);
  animation: gradient-shift 3s ease-in-out infinite;
}

.regenerate-progress .progress-content {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
}

.regenerate-progress .progress-icon {
  font-size: 20px;
  opacity: 0.8;
}

.regenerate-progress .progress-text {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
}

@keyframes gradient-shift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.regenerate-form {
  background-color: var(--bg-secondary);
  border: 2px solid #9c27b0;
  border-radius: 8px;
  padding: 20px;
  margin: 0 24px 16px 24px;
  flex-shrink: 0;
}

.regenerate-form h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: var(--text-primary);
  font-size: 18px;
}

.regenerate-form textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 15px;
  box-sizing: border-box;
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.regenerate-form textarea:focus {
  outline: none;
  border-color: #9c27b0;
}

.regenerate-form .form-actions {
  display: flex;
  gap: 10px;
}

.regenerate-form .btn-primary,
.regenerate-form .btn-secondary {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.regenerate-form .btn-primary {
  background-color: #9c27b0;
  color: white;
}

.regenerate-form .btn-primary:hover:not(:disabled) {
  background-color: #7b1fa2;
}

.regenerate-form .btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Стили для модального окна Privacy Policy */
.privacy-modal {
  max-height: 85vh;
}

.privacy-modal .modal-body {
  padding: 1.5rem 2rem;
  overflow-y: auto;
  max-height: calc(85vh - 80px);
  overscroll-behavior: contain;
}

/* Стили для контента Privacy Policy */
.privacy-content {
  color: var(--text-primary);
  line-height: 1.8;
}

.privacy-content h1 {
  font-size: 1.75rem;
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
  font-weight: 600;
}

.privacy-content h2 {
  font-size: 1.25rem;
  margin: 1.5rem 0 0.75rem 0;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-light);
  padding-bottom: 0.5rem;
  font-weight: 600;
}

.privacy-content h3 {
  font-size: 1.125rem;
  margin: 1.25rem 0 0.5rem 0;
  color: var(--text-primary);
  font-weight: 600;
}

.privacy-content p {
  margin: 0.75rem 0;
  font-size: 0.9375rem;
  color: var(--text-secondary);
}

.privacy-content ul, .privacy-content ol {
  margin: 0.75rem 0;
  padding-left: 2rem;
  color: var(--text-secondary);
}

.privacy-content li {
  margin: 0.5rem 0;
  font-size: 0.9375rem;
}

.privacy-content strong {
  color: var(--text-primary);
  font-weight: 600;
}

.privacy-content a {
  color: var(--success-color);
  text-decoration: none;
}

.privacy-content a:hover {
  text-decoration: underline;
}

.privacy-content hr {
  border: none;
  border-top: 1px solid var(--border-light);
  margin: 1.5rem 0;
}

/* Дополнительные элементы для изоляции */
.privacy-content table {
  max-width: 100%;
  width: 100%;
  border-collapse: collapse;
}

.privacy-content img {
  max-width: 100%;
  height: auto;
}

.privacy-content pre, .privacy-content code {
  max-width: 100%;
  overflow-x: auto;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.privacy-content blockquote {
  max-width: 100%;
  margin: 1rem 0;
  padding-left: 1rem;
  border-left: 3px solid var(--border-color);
}

.regenerate-form .btn-secondary {
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.regenerate-form .btn-secondary:hover {
  background-color: var(--card-hover-bg);
}

/* Стили для выбора промптов */
.prompt-selector {
  margin-bottom: 1rem;
}

.prompt-selector label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
}

.prompts-dropdown {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
}

.prompt-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.75rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.prompt-option:hover {
  background: var(--card-hover-bg);
  border-color: #9c27b0;
  transform: translateX(4px);
}

.prompt-name {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
  font-size: 14px;
}

.prompt-preview {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.prompt-divider {
  text-align: center;
  margin: 1rem 0;
  position: relative;
}

.prompt-divider::before,
.prompt-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 40%;
  height: 1px;
  background: var(--border-color);
}

.prompt-divider::before {
  left: 0;
}

.prompt-divider::after {
  right: 0;
}

.prompt-divider span {
  display: inline-block;
  padding: 0 1rem;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: 13px;
  font-style: italic;
}

/* Стили для модального окна установки плагина */
.plugin-install-modal {
  max-width: 550px;
  max-height: 85vh;
}

.plugin-install-modal .modal-body {
  padding: 1.5rem 1.75rem;
}

/* Chrome Web Store секция */
.install-chrome-store {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.5rem;
}

.chrome-store-info h3 {
  margin: 0 0 0.75rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

.chrome-store-info p {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--text-secondary);
  line-height: 1.5;
  max-width: 450px;
}

.chrome-store-badge {
  display: inline-block;
  transition: all 0.2s ease;
  cursor: pointer;
}

.chrome-store-badge img {
  height: 58px;
  width: auto;
  display: block;
}

.chrome-store-badge:hover {
  transform: translateY(-2px);
  filter: brightness(1.1);
}

/* Стили для модального окна Tips & Tricks */
.tips-modal {
  max-width: 700px;
  max-height: 85vh;
}

.tips-modal .modal-body {
  padding: 1rem 1.5rem;
  overflow-y: auto;
  max-height: calc(85vh - 80px);
  overscroll-behavior: contain;
}

/* Audio Player Modal */
.audio-player-modal {
  max-width: 500px;
  width: 90%;
}

.audio-player-body {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  align-items: center;
}

.audio-player {
  width: 100%;
  max-width: 450px;
  border-radius: 8px;
}

.audio-player-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}

.audio-actions-row {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

.audio-actions-row.secondary {
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-outline {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.8);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-outline:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.btn-outline.active {
  background: rgba(99, 102, 241, 0.2);
  border-color: #6366f1;
  color: #a5b4fc;
}

.btn-danger-outline {
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.5);
  color: #f87171;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-danger-outline:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: #ef4444;
}

/* Audio text button in session card */
.audio-text-btn {
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  border: none;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  color: white;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.audio-text-btn:hover {
  opacity: 0.9;
}

.audio-text-btn.locked {
  background: linear-gradient(135deg, #6366f1, #4f46e5);
}

.audio-text-btn .lock-icon {
  font-size: 0.75rem;
  margin-left: 0.15rem;
  cursor: pointer;
  transition: transform 0.2s;
}

.audio-text-btn .lock-icon:hover {
  transform: scale(1.2);
}

.audio-indicator {
  margin-right: 0.5rem;
  font-size: 0.9em;
}

.audio-indicator.warning {
  cursor: help;
  opacity: 0.8;
}

.audio-indicator.locked {
  color: #f59e0b;
}

.action-btn.lock-btn {
  background: var(--bg-secondary, #1a1a2e);
  border: 1px solid var(--border-color, #333);
}

.action-btn.lock-btn.active {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  border-color: #f59e0b;
}

.cyclic-recording-option {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color, #333);
}

.setting-note.small {
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

.quality-mode-note.info {
  background: rgba(99, 102, 241, 0.15);
  border-color: rgba(99, 102, 241, 0.3);
  color: #a5b4fc;
}

.action-btn.play-btn {
  background: var(--primary-gradient, linear-gradient(135deg, #6366f1, #8b5cf6));
  color: white;
}

.action-btn.play-btn:hover {
  filter: brightness(1.1);
}

/* Audio Loading States */
.audio-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  width: 100%;
}

.audio-loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-color, #333);
  border-top-color: var(--primary-color, #6366f1);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.audio-loading-text {
  color: var(--text-secondary, #9ca3af);
  font-size: 0.95rem;
}

.audio-progress-bar {
  width: 100%;
  max-width: 300px;
  height: 8px;
  background: var(--border-color, #333);
  border-radius: 4px;
  overflow: hidden;
}

.audio-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 4px;
  transition: width 0.2s ease;
}

/* Audio Storage Section */
.audio-storage-section {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.storage-card {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(139, 92, 246, 0.05));
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 1rem;
}

.storage-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.storage-card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.storage-icon {
  font-size: 1.1rem;
}

.refresh-btn-mini {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary, #9ca3af);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.refresh-btn-mini:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary, #fff);
}

.refresh-icon {
  font-size: 0.9rem;
}

.storage-progress {
  margin-bottom: 1rem;
}

.storage-bar-container {
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.storage-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #22c55e, #4ade80);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.storage-bar-fill.warning {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.storage-bar-fill.danger {
  background: linear-gradient(90deg, #ef4444, #f87171);
}

.storage-stats {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.85rem;
}

.storage-used {
  color: var(--text-primary, #fff);
  font-weight: 500;
}

.storage-separator {
  color: var(--text-secondary, #9ca3af);
}

.storage-limit {
  color: var(--text-secondary, #9ca3af);
}

.storage-percent-badge {
  margin-left: auto;
  padding: 0.15rem 0.5rem;
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
}

.storage-percent-badge.warning {
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
}

.storage-percent-badge.danger {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.storage-breakdown-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.breakdown-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  font-size: 0.8rem;
}

.breakdown-icon {
  font-size: 0.9rem;
}

.breakdown-label {
  color: var(--text-secondary, #9ca3af);
  flex: 1;
}

.breakdown-value {
  color: var(--text-primary, #fff);
  font-weight: 500;
}

.storage-alert {
  margin-top: 0.75rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.storage-alert.warning {
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.25);
  color: #fbbf24;
}

.storage-alert.danger {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.25);
  color: #f87171;
}

.alert-icon {
  font-size: 1rem;
}

/* Cyclic Recording Card */
.cyclic-option-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border-color, #333);
  border-radius: 12px;
  padding: 1rem;
}

.cyclic-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.cyclic-info {
  display: flex;
  gap: 0.75rem;
  flex: 1;
}

.cyclic-icon {
  font-size: 1.25rem;
}

.cyclic-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.cyclic-title {
  font-weight: 600;
  color: var(--text-primary, #fff);
  font-size: 0.9rem;
}

.cyclic-desc {
  font-size: 0.8rem;
  color: var(--text-secondary, #9ca3af);
  line-height: 1.4;
}

/* Toggle Switch */
.switch {
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 24px;
  transition: all 0.3s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: all 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.switch input:checked + .slider {
  background: linear-gradient(135deg, #22c55e, #16a34a);
}

.switch input:checked + .slider:before {
  transform: translateX(20px);
}

.cyclic-note {
  margin-top: 0.75rem;
  padding: 0.6rem 0.75rem;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  font-size: 0.8rem;
  color: #a5b4fc;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.note-icon {
  font-size: 0.9rem;
}

.tips-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.tip-item {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-light);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.tip-item:hover {
  border-color: var(--success-color);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.15);
}

.tip-icon {
  flex-shrink: 0;
  font-size: 2rem;
  line-height: 1;
  opacity: 0.9;
}

.tip-content {
  flex: 1;
}

.tip-content h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.tip-content p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.6;
}

.tip-content p + p {
  margin-top: 0.5rem;
}

.tip-note {
  font-style: italic;
  color: var(--info-color) !important;
}

.tip-content code {
  display: inline-block;
  background: var(--bg-tertiary);
  padding: 0.125rem 0.375rem;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.8125rem;
  color: var(--success-color);
  border: 1px solid var(--border-light);
  margin: 0 0.125rem;
}

.install-help li:last-child {
  margin-bottom: 0;
}

.install-help ul ul {
  margin-top: 0.25rem;
  margin-bottom: 0;
}

.install-help code {
  background: var(--bg-tertiary);
  padding: 0.125rem 0.3125rem;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  color: var(--success-color);
}

/* ============================================================================
   CHANGELOG MODAL STYLES
   ============================================================================ */

.changelog-modal {
  max-height: 90vh;
}

.changelog-modal .modal-body {
  padding: 1.5rem;
}

.changelog-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.changelog-section {
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 1rem;
}

.changelog-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.changelog-section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.empty-changelog {
  color: var(--text-secondary);
  font-style: italic;
  padding: 0.5rem;
  text-align: center;
}

.changelog-entries {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.changelog-entry {
  background: var(--bg-secondary);
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;
}

.changelog-entry:hover {
  background: var(--bg-tertiary);
}

.changelog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid var(--border-color);
}

.changelog-version {
  font-weight: 600;
  font-size: 1rem;
  color: var(--primary-color);
  font-family: 'JetBrains Mono', monospace;
}

.changelog-date {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.changelog-changes {
  margin: 0;
  padding-left: 1.5rem;
  list-style-type: disc;
}

.changelog-changes li {
  color: var(--text-primary);
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 0.35rem;
}

.changelog-changes li:last-child {
  margin-bottom: 0;
}

/* Адаптивность для changelog modal */
@media (max-width: 768px) {
  .changelog-modal {
    max-width: 100%;
  }

  .changelog-modal .modal-body {
    padding: 1rem;
  }

  .changelog-content {
    gap: 1.5rem;
  }

  .changelog-entry {
    padding: 0.875rem 1rem;
  }

  .changelog-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .changelog-version {
    font-size: 0.9375rem;
  }

  .changelog-date {
    font-size: 0.8125rem;
  }

  .changelog-changes li {
    font-size: 0.875rem;
  }
}

/* Адаптивность модального окна */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }
  
  .modal-content {
    width: 100%;
    max-height: 95vh;
  }
  
  .modal-header {
    padding: 16px 20px;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .modal-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  .modal-text {
    padding: 20px;
  }
  
  .modal-text pre {
    font-size: 13px;
  }
  
  /* Адаптивность для модального окна установки плагина */
  .plugin-install-modal {
    max-height: 90vh;
    max-width: 95%;
  }
  
  .plugin-install-modal .modal-body {
    padding: 1.25rem 1rem;
  }
  
  .chrome-store-info h3 {
    font-size: 1.125rem;
  }
  
  .chrome-store-info p {
    font-size: 0.875rem;
  }
  
  .chrome-store-badge img {
    height: 48px;
  }
  
  /* Адаптивность для модального окна Tips & Tricks */
  .tips-modal {
    max-height: 90vh;
    max-width: 95%;
  }
  
  .tips-modal .modal-body {
    padding: 0.875rem 1rem;
    max-height: calc(90vh - 70px);
  }
  
  .tips-list {
    gap: 1rem;
  }
  
  .tip-item {
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.875rem;
  }
  
  .tip-icon {
    font-size: 1.75rem;
  }
  
  .tip-content h4 {
    font-size: 0.9375rem;
  }
  
  .tip-content p {
    font-size: 0.8125rem;
  }
  
  /* Адаптивность для модального окна Privacy Policy */
  .privacy-modal {
    max-height: 90vh;
  }
  
  .privacy-modal .modal-body {
    padding: 1rem 1.25rem;
    max-height: calc(90vh - 70px);
  }
  
  .privacy-content h1 {
    font-size: 1.5rem;
  }
  
  .privacy-content h2 {
    font-size: 1.125rem;
  }
  
  .privacy-content h3 {
    font-size: 1rem;
  }
  
  .privacy-content p,
  .privacy-content li {
    font-size: 0.875rem;
  }
  
  /* Адаптивность для модального окна Settings */
  .settings-modal {
    max-height: 90vh;
    max-width: 95%;
  }
  
  .settings-modal .modal-body {
    max-height: calc(90vh - 120px);
  }
  
  .settings-section {
    padding: 16px;
    margin: 12px;
  }
  
  /* Адаптивность для модального окна Prompts */
  .prompts-modal {
    max-height: 90vh;
    max-width: 95%;
  }
  
  .prompts-modal-body {
    max-height: calc(90vh - 80px);
  }
  
  .temp-markers {
    flex-direction: column;
    gap: 12px;
  }
  
  .temp-marker {
    text-align: left;
  }
  
  .install-help h4 {
    font-size: 0.8125rem;
  }
  
  .install-help ul {
    font-size: 0.75rem;
  }
}

/* Анимация для статусов */
@keyframes statusPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Адаптивность для планшетов и мобильных */
@media (max-width: 1200px) {
  .header .title {
    font-size: 1.6rem; /* ✅ Начинаем уменьшать раньше */
  }
  
  .header-left {
    gap: 0.9rem;
  }
}

@media (max-width: 1024px) {
  .header {
    padding: 0.8rem 1.5rem;
  }
  
  .header .title {
    font-size: 1.4rem; /* ✅ Плавное уменьшение */
  }
  
  .header-left {
    gap: 0.7rem; /* ✅ Меньше отступы */
  }
  
  .container {
    padding: 0 1rem 1rem 1rem;
    margin: 1rem auto;
  }
}

@media (max-width: 768px) {
  .header {
    padding: 0.6rem 1rem;
    flex-wrap: nowrap; /* ✅ Не переносить на малых экранах */
  }
  
  .header-left {
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
  }
  
  .header .title {
    font-size: 1.3rem;
    white-space: nowrap;
  }
  
  .logo-small {
    width: 36px;
    height: 36px;
  }
  
  /* Адаптивность для dropdown */
  .system-status-dropdown {
    margin-left: 8px;
    padding-left: 8px;
  }
  
  .status-indicator-btn {
    padding: 5px 10px;
  }
  
  .user-menu-btn {
    padding: 5px 10px;
    gap: 8px;
  }
  
  .user-name-compact {
    font-size: 13px;
  }
  
  .user-avatar {
    width: 28px;
    height: 28px;
  }
  
  .container {
    padding: 0 0.8rem 0.8rem 0.8rem;
    margin: 0.8rem auto;
  }
  
  .navigation {
    gap: 8px;
    flex-wrap: wrap;
  }
  
  .nav-btn {
    font-size: 13px;
    padding: 8px 12px;
  }
  
  
  /* Адаптация текста в сессиях */
  .session-time,
  .session-user,
  .session-id {
    font-size: 12px;
  }
  
  .tab-title {
    font-size: 15px;
  }
  
  /* Адаптация форм */
  .regenerate-form textarea {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .header {
    padding: 0.5rem 0.8rem;
  }
  
  .header-left {
    gap: 0.4rem;
    flex-wrap: nowrap; /* ✅ Даже на маленьких экранах не переносим */
  }
  
  .header .title {
    font-size: 1.1rem; /* ✅ Чуть больше для читаемости */
    white-space: nowrap;
  }
  
  .logo-small {
    width: 32px;
    height: 32px;
    flex-shrink: 0; /* ✅ Не сжимать лого */
  }
  
  /* На малых экранах скрываем имя пользователя */
  .user-name-compact {
    display: none;
  }
  
  .system-status-dropdown {
    padding-left: 0;
    margin-left: 0;
    border-left: none;
  }
  
  .status-indicator-btn {
    padding: 6px 10px;
  }
  
  .user-menu-btn {
    padding: 6px;
    gap: 0;
  }
  
  .dropdown-arrow {
    display: none;
  }
  
  .user-info {
    gap: 10px;
  }
  
  .user-avatar {
    width: 28px;
    height: 28px;
  }
  
  .user-avatar-placeholder {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
  
  .container {
    padding: 0 0.5rem 0.5rem 0.5rem;
    margin: 0.5rem auto;
  }
  
  .navigation {
    flex-direction: column;
    gap: 6px;
  }
  
  .nav-btn {
    width: 100%;
    font-size: 12px;
    padding: 10px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  
  h1 {
    font-size: 1.5rem;
  }
  
  /* Адаптация страницы входа */
  .login-container {
    padding: 1.5rem;
    margin: 1rem;
  }
  
  .logo {
    width: 60px;
    height: 60px;
    margin: 0 auto 1.5rem auto;
  }
  
  /* Адаптация форм */
  .regenerate-form {
    padding: 1rem;
  }
  
  .regenerate-form textarea {
    min-height: 100px;
    font-size: 14px;
  }
  
  .regenerate-form .form-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .regenerate-form .form-actions button {
    width: 100%;
  }

  /* Адаптация поля ввода тайтла для мобильных */
  .tab-text-input {
    padding: 4px 7px;
    min-height: 19px; /* Соответствует session-tab-title на маленьких экранах */
    font-size: 12px;
  }
  
  /* Адаптация текста в карточках */
  .session-time,
  .session-user,
  .session-id {
    font-size: 11px;
  }
  
  .tab-title {
    font-size: 14px;
  }
  
  .session-ttl {
    font-size: 11px;
    padding: 3px 8px;
  }
  
  /* Адаптация кнопок в модальном окне */
  .modal-actions button {
    font-size: 12px;
    padding: 8px 12px;
  }
  
  .prompt-display {
    padding: 0.75rem;
  }
  
  .prompt-text {
    font-size: 13px;
  }
}

/* Компактная таблица системной статистики */
.compact-stats-table {
  margin: 2rem 0;
}

/* Карточка статистики плагина */
.plugin-stats-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;
}

.plugin-stats-card h3 {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  color: var(--text-primary);
}

.plugin-stats-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.stat-group {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.stat-item-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--bg-primary);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid var(--border-light);
}

.stat-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.stat-value-inline {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.beta-highlight {
  color: var(--info-color);
}

.version-distribution {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  padding: 0.75rem;
  background: var(--bg-primary);
  border-radius: 6px;
}

.version-distribution strong {
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-right: 0.25rem;
}

.version-badge {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid var(--border-light);
}

.system-stats-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
}

.system-stats-table thead {
  background: var(--bg-tertiary);
}

.system-stats-table th {
  padding: 0.75rem 1rem;
  text-align: center;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 0.875rem;
  border-bottom: 2px solid var(--border-color);
}

.system-stats-table tbody td {
  padding: 1rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
}

.system-stats-table .success-text {
  color: var(--success-color, #28a745);
}

.system-stats-table .error-text {
  color: var(--danger-color, #dc3545);
}

.system-stats-table .disk-status-disk-ok {
  color: var(--success-color, #28a745);
}

.system-stats-table .disk-status-disk-warning {
  color: #ff9800;
}

.system-stats-table .disk-status-disk-critical {
  color: var(--danger-color, #dc3545);
}

/* Заголовок секции сессий с фильтром */
.sessions-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 2rem 0 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.sessions-section-header h1 {
  margin: 0;
}

.session-filter-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.session-filter-controls label {
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.session-filter-controls select {
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  min-width: 200px;
  transition: all 0.2s;
}

.session-filter-controls select:hover {
  border-color: var(--primary-color);
}

.session-filter-controls select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

/* Полоса статистики по отфильтрованным сессиям */
.filtered-stats-bar {
  display: flex;
  gap: 2rem;
  padding: 1rem 1.5rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  margin-bottom: 1.5rem;
}

.filtered-stats-bar .stat-item {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}

.filtered-stats-bar .stat-label {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
}

.filtered-stats-bar .stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Courier New', monospace;
}

.filtered-stats-bar .success-text {
  color: var(--success-color, #28a745);
}

.filtered-stats-bar .error-text {
  color: var(--danger-color, #dc3545);
}

/* Адаптивность */
@media (max-width: 768px) {
  .system-stats-table th {
    padding: 0.5rem;
    font-size: 0.75rem;
  }

  .system-stats-table tbody td {
    padding: 0.75rem 0.5rem;
    font-size: 1.1rem;
  }

  .sessions-section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .session-filter-controls select {
    min-width: 150px;
    font-size: 0.75rem;
  }

  .filtered-stats-bar {
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
  }

  .filtered-stats-bar .stat-value {
    font-size: 1.1rem;
  }
}

/* Компактная статистика */
.compact-stats-section {
  margin: 1.5rem 0;
  padding: 1.25rem 1.5rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.stats-line {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.stats-group {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.stats-label {
  font-size: 0.95rem;
  color: var(--text-secondary);
  font-weight: 500;
}

.stats-values {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-family: 'Courier New', monospace;
  font-size: 1.1rem;
  font-weight: 600;
}

.stat-number {
  color: var(--text-primary);
  cursor: help;
  transition: color 0.2s;
}

.stat-number:hover {
  color: #007bff;
}

.stat-number.success {
  color: #28a745;
}

.stat-number.success:hover {
  color: #218838;
}

.divider {
  color: var(--text-muted);
  font-weight: 400;
  margin: 0 0.1rem;
}

.separator {
  color: var(--border-color);
  font-weight: 300;
  margin: 0 0.5rem;
}

.stats-values.disk-ok .stat-number {
  color: #28a745;
}

.stats-values.disk-warning .stat-number {
  color: #ff9800;
}

.stats-values.disk-critical .stat-number {
  color: #dc3545;
}

/* Таблица установок плагина */
/* ============================================
   COLLAPSIBLE PLUGIN SECTION
   ============================================ */

.plugin-users-section {
  margin: 1.5rem 0;
}

/* Фильтр для установок плагина */
.plugin-filter-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0;
}

.plugin-filter-controls label {
  font-weight: 500;
  color: var(--text-primary);
}

.plugin-filter-controls select {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.plugin-filter-controls select:hover {
  border-color: var(--primary-color);
}

.plugin-filter-controls select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.collapsible-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.collapsible-header:hover {
  background: var(--card-hover-bg);
  border-color: var(--primary-color);
}

.collapsible-icon {
  font-size: 0.875rem;
  color: var(--text-secondary);
  transition: transform 0.3s ease;
  width: 12px;
  display: inline-block;
}

.collapsible-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  flex: 1;
  text-align: left;
}

.collapsible-content {
  margin-top: 1rem;
  overflow: hidden;
}

/* Анимация collapse */
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
}

.collapse-enter-from,
.collapse-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
}

.plugin-users-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
}

.plugin-users-table thead {
  background: rgba(255, 255, 255, 0.03);
}

.plugin-users-table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-secondary);
  border-bottom: 2px solid var(--border-color);
}

.plugin-users-table tbody tr {
  border-bottom: 1px solid var(--border-light);
  transition: background-color 0.15s;
}

.plugin-users-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.02);
}

.plugin-users-table tbody tr:last-child {
  border-bottom: none;
}

.plugin-users-table td {
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: var(--text-primary);
}

.plugin-users-table .user-email {
  font-weight: 500;
  color: var(--text-primary);
}

.plugin-users-table .ip-cell {
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.plugin-users-table .country-cell {
  text-align: center;
}

.plugin-users-table .country-flag {
  font-size: 1.5rem;
  cursor: help;
  display: inline-block;
  transition: transform 0.2s;
}

.plugin-users-table .country-flag:hover {
  transform: scale(1.2);
}

.plugin-users-table .version-cell {
  text-align: center;
}

.plugin-users-table .version-badge {
  display: inline-block;
  padding: 0.25rem 0.6rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-light);
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #007bff;
  font-family: 'Courier New', monospace;
}

.plugin-users-table .id-cell {
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.plugin-users-table .install-id {
  cursor: help;
}

.plugin-users-table .beta-cell {
  text-align: center;
}

.plugin-users-table .beta-badge {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  background: #ff9800;
  color: #fff;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.plugin-users-table .no-beta {
  color: var(--text-muted);
}

.plugin-users-table .platform-cell,
.plugin-users-table .date-cell {
  color: var(--text-secondary);
  font-size: 0.8rem;
}

/* ============================================
   PLUGIN ERRORS SECTION
   ============================================ */

.plugin-errors-section {
  margin: 1.5rem 0;
}

.plugin-errors-filters {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.plugin-errors-filters select {
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.85rem;
  cursor: pointer;
}

.plugin-errors-table-wrap {
  overflow-x: auto;
}

.plugin-errors-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
}

.plugin-errors-table thead {
  background: rgba(255, 255, 255, 0.03);
}

.plugin-errors-table th {
  padding: 0.6rem 0.75rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.8rem;
  color: var(--text-secondary);
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

.plugin-errors-table td {
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-light);
}

.plugin-error-row {
  cursor: pointer;
  transition: background 0.15s;
}

.plugin-error-row:hover {
  background: var(--card-hover-bg);
}

.error-message-cell {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.error-type-badge {
  display: inline-block;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
}

.trust-badge {
  font-size: 0.9rem;
}

.trust-auth {
  color: #4caf50;
}

.trust-anon {
  color: var(--text-muted);
  font-weight: bold;
}

.plugin-error-stack-row td {
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.1);
}

.error-stack-trace {
  margin: 0;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  overflow-x: auto;
  font-size: 0.78rem;
  line-height: 1.4;
  color: var(--text-secondary);
  white-space: pre-wrap;
  word-break: break-all;
}

.error-stack-trace code {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}

.error-url {
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: var(--text-muted);
  word-break: break-all;
}

.error-meta {
  margin-top: 0.5rem;
  display: flex;
  gap: 1rem;
  font-size: 0.78rem;
  color: var(--text-muted);
}

.no-errors-message {
  text-align: center;
  padding: 2rem;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.plugin-errors-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.5rem;
}

.plugin-errors-pagination button {
  padding: 0.3rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 0.9rem;
}

.plugin-errors-pagination button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.plugin-errors-pagination span {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

/* Responsive */
@media (max-width: 768px) {
  .unified-stats-table td.label {
    font-size: 0.8rem;
    padding: 0.5rem 0.75rem;
  }
  
  .unified-stats-table td.value {
    font-size: 0.95rem;
    padding: 0.5rem 0.75rem;
  }
  
  .unified-stats-table .section-header th {
    font-size: 0.9rem;
    padding: 0.6rem 0.75rem;
  }
}

/* ============================================
   INLINE РЕДАКТИРОВАНИЕ НАЗВАНИЯ
   ============================================ */

.session-tab-title {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 1;
}

.tab-text-input {
  flex: 1;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--accent-color);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.2s;
  min-width: 200px;
  min-height: 26px; /* 38px (min-height подложки) - 12px (padding 6*2) = 26px */
  line-height: 1.5;
  box-sizing: border-box;
}

.tab-text-input:focus {
  outline: none;
  border-color: var(--success-color);
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
}

.edit-btn,
.edit-save-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.edit-btn:hover {
  background: var(--bg-secondary);
  transform: scale(1.1);
}

.edit-save-btn {
  color: var(--success-color);
  font-weight: bold;
  font-size: 1.2rem;
}

.edit-save-btn:hover {
  background: rgba(76, 175, 80, 0.1);
  transform: scale(1.15);
}

/* ============================================
   МОДАЛЬНОЕ ОКНО ПОДТВЕРЖДЕНИЯ УДАЛЕНИЯ
   ============================================ */

.delete-confirm-modal,
.reprocess-confirm-modal {
  max-width: 450px;
}

.delete-warning {
  font-size: 1rem;
  color: var(--text-primary);
  padding: 1rem;
}

.reprocess-info {
  font-size: 1rem;
  color: var(--text-primary);
  padding: 1rem;
}

.delete-session-info,
.reprocess-session-info {
  background: var(--bg-secondary);
  border-left: 4px solid var(--error-color);
  padding: 20px 24px;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  line-height: 1.6;
}

.reprocess-session-info {
  border-left-color: var(--primary-color);
}

/* Скелетон при переобработке в модальном окне */
.reprocessing-skeleton {
  padding: 20px;
}

.reprocessing-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  font-size: 1.1rem;
  color: var(--primary-color);
  font-weight: 500;
}

.reprocessing-icon {
  display: inline-block;
  /* animation: spin 1.5s linear infinite; — убрано */
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.skeleton-lines {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-line {
  height: 18px;
  background: linear-gradient(
    90deg,
    var(--skeleton-base) 0%,
    var(--skeleton-highlight) 50%,
    var(--skeleton-base) 100%
  );
  background-size: 200% 100%;
  border-radius: 4px;
  animation: skeleton-shimmer 2s ease-in-out infinite;
}

.delete-session-info div {
  display: flex;
  align-items: baseline;
  margin-bottom: 0.5rem;
}

.delete-session-info div:last-child {
  margin-bottom: 0;
}

.delete-session-info strong {
  color: var(--text-primary);
  min-width: 100px;
  flex-shrink: 0;
  font-weight: 600;
}

.delete-session-info span {
  color: var(--text-secondary);
  flex: 1;
  word-break: break-word;
}

.delete-note {
  font-size: 0.9rem;
  color: var(--error-color);
  margin: 0;
  font-weight: 500;
  padding: 1rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
}

.btn {
  padding: 0.6rem 1.25rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--bg-tertiary);
  transform: translateY(-1px);
}

.btn-danger {
  background: var(--error-color);
  color: white;
}

.btn-danger:hover {
  background: #d32f2f;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
}

/* ============================================
   TOAST УВЕДОМЛЕНИЯ
   ============================================ */

.toast-notification {
  position: fixed;
  top: 90px;
  right: 20px;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  font-size: 0.95rem;
  font-weight: 500;
  max-width: 400px;
  backdrop-filter: blur(10px);
}

.toast-notification.success {
  background: rgba(76, 175, 80, 0.95);
  color: white;
  border-left: 4px solid #388e3c;
}

.toast-notification.error {
  background: rgba(244, 67, 54, 0.95);
  color: white;
  border-left: 4px solid #c62828;
}

.toast-notification.info {
  background: rgba(33, 150, 243, 0.95);
  color: white;
  border-left: 4px solid #1976d2;
}

.toast-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.toast-message {
  flex: 1;
}

/* Toast анимация появления/исчезновения */
.toast-enter-active {
  animation: toast-slide-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toast-leave-active {
  animation: toast-slide-out 0.25s ease-in;
}

@keyframes toast-slide-in {
  0% {
    opacity: 0;
    transform: translateX(100px) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

@keyframes toast-slide-out {
  0% {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateX(100px) scale(0.8);
  }
}

/* Адаптивность для мобильных */
@media (max-width: 768px) {
  .toast-notification {
    top: 70px;
    right: 10px;
    left: 10px;
    max-width: calc(100% - 20px);
    padding: 0.875rem 1.25rem;
    font-size: 0.875rem;
  }
  
  .tab-text-input {
    min-width: 150px;
    font-size: 13px;
    padding: 5px 8px;
    min-height: 21px; /* Соответствует уменьшенному размеру подложки на планшетах */
  }
  
  .delete-confirm-modal,
  .reprocess-confirm-modal {
    max-width: calc(100% - 2rem);
  }
}

/* ============================================
   НАСТРОЙКИ ТРАНСКРИПЦИИ (Settings Modal)
   ============================================ */

.settings-modal {
  max-width: 700px;
  max-height: 85vh;
}

.settings-modal .modal-body {
  padding: 0;
  overflow-y: auto;
  max-height: calc(85vh - 140px);
  overscroll-behavior: contain;
}

/* ============================================
   ПРОМПТЫ (Prompts Modal)
   ============================================ */

.prompts-modal {
  max-width: 900px;
  max-height: 85vh;
}

.prompts-modal-body {
  padding: 0;
  overflow-y: auto;
  max-height: calc(85vh - 80px);
  overscroll-behavior: contain;
}

.settings-section {
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 20px;
  margin: 16px;
  border: 1px solid var(--border-light);
}

.settings-section:first-child {
  margin-top: 16px;
}

.settings-section:last-child {
  margin-bottom: 16px;
}

.setting-item {
  margin-bottom: 24px;
}

.setting-item:last-child {
  margin-bottom: 0;
}

.setting-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.setting-label {
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
}

.setting-value {
  font-size: 18px;
  font-weight: 600;
  color: var(--accent-color);
  background: var(--accent-bg);
  padding: 4px 12px;
  border-radius: 6px;
}

.setting-slider {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.15); /* Светлый на темном фоне по умолчанию */
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.3); /* Добавляем границу для видимости */
}

/* Светлая тема - темный слайдер */
html:not(.dark) .setting-slider,
body:not(.dark) .setting-slider {
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.15);
}

/* Стилизация track для WebKit (Chrome, Safari, Edge) */
.setting-slider::-webkit-slider-runnable-track {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

html:not(.dark) .setting-slider::-webkit-slider-runnable-track,
body:not(.dark) .setting-slider::-webkit-slider-runnable-track {
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.15);
}

/* Стилизация track для Firefox */
.setting-slider::-moz-range-track {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

html:not(.dark) .setting-slider::-moz-range-track,
body:not(.dark) .setting-slider::-moz-range-track {
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.setting-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--accent-color);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  transition: all 0.2s ease;
  margin-top: -8px; /* Центрируем thumb относительно track */
  border: 3px solid white; /* Белая обводка для контраста */
}

.setting-slider::-webkit-slider-thumb:hover {
  transform: scale(1.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

.setting-slider::-webkit-slider-thumb:active {
  transform: scale(1.05);
}

.setting-slider::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--accent-color);
  cursor: pointer;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  transition: all 0.2s ease;
}

.setting-slider::-moz-range-thumb:hover {
  transform: scale(1.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

.setting-slider::-moz-range-thumb:active {
  transform: scale(1.05);
}

.setting-description {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
}

.temp-markers {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 8px;
}

.temp-marker {
  font-size: 13px;
  color: var(--text-secondary);
  text-align: center;
  flex: 1;
}

.temp-marker strong {
  color: var(--accent-color);
  display: block;
  margin-bottom: 4px;
}

.setting-note {
  margin-top: 12px;
  padding: 12px;
  background: var(--accent-bg);
  border-left: 3px solid var(--accent-color);
  border-radius: 4px;
  font-size: 13px;
}

/* 🆕 Качественная обработка */
.setting-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.setting-toggle-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.setting-info {
  flex: 1;
}

.setting-info .setting-label {
  margin-bottom: 0.25rem;
}

.setting-info .setting-description {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
}

.switch.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.quality-mode-toggle {
  padding: 6px 16px;
  border-radius: 16px;
  border: 2px solid #ddd;
  background: white;
  color: #666;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.quality-mode-toggle:hover {
  border-color: #4CAF50;
  transform: scale(1.05);
}

.quality-mode-toggle.active {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  border-color: #4CAF50;
  color: white;
  box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
}

.quality-mode-toggle.disabled,
.quality-mode-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: #ccc;
  background: #f5f5f5;
  color: #999;
}

.quality-mode-toggle.disabled:hover,
.quality-mode-toggle:disabled:hover {
  transform: none;
  border-color: #ccc;
}

.quality-mode-note {
  margin-top: 12px;
  padding: 12px;
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  border-radius: 4px;
  font-size: 13px;
  color: #856404;
  animation: slideDown 0.3s ease;
}

.active-recording-warning {
  margin-top: 12px;
  padding: 12px;
  background: #ffe5e5;
  border-left: 4px solid #f44336;
  border-radius: 4px;
  font-size: 13px;
  color: #c62828;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dark-mode .quality-mode-toggle {
  background: #2d2d2d;
  border-color: #444;
  color: #ccc;
}

.dark-mode .quality-mode-toggle.disabled,
.dark-mode .quality-mode-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: #333;
  background: #1a1a1a;
  color: #666;
}

.dark-mode .quality-mode-toggle.active {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  border-color: #4CAF50;
  color: white;
}

.dark-mode .quality-mode-note {
  background: #3e3e2d;
  border-left-color: #ffc107;
  color: #ffd966;
}

.dark-mode .active-recording-warning {
  background: #3e2d2d;
  border-left-color: #f44336;
  color: #ff6b6b;
}

/* Адаптивность для настроек */
@media (max-width: 768px) {
  .settings-container {
    padding: 16px;
  }

  .settings-title {
    font-size: 24px;
    margin-bottom: 24px;
  }

  .settings-section {
    padding: 16px;
  }

  .temp-markers {
    flex-direction: column;
    gap: 8px;
  }

  .temp-marker {
    text-align: left;
  }
}

/* ============================================
   CHANGELOG MODAL STYLES
   ============================================ */

.changelog-modal {
  max-width: 800px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.changelog-modal .modal-body {
  overflow-y: auto;
  max-height: calc(85vh - 80px);
  padding: 16px 20px;
  overscroll-behavior: contain;
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.changelog-editor {
  background: var(--card-bg);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.changelog-editor h3 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: var(--text-primary);
}

.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
  color: var(--text-secondary);
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 13px;
  font-family: 'JetBrains Mono', monospace;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--accent-color);
}

.form-group textarea {
  resize: vertical;
  min-height: 120px;
  line-height: 1.5;
}

.form-hint {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: var(--text-muted);
  font-style: italic;
}

.form-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 16px;
}

.changelog-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.changelog-section {
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 12px;
}

.changelog-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.changelog-section-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 10px;
  color: var(--text-primary);
}

.empty-changelog {
  color: var(--text-muted);
  font-style: italic;
  text-align: center;
  padding: 8px;
  font-size: 13px;
}

.changelog-entries {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.changelog-entry {
  padding: 6px 0;
}

.changelog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--border-color);
}

.changelog-version {
  font-size: 14px;
  font-weight: 600;
  color: var(--accent-color);
}

.changelog-date {
  font-size: 11px;
  color: var(--text-muted);
  margin-left: 8px;
}

.changelog-actions {
  display: flex;
  gap: 4px;
}

.changelog-changes {
  list-style: none;
  padding: 0;
  margin: 0;
}

.changelog-changes li {
  padding: 2px 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.4;
}

.changelog-changes li::before {
  content: '•';
  color: var(--accent-color);
  font-weight: bold;
  margin-right: 6px;
}

.btn-icon {
  background: transparent;
  border: none;
  font-size: 14px;
  cursor: pointer;
  padding: 3px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  background: var(--button-hover-bg);
}

.btn-icon-danger:hover {
  background: rgba(239, 68, 68, 0.1);
}

.btn-sm {
  padding: 5px 10px;
  font-size: 12px;
}

/* ============================================
   SKELETON LOADING - Плавная загрузка записей
   ============================================ */

.sessions-skeleton {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
}

.skeleton-card {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.skeleton-content {
  display: flex;
  justify-content: space-between;
  gap: 2rem;
}

.skeleton-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-right {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-end;
}

.skeleton-time,
.skeleton-title,
.skeleton-text,
.skeleton-id,
.skeleton-btn {
  background: linear-gradient(
    90deg,
    var(--skeleton-base) 0%,
    var(--skeleton-highlight) 50%,
    var(--skeleton-base) 100%
  );
  background-size: 200% 100%;
  border-radius: 6px;
  animation: skeleton-shimmer 2s ease-in-out infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.skeleton-time {
  width: 120px;
  height: 18px;
}

.skeleton-title {
  width: 70%;
  height: 22px;
}

.skeleton-text {
  width: 85%;
  height: 16px;
}

.skeleton-id {
  width: 100px;
  height: 16px;
}

.skeleton-actions {
  display: flex;
  gap: 0.5rem;
}

.skeleton-btn {
  width: 70px;
  height: 32px;
}

/* Темная тема для skeleton */
:root {
  --skeleton-base: rgba(100, 116, 139, 0.1);
  --skeleton-highlight: rgba(100, 116, 139, 0.2);
}

[data-theme="dark"] {
  --skeleton-base: rgba(148, 163, 184, 0.1);
  --skeleton-highlight: rgba(148, 163, 184, 0.15);
}

@media (max-width: 768px) {
  .skeleton-content {
    flex-direction: column;
    gap: 1rem;
  }
  
  .skeleton-right {
    align-items: flex-start;
  }
  
  .skeleton-title {
    width: 90%;
  }
  
  .skeleton-text {
    width: 100%;
  }
}

/* Clickable code block */
.clickable-code {
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.clickable-code:hover {
  background: var(--primary-color);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.clickable-code:active {
  transform: translateY(0);
}

@media (max-width: 768px) {
  .changelog-modal {
    max-width: calc(100% - 2rem);
  }
  
  .changelog-editor {
    padding: 12px;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .changelog-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}

</style>
